import { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  HeartHandshake, ArrowLeft, Phone, Mail, Calendar, Activity,
  CreditCard, UserCheck, CheckCircle2, AlertCircle, Clock, Copy,
  Send, RefreshCw, ChevronDown, CheckCircle, ExternalLink, ShieldCheck,
  ShieldAlert, MessageSquare, ChevronRight, User, Users, Link2, Share2, Eye,
  Building, MapPin, Tag, Check, ArrowRight, PhoneCall, PhoneOff,
  Edit, Loader2, UserPlus, Heart, Stethoscope, UserCog, Image,
  FileText, Trash2, Plus, X, Download, ZoomIn, Maximize2, Upload,
  Calculator, Percent, Sparkles, Info, RotateCw, Search, Filter, CheckSquare, Save, Pill,
  XCircle, RotateCcw, Lock
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../services/api';
import opheService from '../../services/ophe.service';
import { useAuth } from '../../context/AuthContext';
import { useSocket } from '../../hooks/useSocket';
import { initiateCall as exotelInitiateCall, getCallLogs as exotelGetCallLogs, saveCallDisposition } from '../../services/exotelApi';
import CallHistoryPanel, { getEffectiveDisposition } from '../leads/components/CallHistoryPanel';

const getLightStatusBadge = (status) => {
  switch (status) {
    case 'call-successful':
    case 'completed':
    case 'answered':
      return { bg: 'bg-emerald-50 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500', label: 'Call was successful' };
    case 'no-answer':
    case 'no-user-answered':
    case 'missed':
      return { bg: 'bg-red-50 text-red-700 border-red-200', dot: 'bg-red-500', label: 'No user answered (Missed)' };
    case 'client-hungup-during-call':
      return { bg: 'bg-rose-50 text-rose-700 border-rose-200', dot: 'bg-rose-500', label: 'Client hung-up during call' };
    case 'client-hungup-before-connecting':
    case 'cancelled':
    case 'call-fatal':
      return { bg: 'bg-amber-50 text-amber-700 border-amber-200', dot: 'bg-amber-500', label: 'Client hung-up before connecting' };
    case 'client-unanswered':
      return { bg: 'bg-amber-50 text-amber-700 border-amber-200', dot: 'bg-amber-500', label: 'Client unanswered' };
    case 'busy':
      return { bg: 'bg-orange-50 text-orange-700 border-orange-200', dot: 'bg-orange-500', label: 'User was busy' };
    case 'ringing':
      return { bg: 'bg-indigo-50 text-indigo-700 border-indigo-200', dot: 'bg-indigo-500 animate-ping', label: 'Ringing' };
    case 'in-progress':
      return { bg: 'bg-blue-50 text-blue-700 border-blue-200', dot: 'bg-blue-500 animate-pulse', label: 'Call in progress' };
    default:
      return { bg: 'bg-slate-50 text-slate-700 border-slate-200', dot: 'bg-slate-400', label: status || 'Unknown' };
  }
};

const getLightDispositionBadge = (dispKey) => {
  switch (dispKey) {
    case 'callback_completed':
    case 'resolved':
      return { bg: 'bg-emerald-50 text-emerald-800 border-emerald-300', dot: 'bg-emerald-500', label: 'Callback Completed' };
    case 'follow_up_needed':
      return { bg: 'bg-blue-50 text-blue-800 border-blue-300', dot: 'bg-blue-500', label: 'Follow-up Needed' };
    case 'appointment_booked':
      return { bg: 'bg-purple-50 text-purple-800 border-purple-300', dot: 'bg-purple-500', label: 'Appointment Booked' };
    case 'not_interested':
      return { bg: 'bg-slate-100 text-slate-700 border-slate-300', dot: 'bg-slate-400', label: 'Not Interested' };
    case 'invalid_number':
      return { bg: 'bg-rose-50 text-rose-800 border-rose-300', dot: 'bg-rose-500', label: 'Invalid Number' };
    case 'pending':
    default:
      return { bg: 'bg-amber-50 text-amber-800 border-amber-300', dot: 'bg-amber-500', label: 'Pending Callback' };
  }
};

const safeInr = (val, digits = 0) => {
  const num = Number(val) || 0;
  if (digits > 0) {
    return num.toLocaleString('en-IN', { minimumFractionDigits: digits, maximumFractionDigits: digits });
  }
  return num.toLocaleString('en-IN');
};

const sanitizePhoneNumber = (val) => {
  if (!val) return '';
  let cleaned = String(val).replace(/\D/g, '');
  if (cleaned.length > 10 && cleaned.startsWith('91')) {
    cleaned = cleaned.slice(2);
  }
  // Strip all leading zeros so a number never starts with 0
  cleaned = cleaned.replace(/^0+/, '');
  return cleaned.slice(0, 10);
};

const addDaysToDate = (dateStr, days) => {
  if (!dateStr) return '';
  const parts = String(dateStr).split('T')[0].split('-');
  if (parts.length !== 3) return '';
  const y = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10) - 1;
  const d = parseInt(parts[2], 10);
  if (isNaN(y) || isNaN(m) || isNaN(d)) return '';
  const dateObj = new Date(y, m, d);
  dateObj.setDate(dateObj.getDate() + Math.max(0, (Number(days) || 1) - 1));
  const resYear = dateObj.getFullYear();
  const resMonth = String(dateObj.getMonth() + 1).padStart(2, '0');
  const resDay = String(dateObj.getDate()).padStart(2, '0');
  return `${resYear}-${resMonth}-${resDay}`;
};

const getNormalizedDate = (rawDate) => {
  if (!rawDate) return '';
  const datePart = String(rawDate).split('T')[0].trim();
  const sep = datePart.includes('/') ? '/' : '-';
  const parts = datePart.split(sep);
  if (parts.length === 3) {
    if (parts[0].length === 4) {
      const y = parseInt(parts[0], 10);
      const m = String(parseInt(parts[1], 10)).padStart(2, '0');
      const d = String(parseInt(parts[2], 10)).padStart(2, '0');
      if (!isNaN(y)) return `${y}-${m}-${d}`;
    } else if (parts[2].length === 4) {
      const d = String(parseInt(parts[0], 10)).padStart(2, '0');
      const m = String(parseInt(parts[1], 10)).padStart(2, '0');
      const y = parseInt(parts[2], 10);
      if (!isNaN(y)) return `${y}-${m}-${d}`;
    }
  }
  try {
    const dt = new Date(rawDate);
    if (!isNaN(dt.getTime())) {
      return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
    }
  } catch (_) { }
  return String(rawDate).slice(0, 10);
};

const getTodayDateStr = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

const calculateDaysBetween = (startStr, endStr) => {
  if (!startStr || !endStr) return 1;
  const pStart = String(startStr).split('T')[0].split('-');
  const pEnd = String(endStr).split('T')[0].split('-');
  if (pStart.length !== 3 || pEnd.length !== 3) return 1;
  const dStart = new Date(parseInt(pStart[0], 10), parseInt(pStart[1], 10) - 1, parseInt(pStart[2], 10));
  const dEnd = new Date(parseInt(pEnd[0], 10), parseInt(pEnd[1], 10) - 1, parseInt(pEnd[2], 10));
  if (isNaN(dStart.getTime()) || isNaN(dEnd.getTime())) return 1;
  const diffTime = dEnd.getTime() - dStart.getTime();
  const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24)) + 1;
  return diffDays > 0 ? diffDays : 1;
};

const getComputedBilling = ({
  perDayRate = 0,
  daysCount = 1,
  discountType = 'none',
  discountValue = 0,
  applyGst = true,
  gstRate = 18,
  alreadyPaidAmount = 0,
  // Mid-care upgrade parameters
  extensionSubMode = 'extra_days',
  originalDays = 0,
  originalDailyRate = 0,
  daysServed = 0,
  daysToUpgrade = 0,
  upgradedDailyRate = 0,
  shiftHours = 12
} = {}) => {
  const rateGst = applyGst && Number(gstRate) >= 0 ? Number(gstRate) : 0;
  const paid = Math.max(0, Number(alreadyPaidAmount) || 0);

  const origDays = Math.max(1, Number(originalDays) || Number(daysCount) || 1);
  const origRate = Math.max(0, Number(originalDailyRate) || Number(perDayRate) || 0);
  const served = Math.max(0, Number(daysServed) || 0);
  const remainingUnusedDays = Math.max(0, origDays - served);
  const upgradeDays = Math.max(1, Number(daysToUpgrade) || (remainingUnusedDays > 0 ? remainingUnusedDays : 1));
  const upgRate = Math.max(0, Number(upgradedDailyRate) || Number(perDayRate) || 0);

  // Consumed Base Cost = Days Served * Original Rate
  const consumedBaseCost = Math.round(served * origRate * 100) / 100;

  // Remaining Unused Credit from Original Booking
  const unusedCredit = Math.round(remainingUnusedDays * origRate * 100) / 100;

  // New Care Cost for Upgraded Days (e.g. 5 days @ 24h)
  const newShiftCost = Math.round(upgradeDays * upgRate * 100) / 100;

  // Net Upgrade Base Difference (New cost - Unused credit)
  const netUpgradeBase = Math.max(0, Math.round((newShiftCost - unusedCredit) * 100) / 100);

  // ── MID-CARE SHIFT UPGRADE CALCULATION ──
  if (extensionSubMode === 'shift_upgrade') {
    const gstAmount = rateGst > 0 ? Math.round(((netUpgradeBase * rateGst) / 100) * 100) / 100 : 0;
    const totalAmount = Math.round((netUpgradeBase + gstAmount) * 100) / 100;
    const balanceDue = totalAmount;

    // Revised Total Package Cost (Consumed + New Upgraded Cost)
    const revisedGrossTotalBase = Math.round((consumedBaseCost + newShiftCost) * 100) / 100;
    const revisedGrossTotalGst = rateGst > 0 ? Math.round(((revisedGrossTotalBase * rateGst) / 100) * 100) / 100 : 0;
    const revisedGrossTotal = Math.round((revisedGrossTotalBase + revisedGrossTotalGst) * 100) / 100;

    return {
      dailyRate: upgRate,
      days: upgradeDays,
      shiftHours: Number(shiftHours) || 24,
      rawBaseTotal: netUpgradeBase,
      discountType: 'none',
      discountValue: 0,
      discountAmount: 0,
      discountedBaseTotal: netUpgradeBase,
      applyGst,
      gstRate: rateGst,
      gstAmount,
      totalAmount,
      alreadyPaidAmount: paid,
      balanceDue,
      extensionSubMode: 'shift_upgrade',
      originalDays: origDays,
      originalDailyRate: origRate,
      daysServed: served,
      remainingUnusedDays,
      unusedCredit,
      newShiftCost,
      netUpgradeBase,
      consumedBaseCost,
      revisedGrossTotalBase,
      revisedGrossTotal,
      perDayBaseCost: upgRate,
      perDayGst: Math.round((upgRate * (rateGst / 100)) * 100) / 100,
      perDayTotalCost: Math.round((upgRate * (1 + rateGst / 100)) * 100) / 100
    };
  }

  const dailyRate = Math.max(0, Number(perDayRate) || 0);
  const days = Math.max(1, Number(daysCount) || 1);

  // 1. Raw Base Total before discount (Daily Rate * Days)
  const rawBaseTotal = Math.round(dailyRate * days * 100) / 100;

  // 2. Discount Calculation
  let discountAmount = 0;
  const dVal = Math.max(0, Number(discountValue) || 0);
  if (discountType === 'percentage') {
    discountAmount = Math.round(((rawBaseTotal * Math.min(100, dVal)) / 100) * 100) / 100;
  } else if (discountType === 'fixed') {
    discountAmount = Math.min(rawBaseTotal, Math.round(dVal * 100) / 100);
  }

  // 3. Discounted Base Subtotal
  const discountedBaseTotal = Math.max(0, Math.round((rawBaseTotal - discountAmount) * 100) / 100);

  // 4. GST on Discounted Base
  const gstAmount = rateGst > 0 ? Math.round(((discountedBaseTotal * rateGst) / 100) * 100) / 100 : 0;

  // 5. Total Gross Amount (Discounted Base + GST)
  const totalAmount = Math.round((discountedBaseTotal + gstAmount) * 100) / 100;

  // 6. Balance Due for Extensions / Upgrades
  const balanceDue = Math.max(0, Math.round((totalAmount - paid) * 100) / 100);

  // 7. Per Day Breakdown Metrics
  const perDayBaseCost = days > 0 ? Math.round((discountedBaseTotal / days) * 100) / 100 : 0;
  const perDayGst = days > 0 ? Math.round((gstAmount / days) * 100) / 100 : 0;
  const perDayTotalCost = days > 0 ? Math.round((totalAmount / days) * 100) / 100 : 0;

  return {
    dailyRate,
    days,
    rawBaseTotal,
    discountType,
    discountValue: dVal,
    discountAmount,
    discountedBaseTotal,
    applyGst,
    gstRate: rateGst,
    gstAmount,
    totalAmount,
    alreadyPaidAmount: paid,
    balanceDue,
    perDayBaseCost,
    perDayGst,
    perDayTotalCost,
    extensionSubMode: 'extra_days',
    originalDays: origDays,
    originalDailyRate: origRate,
    daysServed: served,
    remainingUnusedDays,
    unusedCredit,
    newShiftCost,
    netUpgradeBase,
    consumedBaseCost,
    revisedGrossTotal: totalAmount
  };
};

export default function OpheLeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { socket } = useSocket();

  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [services, setServices] = useState([]);
  const [saving, setSaving] = useState(false);
  const [currentMedication, setCurrentMedication] = useState('');
  const [savingMedication, setSavingMedication] = useState(false);

  // Lost Lead Modal State
  const [showLostModal, setShowLostModal] = useState(false);
  const [lostReason, setLostReason] = useState('Customer not interested');
  const [markingLost, setMarkingLost] = useState(false);

  const handleConfirmMarkLost = async () => {
    if (!lead?._id) return;
    try {
      setMarkingLost(true);
      await api.patch(`/leads/${lead._id}/status`, {
        status: 'not_interested',
        notInterestedReason: lostReason.trim() || 'Customer not interested'
      });
      toast.success('Lead marked as lost and moved to OPHE Lost Leads!');
      setShowLostModal(false);
      navigate('/ophe/pipeline?tab=lost');
    } catch (err) {
      console.error('Failed to mark lead as lost:', err);
      toast.error(err.response?.data?.message || 'Failed to mark lead as lost');
    } finally {
      setMarkingLost(false);
    }
  };

  // Telephony & Call Logs
  const [latestCall, setLatestCall] = useState(null);
  const [showCallHistory, setShowCallHistory] = useState(false);
  const [callHistoryRefresh, setCallHistoryRefresh] = useState(0);
  const [isCallingPatient, setIsCallingPatient] = useState(false);
  const [showQuickDispModal, setShowQuickDispModal] = useState(false);
  const [isSavingQuickDisp, setIsSavingQuickDisp] = useState(false);
  const [quickDispForm, setQuickDispForm] = useState({
    disposition: 'callback_completed',
    dispositionNotes: '',
    callReason: ''
  });

  // Photo & Document Vault
  const [isEditingVault, setIsEditingVault] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);

  // Stage 1: Dynamic Pricing, Shift, Discount & Payment Form state
  const [paymentForm, setPaymentForm] = useState({
    categoryId: '',
    serviceId: '',
    packageName: '',
    shiftHours: 12, // 12, 24, or custom hours
    shiftType: '12-Hour Shift',
    catalogPrice: 0,
    quotedPrice: '', // CC negotiated daily base rate (e.g. ₹799)
    daysCount: 7,
    discountType: 'none', // 'none' | 'percentage' | 'fixed'
    discountValue: '',
    applyGst: true,
    gstRate: 18,
    startDate: '',
    endDate: '',
    alreadyPaidAmount: 0,
    totalPackagePrice: '',
    amount: '',
    description: '',
    channel: 'both',
    primaryEmail: '',
    primaryPhone: '',
    alternateEmail: '',
    alternateWhatsapp: '',
    conditionNotes: '',
    vitalBloodPressure: '',
    vitalSugar: '',
    vitalOxygenSpo2: '',
    mobilityStatus: 'Independent'
  });

  // Stage 2: Care Provider Form state
  const [providerForm, setProviderForm] = useState({
    providerName: '',
    providerPhone: '',
    providerEmail: '',
    providerRole: 'Trained Care Attendant',
    shiftType: '12-Hour Day Shift',
    careStartDate: new Date().toISOString().split('T')[0],
    expectedEndDate: new Date(Date.now() + 6 * 86400000).toISOString().split('T')[0],
    defaultStartTime: '08:00 AM',
    defaultEndTime: '08:00 PM',
    providerNotes: '',
    dailyDutySchedule: []
  });
  const [isEditingProvider, setIsEditingProvider] = useState(false);

  // Stage 3: Completion & Payout Form state
  const [payoutForm, setPayoutForm] = useState({
    finalAmountReceived: '',
    providerPayoutAmount: '',
    payoutMode: 'Bank Transfer / IMPS',
    payoutReferenceId: '',
    payoutDate: new Date().toISOString().split('T')[0],
    completionNotes: '',
    patientFeedback: 'Very Satisfied (5 Star)'
  });

  // Stage UI Active Tab
  const [activeStageTab, setActiveStageTab] = useState(1);
  const [paymentOptionMode, setPaymentOptionMode] = useState('standard'); // 'standard' (Payment) | 'extension' (Extension Payment)
  const [extensionSubMode, setExtensionSubMode] = useState('shift_upgrade'); // 'shift_upgrade' (12h->24h Mid-Care) | 'extra_days' (Add Days)
  const [showExtensionSection, setShowExtensionSection] = useState(false); // Controls top extension payment panel across all stages
  const [upgradeForm, setUpgradeForm] = useState({
    originalDays: 7,
    originalDailyRate: 1000,
    originalShiftHours: 12,
    daysServed: 2,
    daysToUpgrade: 5,
    upgradedShiftHours: 24,
    upgradedDailyRate: 2000
  });
  const [generatingPayment, setGeneratingPayment] = useState(false);
  const [sendingNotification, setSendingNotification] = useState(false);
  const [syncingPayment, setSyncingPayment] = useState(false);
  const [showTransactionsModal, setShowTransactionsModal] = useState(false);

  // Stage 2: Duty Shift CRUD Modal State
  const [ccAgents, setCcAgents] = useState([]);
  const [showDutyModal, setShowDutyModal] = useState(false);
  const [dutyModalForm, setDutyModalForm] = useState({
    mode: 'add', // 'add' | 'edit'
    index: null,
    dayNumber: 1,
    date: new Date().toISOString().split('T')[0],
    startTime: '08:00 AM',
    endTime: '08:00 PM',
    assignedCCId: '',
    assignedCCName: '',
    assignedCCPhone: '',
    providerOption: 'old', // 'old' (Old / Primary Provider) | 'new' (New Provider for this Day)
    providerName: '',
    providerPhone: '',
    providerEmail: '',
    providerRole: 'Trained Care Attendant',
    shiftType: '12-Hour Day Shift',
    status: 'Scheduled',
    notes: ''
  });

  // Unique list of providers for this case (Primary/Old Provider + any providers assigned to daily roster)
  const availableProviders = useMemo(() => {
    const list = [];
    const seen = new Set();

    const primaryName = (lead?.opheProviderData?.providerName || providerForm.providerName || '').trim();
    const primaryPhone = (lead?.opheProviderData?.providerPhone || providerForm.providerPhone || '').trim();
    const primaryEmail = (lead?.opheProviderData?.providerEmail || providerForm.providerEmail || '').trim();
    const primaryRole = lead?.opheProviderData?.providerRole || providerForm.providerRole || 'Trained Care Attendant';
    const primaryShift = lead?.opheProviderData?.shiftType || providerForm.shiftType || '12-Hour Day Shift';

    if (primaryName) {
      list.push({
        id: 'primary',
        name: primaryName,
        phone: primaryPhone,
        email: primaryEmail,
        role: primaryRole,
        shiftType: primaryShift,
        isPrimary: true,
        label: `${primaryName} (Old / Primary Provider)`
      });
      seen.add(primaryName.toLowerCase());
    }

    (providerForm.dailyDutySchedule || []).forEach((day) => {
      const pName = (day.providerName || '').trim();
      const pPhone = (day.providerPhone || '').trim();
      const pEmail = (day.providerEmail || '').trim();
      const pRole = day.providerRole || 'Trained Care Attendant';
      const pShift = day.shiftType || '12-Hour Day Shift';

      if (pName && !seen.has(pName.toLowerCase())) {
        list.push({
          id: `roster-${pName}`,
          name: pName,
          phone: pPhone,
          email: pEmail,
          role: pRole,
          shiftType: pShift,
          isPrimary: false,
          label: `${pName} ${pPhone ? `(${pPhone})` : ''}`
        });
        seen.add(pName.toLowerCase());
      }
    });

    return list;
  }, [lead?.opheProviderData, providerForm.providerName, providerForm.providerPhone, providerForm.providerEmail, providerForm.providerRole, providerForm.shiftType, providerForm.dailyDutySchedule]);
  const [dutyStatusFilter, setDutyStatusFilter] = useState('all');
  const [dutySearchQuery, setDutySearchQuery] = useState('');
  const [showInlineAdd, setShowInlineAdd] = useState(false);
  const [quickAddForm, setQuickAddForm] = useState({
    date: new Date().toISOString().split('T')[0],
    startTime: '08:00 AM',
    endTime: '08:00 PM',
    assignedCCId: '',
    assignedCCName: '',
    assignedCCPhone: '',
    providerName: '',
    providerPhone: '',
    status: 'Scheduled',
    notes: ''
  });

  useEffect(() => {
    loadLeadAndCatalog();
  }, [id]);

  // 📡 Real-time Socket Listener for Payment Confirmed
  useEffect(() => {
    if (!socket || !id) return;

    const handleSocketUpdate = async (data) => {
      const matchId = String(data?.leadId || data?.entityId || '');
      if (matchId === String(id)) {
        if (
          data.action === 'payment_verified' ||
          data.action === 'ophe_payment_received' ||
          data.type === 'ophe_payment_received' ||
          data.status === 'paid' ||
          data.status === 'care_in_progress'
        ) {
          toast.success('🎉 Payment confirmed! Advancing to Stage 2: Provider Assignment.', {
            id: 'ophe-payment-confirmed',
            duration: 5000
          });
          await loadLeadAndCatalog();
          setActiveStageTab(2);
          setShowExtensionSection(false);
        }
      }
    };

    socket.on('lead_updated', handleSocketUpdate);
    socket.on('ophe_payment_received', handleSocketUpdate);

    return () => {
      socket.off('lead_updated', handleSocketUpdate);
      socket.off('ophe_payment_received', handleSocketUpdate);
    };
  }, [socket, id]);

  // 🔄 Auto-Sync & Polling for Pending Payment
  // Automatically detects when patient pays (in another tab, phone, or Razorpay checkout) and moves to Stage 2
  useEffect(() => {
    if (!id) return;

    const isPaid = Boolean(lead?.ophePaymentData?.status === 'paid' || lead?.paymentStatus === 'paid');
    const hasLink = Boolean(lead?.ophePaymentData?.paymentLinkId || lead?.ophePaymentData?.paymentLink);

    // If already confirmed paid or no link exists yet, no polling needed
    if (isPaid || !hasLink) return;

    let isPolling = false;
    const checkPayment = async () => {
      if (isPolling) return;
      isPolling = true;
      try {
        const res = await opheService.syncPayment(id);
        const updatedLead = res.data?.data;
        if (updatedLead?.ophePaymentData?.status === 'paid' || updatedLead?.paymentStatus === 'paid') {
          setLead(updatedLead);
          setActiveStageTab(2);
          setShowExtensionSection(false);
          toast.success(`🎉 Payment of ₹${safeInr(updatedLead.ophePaymentData?.amount)} confirmed! Advancing to Stage 2: Care Provider Assignment.`, {
            id: 'auto-sync-paid',
            duration: 6000
          });
        }
      } catch (_) {
        // silent sync
      } finally {
        isPolling = false;
      }
    };

    // 1. Check immediately on mount/state change
    checkPayment();

    // 2. Poll every 3.5 seconds while payment is pending
    const interval = setInterval(checkPayment, 3500);

    // 3. Immediately sync on tab focus or visibility change (e.g. user returns to this CRM tab after paying)
    const onFocus = () => checkPayment();
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);

    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [id, lead?.ophePaymentData?.status, lead?.ophePaymentData?.paymentLinkId, lead?.paymentStatus]);

  // When Extension Mode is opened, automatically switch to Stage 2 (Care Provider & Execution) instead of Stage 3 (Completion/Payout)
  const prevExtensionRef = useRef(showExtensionSection);
  useEffect(() => {
    if (!prevExtensionRef.current && showExtensionSection) {
      setActiveStageTab(2);
    }
    prevExtensionRef.current = showExtensionSection;
  }, [showExtensionSection]);

  // Load Exotel Call Logs for this lead
  useEffect(() => {
    if (id) {
      exotelGetCallLogs(id)
        .then(logs => {
          if (logs && logs.length > 0) setLatestCall(logs[0]);
          else setLatestCall(null);
        })
        .catch(() => { });
    }
  }, [id, callHistoryRefresh]);

  const loadLeadAndCatalog = async () => {
    try {
      setLoading(true);
      const [leadRes, catRes, servRes, agentRes] = await Promise.all([
        opheService.getLeadById(id),
        opheService.getCategories({ isActive: true }),
        opheService.getServices({ isActive: true }),
        api.get('/users?role=customer_care&limit=100').catch(() => ({ data: { data: { users: [] } } }))
      ]);

      const leadData = leadRes.data?.data;
      const allCategories = catRes.data?.data || [];
      const allServices = servRes.data?.data || [];
      const allAgents = agentRes.data?.data?.users || agentRes.data?.users || (Array.isArray(agentRes.data?.data) ? agentRes.data.data : []);
      setLead(leadData);
      setCategories(allCategories);
      setServices(allServices);
      setCcAgents(Array.isArray(allAgents) ? allAgents : []);

      if (leadData) {
        const isS1Done = Boolean(leadData.ophePaymentData?.status === 'paid' || leadData.paymentStatus === 'paid');
        const isS3Done = Boolean(leadData.status === 'completed' || leadData.registrationStep === 3);
        const provSchedule = Array.isArray(leadData.opheProviderData?.dailyDutySchedule) ? leadData.opheProviderData.dailyDutySchedule : [];
        const allDaysDone = provSchedule.length > 0 && provSchedule.every(d => d.status === 'Completed');
        const isS2Done = Boolean(isS1Done && leadData.opheProviderData?.providerName?.trim() && (allDaysDone || isS3Done));

        let currentStep = 1;
        if (isS3Done) {
          currentStep = 3;
        } else if (isS1Done) {
          // If payment is paid, stay in Stage 2 (Active Care) until all days are completed or user moves to Stage 3
          currentStep = (allDaysDone && leadData.opheProviderData?.providerName?.trim()) ? 3 : 2;
        } else {
          currentStep = 1;
        }
        setActiveStageTab(currentStep);

        const sDetails = leadData.opheServiceDetails || {};
        const pData = leadData.ophePaymentData || {};
        const medInfo = leadData.opheMedicalInfo || {};
        const provData = leadData.opheProviderData || {};
        const payData = leadData.ophePayoutData || {};

        // 1. Auto-detect matched service from catalog
        const sServiceId = typeof sDetails.serviceId === 'object' ? sDetails.serviceId?._id : sDetails.serviceId;
        let matchedService = null;
        if (sServiceId) {
          matchedService = allServices.find(s => String(s._id) === String(sServiceId));
        }
        if (!matchedService && sDetails.packageName) {
          matchedService = allServices.find(s =>
            (s.serviceName && s.serviceName.toLowerCase() === sDetails.packageName.toLowerCase()) ||
            (s.name && s.name.toLowerCase() === sDetails.packageName.toLowerCase())
          );
        }
        if (!matchedService && leadData.treatmentRequired) {
          matchedService = allServices.find(s =>
            (s.serviceName && s.serviceName.toLowerCase().includes(leadData.treatmentRequired.toLowerCase())) ||
            (s.name && s.name.toLowerCase().includes(leadData.treatmentRequired.toLowerCase()))
          );
        }

        const shiftHours = Number(sDetails.shiftHours) || 12;
        const shiftType = sDetails.shiftType || (shiftHours === 24 ? '24 Hours (Full Day Live-in)' : '12 Hours (Day / Night Shift)');

        let catPrice = 999;
        if (matchedService) {
          if (shiftHours === 24 && matchedService.pricing?.hours24) {
            catPrice = matchedService.pricing.hours24;
          } else if (shiftHours === 12 && matchedService.pricing?.hours12) {
            catPrice = matchedService.pricing.hours12;
          } else if (matchedService.pricing?.hours12) {
            catPrice = matchedService.pricing.hours12;
          } else if (matchedService.pricing?.perDay) {
            catPrice = shiftHours === 24 ? Math.round(matchedService.pricing.perDay * 1.8) : matchedService.pricing.perDay;
          } else if (matchedService.durationPricing?.Day) {
            catPrice = shiftHours === 24 ? Math.round(matchedService.durationPricing.Day * 1.8) : matchedService.durationPricing.Day;
          } else if (matchedService.basePrice) {
            catPrice = matchedService.basePrice;
          }
        } else {
          catPrice = sDetails.catalogPrice || 999;
        }

        // Initial daily rate: negotiated rate saved, or fresh catalog price for 12h/24h
        let initDailyRate = sDetails.quotedPrice !== undefined && sDetails.quotedPrice !== '' ? Number(sDetails.quotedPrice) : '';
        if (initDailyRate === '' || isNaN(initDailyRate) || initDailyRate === 0) {
          initDailyRate = catPrice;
        }

        const initStartDate = sDetails.startDate ? new Date(sDetails.startDate).toISOString().split('T')[0] : '';
        const initEndDate = sDetails.endDate ? new Date(sDetails.endDate).toISOString().split('T')[0] : '';
        const initDays = sDetails.daysCount || (initStartDate && initEndDate ? calculateDaysBetween(initStartDate, initEndDate) : 7);
        const initGstRate = sDetails.gstRate !== undefined ? sDetails.gstRate : 18;
        const initApplyGst = sDetails.gstRate !== 0;
        const initDiscountType = sDetails.discountType || 'none';
        const initDiscountVal = sDetails.discountValue || '';

        // Already paid calculation: sum all verified paid entries from ophePaymentHistory
        let calculatedPaid = 0;
        if (Array.isArray(leadData.ophePaymentHistory) && leadData.ophePaymentHistory.length > 0) {
          const paidEntries = leadData.ophePaymentHistory.filter(h => h.status === 'paid');
          if (paidEntries.length > 0) {
            calculatedPaid = paidEntries.reduce((sum, h) => sum + (Number(h.amount) || 0), 0);
          }
        }
        if (calculatedPaid === 0 && pData.status === 'paid') {
          calculatedPaid = Number(pData.amount || sDetails.totalAmount || 0);
        }
        const alreadyPaid = calculatedPaid > 0 ? calculatedPaid : Number(sDetails.alreadyPaidAmount || 0);

        // Keep Option 1 (Standard Booking) by default; only switch to Option 2 if user explicitly clicks it
        setPaymentOptionMode(prev => prev === 'extension' ? 'extension' : 'standard');

        const defaultOrigDays = initDays || 7;
        const defaultOrigRate = initDailyRate || 1000;
        const defaultUpgRate = matchedService?.pricing?.hours24 || (defaultOrigRate ? Math.round(defaultOrigRate * 1.8) : 2000);
        const defaultServed = Math.min(2, Math.max(1, defaultOrigDays - 1));
        const defaultToUpgrade = Math.max(1, defaultOrigDays - defaultServed);

        setUpgradeForm({
          originalDays: defaultOrigDays,
          originalDailyRate: defaultOrigRate,
          originalShiftHours: shiftHours || 12,
          daysServed: defaultServed,
          daysToUpgrade: defaultToUpgrade,
          upgradedShiftHours: 24,
          upgradedDailyRate: defaultUpgRate
        });

        const initBilling = getComputedBilling({
          perDayRate: initDailyRate,
          daysCount: initDays,
          discountType: initDiscountType,
          discountValue: initDiscountVal,
          applyGst: initApplyGst,
          gstRate: initGstRate,
          alreadyPaidAmount: alreadyPaid > 0 ? alreadyPaid : 0,
          extensionSubMode: (alreadyPaid > 0 || pData.status === 'paid') ? 'shift_upgrade' : 'extra_days',
          originalDays: defaultOrigDays,
          originalDailyRate: defaultOrigRate,
          daysServed: defaultServed,
          daysToUpgrade: defaultToUpgrade,
          upgradedDailyRate: defaultUpgRate,
          shiftHours: 24
        });

        const serviceName = matchedService?.serviceName || matchedService?.name || sDetails.packageName || leadData.treatmentRequired || 'OPHE Care Service';

        setPaymentForm({
          categoryId: typeof sDetails.categoryId === 'object' ? sDetails.categoryId?._id : (sDetails.categoryId || (matchedService?.categoryId?._id || matchedService?.categoryId || '')),
          serviceId: matchedService?._id || sServiceId || '',
          packageName: serviceName,
          shiftHours: shiftHours,
          shiftType: shiftType,
          catalogPrice: catPrice,
          quotedPrice: initDailyRate,
          daysCount: initDays,
          discountType: initDiscountType,
          discountValue: initDiscountVal,
          applyGst: initApplyGst,
          gstRate: initGstRate,
          startDate: initStartDate,
          endDate: initEndDate,
          alreadyPaidAmount: alreadyPaid,
          totalPackagePrice: initBilling.totalAmount,
          amount: pData.amount || initBilling.totalAmount,
          description: pData.description || `Payment for ${serviceName} (${initDays} Days @ ₹${initBilling.perDayTotalCost}/day incl. ${initApplyGst ? `${initGstRate}% GST` : '0% GST'})`,
          channel: 'both',
          primaryEmail: leadData.patientEmail || '',
          primaryPhone: leadData.patientPhone || '',
          alternateEmail: leadData.alternateEmail || pData.alternateEmail || '',
          alternateWhatsapp: leadData.alternatePhone || pData.alternateWhatsapp || '',
          conditionNotes: medInfo.conditionNotes || '',
          vitalBloodPressure: medInfo.vitalBloodPressure || '',
          vitalSugar: medInfo.vitalSugar || '',
          vitalOxygenSpo2: medInfo.vitalOxygenSpo2 || '',
          mobilityStatus: medInfo.mobilityStatus || 'Independent'
        });
        setCurrentMedication(medInfo.currentMedication || '');

        const startDateFormatted = provData.careStartDate
          ? new Date(provData.careStartDate).toISOString().split('T')[0]
          : (sDetails.startDate ? new Date(sDetails.startDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]);

        const endDateFormatted = provData.expectedEndDate
          ? new Date(provData.expectedEndDate).toISOString().split('T')[0]
          : (sDetails.endDate ? new Date(sDetails.endDate).toISOString().split('T')[0] : new Date(Date.now() + 6 * 86400000).toISOString().split('T')[0]);

        setProviderForm({
          providerName: provData.providerName || '',
          providerPhone: sanitizePhoneNumber(provData.providerPhone || ''),
          providerEmail: provData.providerEmail || '',
          providerRole: provData.providerRole || 'Trained Care Attendant',
          shiftType: provData.shiftType || '12-Hour Day Shift',
          careStartDate: startDateFormatted,
          expectedEndDate: endDateFormatted,
          defaultStartTime: provData.defaultStartTime || (provData.shiftType?.includes('Night') ? '08:00 PM' : '08:00 AM'),
          defaultEndTime: provData.defaultEndTime || (provData.shiftType?.includes('Night') ? '08:00 AM' : '08:00 PM'),
          providerNotes: provData.providerNotes || '',
          dailyDutySchedule: Array.isArray(provData.dailyDutySchedule) && provData.dailyDutySchedule.length > 0 ? provData.dailyDutySchedule : []
        });

        setPayoutForm({
          finalAmountReceived: payData.finalAmountReceived || pData.amount || sDetails.totalAmount || sDetails.quotedPrice || '',
          providerPayoutAmount: payData.providerPayoutAmount || payData.providerPayout || payData.payoutAmount || '',
          payoutMode: payData.payoutMode || payData.paymentMethod || 'Bank Transfer / IMPS',
          payoutReferenceId: payData.payoutReferenceId || payData.payoutReference || '',
          payoutDate: payData.processedAt ? new Date(payData.processedAt).toISOString().split('T')[0] : (payData.payoutDate ? new Date(payData.payoutDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]),
          completionNotes: payData.completionNotes || payData.payoutNotes || payData.notes || '',
          patientFeedback: payData.patientFeedback || 'Very Satisfied (5 Star)'
        });
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load OPHE case data');
    } finally {
      setLoading(false);
    }
  };

  // Exotel Call Handler
  const handleExotelCall = async () => {
    if (!lead?.patientPhone) return toast.error('No patient phone number available.');
    if (!user?.phone) return toast.error('Your profile has no phone number. Update profile to make calls.');

    setIsCallingPatient(true);
    try {
      const res = await exotelInitiateCall({
        leadId: id,
        fromNumber: user.phone,
        toNumber: lead.patientPhone
      });
      toast.success(res?.message || 'Call initiated via Exotel. Connecting your phone...');
      setTimeout(() => setCallHistoryRefresh(p => p + 1), 3000);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to initiate call');
    } finally {
      setIsCallingPatient(false);
    }
  };

  const handleOpenQuickDispModal = () => {
    if (!latestCall) return;
    const eff = getEffectiveDisposition(latestCall);
    setQuickDispForm({
      disposition: eff === 'pending' ? 'callback_completed' : eff,
      dispositionNotes: latestCall.dispositionNotes || latestCall.notes || '',
      callReason: latestCall.callReason || ''
    });
    setShowQuickDispModal(true);
  };

  const handleSaveQuickDisp = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!latestCall?._id) return;
    setIsSavingQuickDisp(true);
    try {
      const res = await saveCallDisposition(latestCall._id, {
        disposition: quickDispForm.disposition,
        dispositionNotes: quickDispForm.dispositionNotes.trim(),
        notes: quickDispForm.dispositionNotes.trim(),
        callReason: quickDispForm.callReason.trim()
      });
      const updated = res?.data || {
        ...latestCall,
        disposition: quickDispForm.disposition,
        dispositionNotes: quickDispForm.dispositionNotes.trim(),
        notes: quickDispForm.dispositionNotes.trim(),
        callReason: quickDispForm.callReason.trim(),
        dispositionUpdatedAt: new Date().toISOString()
      };
      setLatestCall(updated);
      setCallHistoryRefresh(prev => prev + 1);
      toast.success('Follow-up status updated');
      setShowQuickDispModal(false);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to update status');
    } finally {
      setIsSavingQuickDisp(false);
    }
  };

  // Photo & Document Vault Handlers
  const handleVaultUpload = async (e, type = 'patient_photo') => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploadingDoc(true);
    try {
      const formData = new FormData();
      files.forEach(file => formData.append('documents', file));

      const uploadRes = await api.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const newDocs = uploadRes.data?.data?.files?.map((f, i) => ({
        name: files[i]?.name || `Document_${Date.now()}`,
        type: type,
        url: f.url,
        uploadedAt: new Date()
      })) || [];

      const updatedDocs = [...(lead.documents || []), ...newDocs];
      const res = await opheService.updateLead(id, { documents: updatedDocs });
      setLead(res.data?.data || { ...lead, documents: updatedDocs });
      toast.success(`${files.length} photo/document(s) uploaded successfully!`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to upload photo/document');
    } finally {
      setUploadingDoc(false);
    }
  };

  const handleFilePreview = (doc) => {
    if (!doc?.url) return;
    const isPDF = doc.url.toLowerCase().endsWith('.pdf') || doc.name?.toLowerCase().endsWith('.pdf');
    setPreviewFile({
      url: doc.url,
      name: doc.name || 'Document Preview',
      isPDF
    });
  };

  const handleDeleteDocument = async (docToDelete) => {
    if (!window.confirm(`Are you sure you want to delete ${docToDelete.name || 'this document'}?`)) return;
    try {
      const updatedDocs = (lead.documents || []).filter(d => (d._id && docToDelete._id ? d._id !== docToDelete._id : d.url !== docToDelete.url));
      const res = await opheService.updateLead(id, { documents: updatedDocs });
      setLead(res.data?.data || { ...lead, documents: updatedDocs });
      toast.success('Document deleted successfully');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete document');
    }
  };

  // Filter services by category
  const filteredServices = paymentForm.categoryId
    ? services.filter(s => {
      const cId = typeof s.categoryId === 'object' ? s.categoryId?._id : s.categoryId;
      return cId === paymentForm.categoryId;
    })
    : services;

  // ── True Total Collected across all paid transactions in ophePaymentHistory ──
  const totalPaidCollected = useMemo(() => {
    if (Array.isArray(lead?.ophePaymentHistory) && lead.ophePaymentHistory.length > 0) {
      const paidList = lead.ophePaymentHistory.filter(item => item.status === 'paid');
      if (paidList.length > 0) {
        return paidList.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
      }
    }
    return lead?.ophePaymentData?.status === 'paid'
      ? Number(lead?.ophePaymentData?.amount || lead?.opheServiceDetails?.totalAmount || 0)
      : Number(paymentForm.alreadyPaidAmount || 0);
  }, [lead?.ophePaymentHistory, lead?.ophePaymentData, lead?.opheServiceDetails, paymentForm.alreadyPaidAmount]);

  const isExtensionActive = showExtensionSection || paymentOptionMode === 'extension';
  const effectiveAlreadyPaid = isExtensionActive
    ? (totalPaidCollected > 0 ? totalPaidCollected : Number(paymentForm.alreadyPaidAmount || 0))
    : 0;

  const currentBilling = getComputedBilling({
    perDayRate: paymentForm.quotedPrice,
    daysCount: paymentForm.daysCount,
    discountType: paymentForm.discountType,
    discountValue: paymentForm.discountValue,
    applyGst: paymentForm.applyGst,
    gstRate: paymentForm.gstRate,
    alreadyPaidAmount: effectiveAlreadyPaid,
    extensionSubMode: isExtensionActive ? extensionSubMode : 'extra_days',
    originalDays: upgradeForm?.originalDays || paymentForm.daysCount,
    originalDailyRate: upgradeForm?.originalDailyRate || paymentForm.quotedPrice,
    daysServed: upgradeForm?.daysServed || 0,
    daysToUpgrade: upgradeForm?.daysToUpgrade || 1,
    upgradedDailyRate: upgradeForm?.upgradedDailyRate || 0,
    shiftHours: upgradeForm?.upgradedShiftHours || paymentForm.shiftHours
  });

  const handleUpgradeFormChange = (key, value) => {
    setUpgradeForm(prev => {
      const numVal = Math.max(0, Number(value) || 0);
      const updated = { ...prev, [key]: numVal };
      if (key === 'originalDays') {
        const srv = Math.min(Math.max(0, updated.originalDays - 1), prev.daysServed);
        updated.daysServed = srv;
        updated.daysToUpgrade = Math.max(1, updated.originalDays - srv);
      } else if (key === 'daysServed') {
        updated.daysToUpgrade = Math.max(1, prev.originalDays - numVal);
      }
      return updated;
    });
  };

  const handleServiceSelect = (serviceId) => {
    const selected = services.find(s => s._id === serviceId);
    if (selected) {
      const hours = paymentForm.shiftHours || 12;
      const rate12 = selected.pricing?.hours12 || selected.pricing?.perDay || selected.durationPricing?.Day || selected.basePrice || 999;
      const rate24 = selected.pricing?.hours24 || Math.round(rate12 * 1.8);
      const customRate = selected.pricing?.customHoursRate ? selected.pricing.customHoursRate * hours : rate12;

      const newDailyRate = hours === 24 ? rate24 : (hours === 12 ? rate12 : customRate);
      const catPrice = hours === 24 ? rate24 : rate12;
      const cId = typeof selected.categoryId === 'object' ? selected.categoryId?._id : selected.categoryId;

      setPaymentForm(prev => ({
        ...prev,
        serviceId,
        categoryId: cId || prev.categoryId,
        packageName: selected.serviceName || selected.name || 'OPHE Care Package',
        catalogPrice: catPrice,
        quotedPrice: newDailyRate,
        description: `Payment for ${selected.serviceName || selected.name} (${prev.daysCount} Days, ${prev.shiftType})`
      }));
    } else {
      setPaymentForm(prev => ({ ...prev, serviceId }));
    }
  };

  const handleShiftChange = (shiftHours, shiftTypeName) => {
    const selected = services.find(s => s._id === paymentForm.serviceId);
    const hrs = Number(shiftHours) || 12;
    let newDailyRate = paymentForm.quotedPrice;

    if (selected) {
      if (hrs === 24 && selected.pricing?.hours24) {
        newDailyRate = selected.pricing.hours24;
      } else if (hrs === 12 && selected.pricing?.hours12) {
        newDailyRate = selected.pricing.hours12;
      } else if (selected.pricing?.customHoursRate && hrs !== 12 && hrs !== 24) {
        newDailyRate = selected.pricing.customHoursRate * hrs;
      } else if (selected.pricing?.perDay) {
        newDailyRate = hrs === 24 ? Math.round(selected.pricing.perDay * 1.8) : selected.pricing.perDay;
      }
    } else {
      const catPrice = paymentForm.catalogPrice || 999;
      if (hrs === 24) {
        newDailyRate = Math.round(catPrice * 1.8);
      } else if (hrs === 12) {
        newDailyRate = catPrice;
      }
    }

    setPaymentForm(prev => ({
      ...prev,
      shiftHours: hrs,
      shiftType: shiftTypeName || (hrs === 24 ? '24 Hours (Full Day Live-in)' : hrs === 12 ? '12 Hours (Day / Night Shift)' : `${hrs} Hours Shift`),
      quotedPrice: newDailyRate
    }));
  };

  const handlePerDayBaseChange = (perDayBase) => {
    setPaymentForm(prev => ({
      ...prev,
      quotedPrice: perDayBase
    }));
  };

  const handleDaysChange = (days) => {
    if (days === '' || days === null || days === undefined) {
      setPaymentForm(prev => ({
        ...prev,
        daysCount: ''
      }));
      return;
    }
    const num = parseInt(days, 10);
    const d = isNaN(num) ? '' : Math.max(1, num);
    const start = paymentForm.startDate || new Date().toISOString().split('T')[0];
    const newEndDate = d ? addDaysToDate(start, d) : paymentForm.endDate;
    setPaymentForm(prev => ({
      ...prev,
      startDate: start,
      daysCount: d,
      endDate: newEndDate
    }));
  };

  const handleDaysCountChange = handleDaysChange;

  const handleDiscountTypeChange = (discountType) => {
    setPaymentForm(prev => ({
      ...prev,
      discountType,
      discountValue: discountType === 'none' ? '' : prev.discountValue
    }));
  };

  const handleDiscountValueChange = (discountValue) => {
    setPaymentForm(prev => ({
      ...prev,
      discountValue
    }));
  };

  const handleStartDateChange = (startDate) => {
    const days = Number(paymentForm.daysCount) || 1;
    const newEndDate = startDate ? addDaysToDate(startDate, days) : paymentForm.endDate;
    setPaymentForm(prev => ({
      ...prev,
      startDate,
      endDate: newEndDate
    }));
  };

  const handleEndDateChange = (endDate) => {
    let days = paymentForm.daysCount;
    if (paymentForm.startDate && endDate) {
      days = calculateDaysBetween(paymentForm.startDate, endDate);
    }
    setPaymentForm(prev => ({
      ...prev,
      endDate,
      daysCount: days
    }));
  };

  const handleGstToggle = (applyGst) => {
    setPaymentForm(prev => ({
      ...prev,
      applyGst
    }));
  };

  const handleGstRateChange = (rate) => {
    setPaymentForm(prev => ({
      ...prev,
      gstRate: Number(rate)
    }));
  };

  // Save Package & Vitals Updates
  const handleSavePackageAndVitals = async () => {
    if (isCaseSettled) return toast.error('This case has been completed and settled. Modifications are locked.');
    try {
      setSaving(true);
      const selectedService = services.find(s => s._id === paymentForm.serviceId);
      const billing = currentBilling;

      const payload = {
        opheServiceDetails: {
          categoryId: paymentForm.categoryId || undefined,
          serviceId: paymentForm.serviceId || undefined,
          packageName: selectedService?.serviceName || selectedService?.name || paymentForm.packageName || lead.opheServiceDetails?.packageName || 'OPHE Care Package',
          shiftHours: Number(paymentForm.shiftHours) || 12,
          shiftType: paymentForm.shiftType || '12-Hour Shift',
          catalogPrice: Number(paymentForm.catalogPrice) || 0,
          unitPrice: Number(paymentForm.quotedPrice) || 0,
          quotedPrice: Number(paymentForm.quotedPrice) || 0,
          daysCount: Number(paymentForm.daysCount) || 1,
          discountType: paymentForm.discountType || 'none',
          discountValue: Number(paymentForm.discountValue) || 0,
          discountAmount: billing.discountAmount,
          subtotalAmount: billing.discountedBaseTotal,
          gstRate: paymentForm.applyGst ? Number(paymentForm.gstRate || 18) : 0,
          gstAmount: billing.gstAmount,
          totalAmount: billing.totalAmount,
          price: billing.totalAmount,
          alreadyPaidAmount: billing.alreadyPaidAmount,
          balanceDue: billing.balanceDue,
          startDate: paymentForm.startDate || undefined,
          endDate: paymentForm.endDate || undefined,
          serviceNotes: paymentForm.conditionNotes
        },
        opheMedicalInfo: {
          conditionNotes: paymentForm.conditionNotes,
          vitalBloodPressure: paymentForm.vitalBloodPressure,
          vitalSugar: paymentForm.vitalSugar,
          vitalOxygenSpo2: paymentForm.vitalOxygenSpo2,
          mobilityStatus: paymentForm.mobilityStatus
        }
      };

      const res = await opheService.updateLead(id, payload);
      setLead(res.data?.data || lead);
      toast.success('Package, shift, discount & vitals saved successfully!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save package and vitals');
    } finally {
      setSaving(false);
    }
  };

  // Post-Payment: Save Current Medication Details
  const handleSaveCurrentMedication = async (medValue = currentMedication) => {
    if (isCaseSettled) return toast.error('This case has been completed and settled. Modifications are locked.');
    try {
      setSavingMedication(true);
      const existingMed = lead?.opheMedicalInfo || {};
      const valueToSave = (medValue !== undefined ? medValue : currentMedication || '').trim();
      const payload = {
        opheMedicalInfo: {
          ...existingMed,
          currentMedication: valueToSave
        }
      };
      const res = await opheService.updateLead(id, payload);
      const updatedLead = res.data?.data || {
        ...lead,
        opheMedicalInfo: {
          ...existingMed,
          currentMedication: valueToSave
        }
      };
      setLead(updatedLead);
      setCurrentMedication(valueToSave);
      toast.success('Patient current medication saved successfully!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save current medication');
    } finally {
      setSavingMedication(false);
    }
  };

  // Stage 1 & Top Extension: Generate Payment Link (Supports Total, Balance Due for Extensions & Mid-Care Shift Upgrades)
  const handleCreatePaymentLink = async (overrideMode) => {
    if (isCaseSettled) return toast.error('This case has been completed and settled. Modifications are locked.');
    const isExtension = typeof overrideMode === 'string'
      ? overrideMode === 'extension'
      : (showExtensionSection || paymentOptionMode === 'extension');
    const isShiftUpgrade = isExtension && extensionSubMode === 'shift_upgrade';
    const finalAmount = isShiftUpgrade
      ? currentBilling.balanceDue
      : isExtension && effectiveAlreadyPaid > 0
        ? currentBilling.balanceDue
        : currentBilling.totalAmount;

    if (isExtension && finalAmount <= 0) {
      return toast.error(`Package total / upgrade difference is ₹0. Please adjust days or rates to generate extension link.`);
    }

    if (!finalAmount || finalAmount <= 0) {
      return toast.error('Payment amount must be greater than ₹0.');
    }

    try {
      setGeneratingPayment(true);

      const selectedService = services.find(s => s._id === paymentForm.serviceId);
      const pkgName = selectedService?.serviceName || selectedService?.name || paymentForm.packageName || lead.opheServiceDetails?.packageName || 'OPHE Care Package';

      let desc = '';
      let serviceDetailsUpdate = {};

      if (isShiftUpgrade) {
        desc = `Care Shift Upgrade: ${currentBilling.days} Days upgraded to 24-Hour Live-in Care @ ₹${upgradeForm.upgradedDailyRate}/day (after ₹${safeInr(currentBilling.unusedCredit)} credit for unused 12h care) - Balance Due: ₹${safeInr(finalAmount)}`;
        serviceDetailsUpdate = {
          categoryId: paymentForm.categoryId || undefined,
          serviceId: paymentForm.serviceId || undefined,
          packageName: pkgName,
          shiftHours: Number(upgradeForm.upgradedShiftHours) || 24,
          shiftType: Number(upgradeForm.upgradedShiftHours) === 24 ? '24 Hours (Full Day Live-in)' : `${upgradeForm.upgradedShiftHours} Hours Shift`,
          unitPrice: Number(upgradeForm.upgradedDailyRate) || 0,
          quotedPrice: Number(upgradeForm.upgradedDailyRate) || 0,
          daysCount: Number(upgradeForm.originalDays) || 7,
          discountType: 'none',
          discountValue: 0,
          discountAmount: 0,
          subtotalAmount: currentBilling.netUpgradeBase,
          gstRate: paymentForm.applyGst ? Number(paymentForm.gstRate || 18) : 0,
          gstAmount: currentBilling.gstAmount,
          totalAmount: currentBilling.revisedGrossTotal || currentBilling.totalAmount,
          price: currentBilling.revisedGrossTotal || currentBilling.totalAmount,
          alreadyPaidAmount: effectiveAlreadyPaid || currentBilling.consumedBaseCost,
          balanceDue: finalAmount,
          startDate: paymentForm.startDate || undefined,
          endDate: paymentForm.endDate || undefined,
          serviceNotes: paymentForm.conditionNotes,
          extensionSubMode: 'shift_upgrade',
          upgradeBreakdown: {
            originalDays: upgradeForm.originalDays,
            originalDailyRate: upgradeForm.originalDailyRate,
            daysServed: upgradeForm.daysServed,
            daysToUpgrade: upgradeForm.daysToUpgrade,
            upgradedDailyRate: upgradeForm.upgradedDailyRate,
            unusedCredit: currentBilling.unusedCredit,
            netUpgradeBase: currentBilling.netUpgradeBase
          }
        };
      } else {
        desc = paymentForm.description || (isExtension
          ? `Extension for ${pkgName} (${currentBilling.days} Days, ${paymentForm.shiftType}) - Balance Due: ₹${safeInr(finalAmount)}`
          : `Payment for ${pkgName} (${currentBilling.days} Days @ ₹${currentBilling.perDayTotalCost}/day incl. ${paymentForm.applyGst ? `${paymentForm.gstRate}% GST` : '0% GST'})`);

        serviceDetailsUpdate = {
          categoryId: paymentForm.categoryId || undefined,
          serviceId: paymentForm.serviceId || undefined,
          packageName: pkgName,
          shiftHours: Number(paymentForm.shiftHours) || 12,
          shiftType: paymentForm.shiftType || '12-Hour Shift',
          catalogPrice: Number(paymentForm.catalogPrice) || 0,
          unitPrice: Number(paymentForm.quotedPrice) || 0,
          quotedPrice: Number(paymentForm.quotedPrice) || 0,
          daysCount: Number(paymentForm.daysCount) || 1,
          discountType: paymentForm.discountType || 'none',
          discountValue: Number(paymentForm.discountValue) || 0,
          discountAmount: currentBilling.discountAmount,
          subtotalAmount: currentBilling.discountedBaseTotal,
          gstRate: paymentForm.applyGst ? Number(paymentForm.gstRate || 18) : 0,
          gstAmount: currentBilling.gstAmount,
          totalAmount: currentBilling.totalAmount,
          price: currentBilling.totalAmount,
          alreadyPaidAmount: currentBilling.alreadyPaidAmount,
          balanceDue: currentBilling.balanceDue,
          startDate: paymentForm.startDate || undefined,
          endDate: paymentForm.endDate || undefined,
          serviceNotes: paymentForm.conditionNotes,
          extensionSubMode: isExtension ? 'extra_days' : undefined
        };
      }

      // Auto-save package details first
      const payload = {
        opheServiceDetails: serviceDetailsUpdate,
        opheMedicalInfo: {
          conditionNotes: paymentForm.conditionNotes,
          vitalBloodPressure: paymentForm.vitalBloodPressure,
          vitalSugar: paymentForm.vitalSugar,
          vitalOxygenSpo2: paymentForm.vitalOxygenSpo2,
          mobilityStatus: paymentForm.mobilityStatus
        }
      };

      await opheService.updateLead(id, payload);

      const res = await opheService.createPaymentLink(id, {
        amount: finalAmount,
        description: desc,
        primaryEmail: paymentForm.primaryEmail || lead.patientEmail || '',
        primaryWhatsapp: paymentForm.primaryPhone || lead.patientPhone || '',
        alternateEmail: paymentForm.alternateEmail || lead.alternateEmail || '',
        alternateWhatsapp: paymentForm.alternateWhatsapp || lead.alternatePhone || '',
        channel: paymentForm.channel || 'all'
      });

      if (res.data?.data && res.data.data.patientName) {
        setLead(res.data.data);
      } else {
        await loadLeadAndCatalog();
      }
      const hasEmail = Boolean((paymentForm.primaryEmail || lead.patientEmail || paymentForm.alternateEmail || lead.alternateEmail || '').trim());
      const emailNote = hasEmail ? ' & OPHE template emailed' : '';
      toast.success(isShiftUpgrade
        ? `⚡ Shift Upgrade Payment Link for ₹${safeInr(finalAmount)} generated${emailNote}!`
        : isExtension
          ? `⚡ Extension Payment Link for ₹${safeInr(finalAmount)} generated${emailNote}!`
          : `⚡ Razorpay Payment Link for ₹${safeInr(finalAmount)} generated${emailNote}!`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to generate payment link');
    } finally {
      setGeneratingPayment(false);
    }
  };

  // Stage 1: Dispatch Link Notifications
  const handleSendNotification = async (channel, customLink = null, target = 'all') => {
    try {
      setSendingNotification(true);
      const activeLink = customLink || lead.ophePaymentData?.paymentLink || (Array.isArray(lead.ophePaymentHistory) && lead.ophePaymentHistory[0]?.paymentLink);
      if (!activeLink) {
        toast.error('No payment link available to dispatch. Please generate a link first.');
        return;
      }
      const res = await opheService.sendPaymentLink(id, {
        channel,
        target,
        primaryEmail: paymentForm.primaryEmail || lead.patientEmail || '',
        primaryWhatsapp: paymentForm.primaryPhone || lead.patientPhone || '',
        alternateEmail: paymentForm.alternateEmail || lead.alternateEmail || '',
        alternateWhatsapp: paymentForm.alternateWhatsapp || lead.alternatePhone || '',
        paymentLink: activeLink
      });
      if (res.data?.data && res.data.data.patientName) {
        setLead(res.data.data);
      } else {
        await loadLeadAndCatalog();
      }
      const targetLabel = target === 'primary' ? 'Primary Contact' : target === 'alternate' ? 'Alternative Contact' : 'All Contacts';
      toast.success(`Payment link dispatched via ${channel.toUpperCase()} to ${targetLabel}!`);
    } catch (err) {
      toast.error(err.response?.data?.message || `Failed to send ${channel} notification`);
    } finally {
      setSendingNotification(false);
    }
  };

  // Stage 1: Delete / Cancel Payment Record
  const handleDeletePaymentRecord = async (paymentId) => {
    if (!window.confirm('Are you sure you want to delete this payment link record?')) return;
    try {
      const res = await opheService.deletePaymentRecord(id, paymentId);
      if (res.data?.data && res.data.data.patientName) {
        setLead(res.data.data);
      } else {
        await loadLeadAndCatalog();
      }
      toast.success('Payment link record removed successfully');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete payment record');
    }
  };

  // Stage 1: Sync Razorpay Payment
  const handleSyncPayment = async (silent = false) => {
    try {
      setSyncingPayment(true);
      const res = await opheService.syncPayment(id);
      const updatedLead = res.data?.data;
      if (updatedLead && updatedLead.patientName) {
        setLead(updatedLead);
      } else {
        await loadLeadAndCatalog();
      }

      if (updatedLead?.ophePaymentData?.status === 'paid') {
        if (!silent) toast.success('🎉 Payment verified as PAID! You can now proceed to Stage 2.');
        setActiveStageTab(2);
      } else {
        const syncMsg = res.data?.message || res.data?.syncResult?.message || 'Payment is still pending on Razorpay.';
        if (!silent) toast(syncMsg, { icon: 'ℹ️' });
      }
    } catch (err) {
      console.error('[OPHE] Failed to sync payment:', err);
      if (!silent) toast.error(err.response?.data?.message || err.message || 'Failed to sync payment');
    } finally {
      setSyncingPayment(false);
    }
  };

  // ── Multi-Day Care Duty Schedule Handlers ──────────────────────────────────
  const handleGenerateDailySchedule = (customDaysCount = null) => {
    const start = providerForm.careStartDate ? new Date(providerForm.careStartDate) : new Date();
    let numDays = Number(lead?.opheServiceDetails?.daysCount || paymentForm.daysCount || 7);

    if (customDaysCount && typeof customDaysCount === 'number') {
      numDays = customDaysCount;
    } else if (providerForm.expectedEndDate && providerForm.careStartDate) {
      const end = new Date(providerForm.expectedEndDate);
      const diffTime = end.getTime() - start.getTime();
      const calculated = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      if (calculated > 0 && calculated <= 180) {
        numDays = calculated;
      }
    }

    const defaultCCName = lead?.assignedCustomerCare?.name || (lead?.assignedCustomerCare?.firstName ? `${lead.assignedCustomerCare.firstName} ${lead.assignedCustomerCare.lastName || ''}`.trim() : '') || (user?.firstName ? `${user.firstName} ${user.lastName || ''}`.trim() : user?.name || '');
    const defaultCCPhone = lead?.assignedCustomerCare?.phone || user?.phone || '';
    const defaultCCId = lead?.assignedCustomerCare?._id || user?._id || '';

    const newSchedule = [];
    for (let i = 0; i < numDays; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      const dateStr = d.toISOString().split('T')[0];

      // Keep existing notes / status if matching date exists
      const existing = (providerForm.dailyDutySchedule || []).find(item => item.date === dateStr);

      newSchedule.push({
        dayNumber: i + 1,
        date: dateStr,
        startTime: existing?.startTime || providerForm.defaultStartTime || (providerForm.shiftType?.includes('Night') ? '08:00 PM' : '08:00 AM'),
        endTime: existing?.endTime || providerForm.defaultEndTime || (providerForm.shiftType?.includes('Night') ? '08:00 AM' : '08:00 PM'),
        assignedCCName: existing?.assignedCCName || defaultCCName,
        assignedCCPhone: existing?.assignedCCPhone || defaultCCPhone,
        assignedCCId: existing?.assignedCCId || defaultCCId,
        providerName: existing?.providerName || providerForm.providerName || lead.opheProviderData?.providerName || 'Primary Provider',
        providerPhone: existing?.providerPhone || providerForm.providerPhone || lead.opheProviderData?.providerPhone || '',
        status: existing?.status || (i === 0 ? 'In-Progress' : 'Scheduled'),
        notes: existing?.notes || ''
      });
    }

    const finalEnd = new Date(start);
    finalEnd.setDate(start.getDate() + (numDays - 1));
    const finalEndDateStr = finalEnd.toISOString().split('T')[0];

    setProviderForm(prev => ({
      ...prev,
      expectedEndDate: finalEndDateStr,
      dailyDutySchedule: newSchedule
    }));
    toast.success(`Generated ${numDays}-day duty roster (${newSchedule[0]?.date} to ${finalEndDateStr})`);
  };

  const handleMarkAllDaysCompleted = () => {
    if (isCaseSettled) return toast.error('This case has been completed and settled. Modifications are locked.');
    if (!providerForm.dailyDutySchedule || providerForm.dailyDutySchedule.length === 0) {
      return toast.error('No duty schedule days found to mark complete.');
    }
    setProviderForm(prev => ({
      ...prev,
      dailyDutySchedule: (prev.dailyDutySchedule || []).map(day => ({
        ...day,
        status: 'Completed'
      }))
    }));
    toast.success('All scheduled days marked as Completed ✓');
  };

  const handleProceedToStage3 = async () => {
    if (providerForm.providerName && providerForm.providerPhone) {
      try {
        setSaving(true);
        const res = await opheService.assignProvider(id, {
          ...providerForm,
          leadStatus: 'care_in_progress'
        });
        setLead(res.data?.data || lead);
      } catch (_) {
        // proceed
      } finally {
        setSaving(false);
      }
    }
    setShowExtensionSection(false);
    setPaymentOptionMode('standard');
    setActiveStageTab(3);
    toast.success('Proceeding to Stage 3: Care Completion & Provider Payout');
  };

  const handleOpenAddDutyModal = () => {
    const list = providerForm.dailyDutySchedule || [];
    const last = list[list.length - 1];
    let nextDate = new Date();
    if (last?.date) {
      nextDate = new Date(last.date);
      nextDate.setDate(nextDate.getDate() + 1);
    }
    const dateStr = nextDate.toISOString().split('T')[0];

    const defaultCCName = lead?.assignedCustomerCare?.name || (lead?.assignedCustomerCare?.firstName ? `${lead.assignedCustomerCare.firstName} ${lead.assignedCustomerCare.lastName || ''}`.trim() : '') || (user?.firstName ? `${user.firstName} ${user.lastName || ''}`.trim() : user?.name || '');
    const defaultCCPhone = lead?.assignedCustomerCare?.phone || user?.phone || '';
    const defaultCCId = lead?.assignedCustomerCare?._id || user?._id || '';

    const primaryName = (lead?.opheProviderData?.providerName || providerForm.providerName || '').trim();
    const primaryPhone = (lead?.opheProviderData?.providerPhone || providerForm.providerPhone || '').trim();
    const primaryEmail = (lead?.opheProviderData?.providerEmail || providerForm.providerEmail || '').trim();
    const primaryRole = lead?.opheProviderData?.providerRole || providerForm.providerRole || 'Trained Care Attendant';
    const primaryShift = lead?.opheProviderData?.shiftType || providerForm.shiftType || '12-Hour Day Shift';

    setDutyModalForm({
      mode: 'add',
      index: list.length,
      dayNumber: list.length + 1,
      date: dateStr,
      startTime: providerForm.defaultStartTime || (providerForm.shiftType?.includes('Night') ? '08:00 PM' : '08:00 AM'),
      endTime: providerForm.defaultEndTime || (providerForm.shiftType?.includes('Night') ? '08:00 AM' : '08:00 PM'),
      assignedCCId: defaultCCId,
      assignedCCName: defaultCCName,
      assignedCCPhone: defaultCCPhone,
      providerOption: 'old',
      providerName: primaryName,
      providerPhone: primaryPhone,
      providerEmail: primaryEmail,
      providerRole: primaryRole,
      shiftType: primaryShift,
      status: 'Scheduled',
      notes: ''
    });
    setShowDutyModal(true);
  };

  const handleOpenEditDutyModal = (day, idx, forceOption = null) => {
    if (isCaseSettled) return toast.error('This case has been completed and settled. Modifications are locked.');
    const defaultCCName = lead?.assignedCustomerCare?.name || (lead?.assignedCustomerCare?.firstName ? `${lead.assignedCustomerCare.firstName} ${lead.assignedCustomerCare.lastName || ''}`.trim() : '') || (user?.firstName ? `${user.firstName} ${user.lastName || ''}`.trim() : user?.name || '');
    const defaultCCPhone = lead?.assignedCustomerCare?.phone || user?.phone || '';
    const defaultCCId = lead?.assignedCustomerCare?._id || user?._id || '';

    const primaryName = (lead?.opheProviderData?.providerName || providerForm.providerName || '').trim();
    const primaryPhone = (lead?.opheProviderData?.providerPhone || providerForm.providerPhone || '').trim();
    const primaryEmail = (lead?.opheProviderData?.providerEmail || providerForm.providerEmail || '').trim();
    const primaryRole = lead?.opheProviderData?.providerRole || providerForm.providerRole || 'Trained Care Attendant';
    const primaryShift = lead?.opheProviderData?.shiftType || providerForm.shiftType || '12-Hour Day Shift';

    const isDayPrimary = !day.providerName || (primaryName && day.providerName.trim().toLowerCase() === primaryName.toLowerCase()) || day.providerName === 'Primary Provider';

    const selectedOption = forceOption || (isDayPrimary ? 'old' : 'new');

    const normDate = getNormalizedDate(day.date) || new Date().toISOString().split('T')[0];
    const isDayFuture = Boolean(normDate && normDate > getTodayDateStr());

    setDutyModalForm({
      mode: 'edit',
      index: idx,
      dayNumber: day.dayNumber || idx + 1,
      date: normDate,
      startTime: day.startTime || '08:00 AM',
      endTime: day.endTime || '08:00 PM',
      assignedCCId: day.assignedCCId || defaultCCId,
      assignedCCName: day.assignedCCName || defaultCCName,
      assignedCCPhone: day.assignedCCPhone || defaultCCPhone,
      providerOption: selectedOption,
      providerName: selectedOption === 'old' ? primaryName : (day.providerName || ''),
      providerPhone: selectedOption === 'old' ? primaryPhone : (day.providerPhone || ''),
      providerEmail: selectedOption === 'old' ? primaryEmail : (day.providerEmail || ''),
      providerRole: selectedOption === 'old' ? primaryRole : (day.providerRole || 'Trained Care Attendant'),
      shiftType: selectedOption === 'old' ? primaryShift : (day.shiftType || '12-Hour Day Shift'),
      status: isDayFuture ? 'Scheduled' : (day.status || 'Scheduled'),
      notes: day.notes || ''
    });
    setShowDutyModal(true);
  };

  const handleSaveDutyModal = (e) => {
    if (e) e.preventDefault();
    const { mode, index, dayNumber, date, startTime, endTime, assignedCCId, assignedCCName, assignedCCPhone, providerOption, providerName, providerPhone, providerEmail, providerRole, shiftType, status, notes } = dutyModalForm;

    if (!date) return toast.error('Please select a duty date.');
    if (!startTime || !endTime) return toast.error('Please specify work start and end timings.');

    const primaryName = (lead?.opheProviderData?.providerName || providerForm.providerName || '').trim();
    const primaryPhone = (lead?.opheProviderData?.providerPhone || providerForm.providerPhone || '').trim();
    const primaryEmail = (lead?.opheProviderData?.providerEmail || providerForm.providerEmail || '').trim();
    const primaryRole = lead?.opheProviderData?.providerRole || providerForm.providerRole || 'Trained Care Attendant';
    const primaryShift = lead?.opheProviderData?.shiftType || providerForm.shiftType || '12-Hour Day Shift';

    let finalProvName = (providerName || '').trim();
    let finalProvPhone = (providerPhone || '').trim();
    let finalProvEmail = (providerEmail || '').trim();
    let finalProvRole = providerRole || 'Trained Care Attendant';
    let finalShiftType = shiftType || '12-Hour Day Shift';

    if (providerOption === 'old') {
      finalProvName = primaryName || finalProvName || 'Primary Provider';
      finalProvPhone = primaryPhone || finalProvPhone;
      finalProvEmail = primaryEmail;
      finalProvRole = primaryRole;
      finalShiftType = primaryShift;
    }

    if (!finalProvName) {
      return toast.error('Please specify provider name for this shift.');
    }

    const isModalFuture = Boolean(
      date && getNormalizedDate(date) > getTodayDateStr()
    );
    const finalDutyStatus = isModalFuture ? 'Scheduled' : (status || 'Scheduled');

    const newRecord = {
      dayNumber: Number(dayNumber) || 1,
      date,
      startTime,
      endTime,
      assignedCCId: assignedCCId || undefined,
      assignedCCName: assignedCCName || '',
      assignedCCPhone: assignedCCPhone || '',
      providerName: finalProvName,
      providerPhone: finalProvPhone || '',
      providerEmail: finalProvEmail || '',
      providerRole: finalProvRole,
      shiftType: finalShiftType,
      status: finalDutyStatus,
      notes: notes || ''
    };

    setProviderForm(prev => {
      const list = [...(prev.dailyDutySchedule || [])];
      if (mode === 'edit' && index !== null && list[index]) {
        list[index] = newRecord;
      } else {
        list.push(newRecord);
      }
      return { ...prev, dailyDutySchedule: list };
    });

    setShowDutyModal(false);
    toast.success(mode === 'edit' ? `Day ${dayNumber} shift updated with ${finalProvName} ✓` : `Day ${dayNumber} added to duty roster ✓`);
  };


  const handleUpdateDutyDay = (index, field, value) => {
    if (isCaseSettled) return toast.error('This case has been completed and settled. Modifications are locked.');
    const list = providerForm.dailyDutySchedule || lead?.opheProviderData?.dailyDutySchedule || [];
    const targetDay = list[index];
    const targetNorm = targetDay?.date ? getNormalizedDate(targetDay.date) : '';
    const todayLocal = getTodayDateStr();

    if (field === 'status' && targetNorm && targetNorm > todayLocal) {
      return toast.error(`Duty status for ${targetDay.date} is in the future and locked to Scheduled.`);
    }

    setProviderForm(prev => {
      const updated = [...(prev.dailyDutySchedule || [])];
      if (updated[index]) {
        updated[index] = { ...updated[index], [field]: value };
      }
      return { ...prev, dailyDutySchedule: updated };
    });
  };

  const handleAddDutyDay = () => {
    if (isCaseSettled) return toast.error('This case has been completed and settled. Modifications are locked.');
    setProviderForm(prev => {
      const list = [...(prev.dailyDutySchedule || [])];
      const last = list[list.length - 1];
      let nextDate = new Date();
      if (last?.date) {
        nextDate = new Date(last.date);
        nextDate.setDate(nextDate.getDate() + 1);
      }
      const dateStr = nextDate.toISOString().split('T')[0];
      list.push({
        dayNumber: list.length + 1,
        date: dateStr,
        startTime: prev.defaultStartTime || '08:00 AM',
        endTime: prev.defaultEndTime || '08:00 PM',
        providerName: prev.providerName || '',
        providerPhone: prev.providerPhone || '',
        status: 'Scheduled',
        notes: ''
      });
      return { ...prev, expectedEndDate: dateStr, dailyDutySchedule: list };
    });
    toast.success('Added new duty day');
  };

  const handleRemoveDutyDay = (index) => {
    if (isCaseSettled) return toast.error('This case has been completed and settled. Modifications are locked.');
    setProviderForm(prev => {
      const filtered = (prev.dailyDutySchedule || []).filter((_, i) => i !== index);
      const renumbered = filtered.map((item, i) => ({ ...item, dayNumber: i + 1 }));
      return { ...prev, dailyDutySchedule: renumbered };
    });
    toast.success('Removed duty day');
  };

  const handleDuplicateDutyDay = (index) => {
    if (isCaseSettled) return toast.error('This case has been completed and settled. Modifications are locked.');
    setProviderForm(prev => {
      const list = [...(prev.dailyDutySchedule || [])];
      const source = list[index];
      if (!source) return prev;
      let nextDate = new Date();
      if (source.date) {
        nextDate = new Date(source.date);
        nextDate.setDate(nextDate.getDate() + 1);
      }
      const cloned = {
        ...source,
        dayNumber: list.length + 1,
        date: nextDate.toISOString().split('T')[0],
        status: 'Scheduled'
      };
      list.push(cloned);
      return { ...prev, dailyDutySchedule: list };
    });
    toast.success('Cloned shift to next day ✓');
  };

  const handleInlineAddDuty = (e) => {
    if (e) e.preventDefault();
    if (!quickAddForm.date) return toast.error('Please select a duty date.');
    const defaultCCName = lead?.assignedCustomerCare?.name || (user?.firstName ? `${user.firstName} ${user.lastName || ''}`.trim() : user?.name || 'Care Coordinator');
    const defaultCCPhone = lead?.assignedCustomerCare?.phone || user?.phone || '';
    const defaultCCId = lead?.assignedCustomerCare?._id || user?._id || undefined;

    setProviderForm(prev => {
      const list = [...(prev.dailyDutySchedule || [])];
      const newRecord = {
        dayNumber: list.length + 1,
        date: quickAddForm.date,
        startTime: quickAddForm.startTime || prev.defaultStartTime || '08:00 AM',
        endTime: quickAddForm.endTime || prev.defaultEndTime || '08:00 PM',
        assignedCCId: quickAddForm.assignedCCId || defaultCCId,
        assignedCCName: quickAddForm.assignedCCName || defaultCCName,
        assignedCCPhone: quickAddForm.assignedCCPhone || defaultCCPhone,
        providerName: quickAddForm.providerName || prev.providerName || lead.opheProviderData?.providerName || 'Primary Provider',
        providerPhone: quickAddForm.providerPhone || prev.providerPhone || lead.opheProviderData?.providerPhone || '',
        status: quickAddForm.status || 'Scheduled',
        notes: quickAddForm.notes || ''
      };
      return { ...prev, dailyDutySchedule: [...list, newRecord] };
    });

    setQuickAddForm(prev => {
      let nextDate = new Date(prev.date);
      nextDate.setDate(nextDate.getDate() + 1);
      return {
        ...prev,
        date: nextDate.toISOString().split('T')[0],
        notes: ''
      };
    });
    setShowInlineAdd(false);
    toast.success('Added new duty shift to roster ✓');
  };

  const handleApplyDefaultTimingsToAllDays = () => {
    if (!providerForm.dailyDutySchedule || providerForm.dailyDutySchedule.length === 0) {
      return toast.error('Generate or add duty days first.');
    }
    setProviderForm(prev => ({
      ...prev,
      dailyDutySchedule: prev.dailyDutySchedule.map(day => ({
        ...day,
        startTime: prev.defaultStartTime,
        endTime: prev.defaultEndTime
      }))
    }));
    toast.success(`Applied ${providerForm.defaultStartTime} – ${providerForm.defaultEndTime} to all days!`);
  };

  const handleQuickStatusToggle = (index) => {
    setProviderForm(prev => {
      const updated = [...(prev.dailyDutySchedule || [])];
      if (updated[index]) {
        const cur = updated[index].status;
        const next = cur === 'Completed' ? 'Scheduled' : cur === 'In-Progress' ? 'Completed' : 'In-Progress';
        updated[index] = { ...updated[index], status: next };
      }
      return { ...prev, dailyDutySchedule: updated };
    });
  };

  // Stage 2: Save Duty Roster (Persists daily schedule without overwriting custom daily provider assignments)
  const handleSaveDutyRoster = async () => {
    if (isCaseSettled) return toast.error('This case has been completed and settled. Modifications are locked.');
    if (!lead) return;
    try {
      setSaving(true);
      const res = await opheService.assignProvider(id, {
        ...providerForm,
        dailyDutySchedule: providerForm.dailyDutySchedule || [],
        leadStatus: lead.status || 'care_in_progress'
      });
      const updatedLead = res.data?.data || lead;
      setLead(updatedLead);
      toast.success('Duty roster & daily provider assignments saved successfully ✓');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save duty roster');
    } finally {
      setSaving(false);
    }
  };

  // Stage 2: Assign / Reassign Main Provider & Start Active Care
  const handleAssignProvider = async () => {
    if (isCaseSettled) return toast.error('This case has been completed and settled. Modifications are locked.');
    const cleanedPhone = sanitizePhoneNumber(providerForm.providerPhone);
    if (!providerForm.providerName || !cleanedPhone) {
      return toast.error('Please enter provider full name and valid 10-digit contact number.');
    }

    const bookedDays = Number(lead?.opheServiceDetails?.daysCount || paymentForm.daysCount || 7);
    let scheduleToSave = [...(providerForm.dailyDutySchedule || [])];

    // Auto initialize schedule if not generated or empty
    if (scheduleToSave.length === 0) {
      const start = providerForm.careStartDate ? new Date(providerForm.careStartDate) : new Date();
      const defaultCCName = lead?.assignedCustomerCare?.name || (user?.firstName ? `${user.firstName} ${user.lastName || ''}`.trim() : user?.name || 'Care Coordinator');
      const defaultCCPhone = lead?.assignedCustomerCare?.phone || user?.phone || '';
      const defaultCCId = lead?.assignedCustomerCare?._id || user?._id || undefined;

      for (let i = 0; i < bookedDays; i++) {
        const d = new Date(start);
        d.setDate(start.getDate() + i);
        scheduleToSave.push({
          dayNumber: i + 1,
          date: d.toISOString().split('T')[0],
          startTime: providerForm.defaultStartTime || '08:00 AM',
          endTime: providerForm.defaultEndTime || '08:00 PM',
          assignedCCId: defaultCCId,
          assignedCCName: defaultCCName,
          assignedCCPhone: defaultCCPhone,
          providerName: providerForm.providerName.trim(),
          providerPhone: cleanedPhone,
          status: 'Scheduled',
          notes: ''
        });
      }
    } else {
      // If schedule exists and primary provider was updated, update ONLY days that were using the default/primary provider
      // Preserves daily custom/new provider assignments!
      const previousPrimaryName = (lead?.opheProviderData?.providerName || '').trim().toLowerCase();
      scheduleToSave = scheduleToSave.map(day => {
        const dayProvName = (day.providerName || '').trim().toLowerCase();
        const isPrimaryOrUnset = !dayProvName || dayProvName === 'primary provider' || (previousPrimaryName && dayProvName === previousPrimaryName);
        if (day.status === 'Scheduled' && isPrimaryOrUnset) {
          return {
            ...day,
            providerName: providerForm.providerName.trim(),
            providerPhone: cleanedPhone,
            startTime: providerForm.defaultStartTime || day.startTime,
            endTime: providerForm.defaultEndTime || day.endTime
          };
        }
        return day;
      });
    }

    try {
      setSaving(true);
      const res = await opheService.assignProvider(id, {
        ...providerForm,
        providerPhone: cleanedPhone,
        dailyDutySchedule: scheduleToSave,
        leadStatus: 'care_in_progress'
      });
      const updatedLead = res.data?.data || lead;
      setLead(updatedLead);
      setProviderForm(prev => ({
        ...prev,
        dailyDutySchedule: scheduleToSave
      }));
      setIsEditingProvider(false);
      setShowExtensionSection(false);
      setPaymentOptionMode('standard');
      setActiveStageTab(2);
      toast.success('Care Provider assigned & Duty Schedule saved! Case is now in Stage 2: Active Care.');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to assign provider');
    } finally {
      setSaving(false);
    }
  };

  // Stage 3: Complete Case & Settle Provider Payout
  const handleCompletePayout = async () => {
    if (lead?.status === 'completed' || (lead?.ophePayoutData?.payoutStatus === 'processed' && (Number(lead?.ophePayoutData?.payoutAmount) > 0 || Number(lead?.ophePayoutData?.providerPayout) > 0))) {
      return toast.error('This case has already been completed and settled. Modifications are locked.');
    }

    if (!payoutForm.providerPayoutAmount || Number(payoutForm.providerPayoutAmount) <= 0) {
      return toast.error('Please enter provider payout compensation amount.');
    }

    try {
      setSaving(true);
      const res = await opheService.completeCase(id, {
        finalAmountReceived: Number(payoutForm.finalAmountReceived) || 0,
        providerPayoutAmount: Number(payoutForm.providerPayoutAmount) || 0,
        payoutMode: payoutForm.payoutMode,
        payoutReferenceId: payoutForm.payoutReferenceId,
        payoutDate: payoutForm.payoutDate,
        completionNotes: payoutForm.completionNotes,
        patientFeedback: payoutForm.patientFeedback
      });
      const updatedLead = res.data?.data || lead;
      setLead(updatedLead);
      if (updatedLead?.ophePayoutData) {
        const pd = updatedLead.ophePayoutData;
        setPayoutForm({
          finalAmountReceived: pd.finalAmountReceived || payoutForm.finalAmountReceived,
          providerPayoutAmount: pd.providerPayoutAmount || pd.providerPayout || pd.payoutAmount || payoutForm.providerPayoutAmount,
          payoutMode: pd.payoutMode || pd.paymentMethod || payoutForm.payoutMode,
          payoutReferenceId: pd.payoutReferenceId || pd.payoutReference || payoutForm.payoutReferenceId,
          payoutDate: pd.processedAt ? new Date(pd.processedAt).toISOString().split('T')[0] : payoutForm.payoutDate,
          completionNotes: pd.completionNotes || pd.payoutNotes || payoutForm.completionNotes,
          patientFeedback: pd.patientFeedback || payoutForm.patientFeedback
        });
      }
      setShowExtensionSection(false);
      setPaymentOptionMode('standard');
      setActiveStageTab(3);
      toast.success('🎉 OPHE Care case successfully completed and payout settled!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to complete OPHE case');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex flex-col items-center justify-center min-h-[60vh] gap-3">
        <div className="w-10 h-10 border-4 border-teal-600 border-t-transparent rounded-full animate-spin" />
        <span className="text-sm font-semibold text-slate-500">Loading OPHE Care Case...</span>
      </div>
    );
  }

  if (!lead) {
    return (
      <div className="p-8 text-center space-y-4">
        <AlertCircle className="w-12 h-12 text-rose-500 mx-auto" />
        <h2 className="text-lg font-bold text-slate-800">OPHE Case Not Found</h2>
        <Link to="/ophe/pipeline" className="text-sm text-teal-600 font-semibold hover:underline">
          Return to OPHE Pipeline
        </Link>
      </div>
    );
  }

  const isStage1Done = Boolean(lead?.ophePaymentData?.status === 'paid' || lead?.paymentStatus === 'paid');
  const isStage3Done = Boolean(lead?.status === 'completed' || lead?.registrationStep === 3);
  const isCaseSettled = Boolean(
    lead?.status === 'completed' ||
    (lead?.ophePayoutData?.payoutStatus === 'processed' &&
      (Number(lead?.ophePayoutData?.payoutAmount) > 0 ||
        Number(lead?.ophePayoutData?.providerPayout) > 0 ||
        Number(lead?.ophePayoutData?.providerPayoutAmount) > 0))
  );
  const activeDutySchedule = Array.isArray(lead?.opheProviderData?.dailyDutySchedule) && lead.opheProviderData.dailyDutySchedule.length > 0
    ? lead.opheProviderData.dailyDutySchedule
    : (Array.isArray(providerForm.dailyDutySchedule) ? providerForm.dailyDutySchedule : []);
  const allScheduleDaysCompleted = activeDutySchedule.length > 0 && activeDutySchedule.every(d => d.status === 'Completed');
  const isProviderAssigned = Boolean(lead?.opheProviderData?.providerName?.trim());

  // Stage 2 is considered complete only when provider is assigned AND all duty days are completed (or case is completed)
  const isStage2Done = Boolean(isStage1Done && isProviderAssigned && (allScheduleDaysCompleted || isStage3Done));

  // Current progression: Stay in Stage 2 (Active Care) while care is ongoing; only advance to Stage 3 when all days are completed or case is completed
  const currentStep = isStage3Done ? 3 : (allScheduleDaysCompleted && isProviderAssigned) ? 3 : isStage1Done ? 2 : 1;
  const assignedAgentName = lead.assignedCustomerCare?.name ||
    `${lead.assignedCustomerCare?.firstName || ''} ${lead.assignedCustomerCare?.lastName || ''}`.trim() ||
    'Admin User';

  return (
    <div className="p-4 md:p-8 space-y-6 max-w-[1600px] mx-auto min-h-screen">

      {/* 🚀 Top Bar: Back Link & Overview */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-4 px-6 rounded-2xl border border-slate-200 shadow-sm">
        <Link
          to="/ophe/pipeline"
          className="inline-flex items-center gap-2 text-xs font-bold text-teal-700 hover:text-teal-900 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to OPHE Pipeline</span>
        </Link>

        <div className="flex items-center gap-2 flex-wrap">
          {lead?.status === 'not_interested' ? (
            <button
              type="button"
              onClick={() => navigate('/ophe/pipeline?tab=lost')}
              className="inline-flex items-center gap-1.5 px-3.5 py-1 rounded-full text-xs font-black bg-rose-50 text-rose-700 hover:bg-rose-100 border border-rose-200 transition-all cursor-pointer shadow-2xs"
              title="View in OPHE Lost Leads"
            >
              <XCircle className="w-3.5 h-3.5 text-rose-600" />
              <span>● Lost Lead (View OPHE Lost)</span>
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setShowLostModal(true)}
              className="inline-flex items-center gap-1.5 px-3.5 py-1 rounded-full text-xs font-bold bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 transition-all cursor-pointer shadow-2xs hover:shadow active:scale-95"
              title="Mark this case as Lost Lead and go to OPHE Lost Leads"
            >
              <XCircle className="w-3.5 h-3.5 text-rose-600" />
              <span>Lost Lead</span>
            </button>
          )}

          <span className={`px-3 py-1 rounded-full text-xs font-black tracking-wide ${lead?.status === 'not_interested'
              ? 'bg-rose-100 text-rose-800 border border-rose-200'
              : isStage3Done
                ? 'bg-emerald-100 text-emerald-800 border border-emerald-200'
                : isStage2Done
                  ? 'bg-indigo-100 text-indigo-800 border border-indigo-200'
                  : isStage1Done
                    ? 'bg-teal-100 text-teal-800 border border-teal-200'
                    : 'bg-amber-100 text-amber-800 border border-amber-200'
            }`}>
            {lead?.status === 'not_interested'
              ? '● Case Closed / Lost'
              : isStage3Done
                ? '● Stage 3: Care Completed'
                : isStage2Done
                  ? '● Stage 3: Payout & Settlement'
                  : isStage1Done
                    ? '● Stage 2: Provider Assignment'
                    : '● Stage 1: Payment Stage'}
          </span>

          <span className="text-xs px-2.5 py-1 rounded-full bg-teal-50 text-teal-800 font-bold border border-teal-200">
            OPHE Care Case
          </span>
        </div>
      </div>

      {/* ─── 2-COLUMN MAIN CRM LAYOUT ────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 min-w-0">

        {/* ─── LEFT COLUMN: PATIENT DETAILS SIDEBAR ──────────────────────────── */}
        <div className="lg:col-span-1 space-y-6 min-w-0">

          {/* Patient Details Card */}
          <div className="bg-white rounded-2xl shadow-sm p-6 border border-slate-200 space-y-5">

            {/* Header with Disposition / Call status */}
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <h2 className="text-base font-black text-slate-800 tracking-tight">Patient Details</h2>
              {latestCall ? (
                <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[10px] font-bold border ${getLightDispositionBadge(getEffectiveDisposition(latestCall)).bg}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${getLightDispositionBadge(getEffectiveDisposition(latestCall)).dot}`} />
                  {getLightDispositionBadge(getEffectiveDisposition(latestCall)).label}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[10px] font-bold border bg-amber-50 text-amber-800 border-amber-300">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                  Pending Callback
                </span>
              )}
            </div>

            {/* Name & Phone Grid */}
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

                {/* Name */}
                <div className="space-y-0.5">
                  <p className="text-[10px] text-slate-400 uppercase font-black tracking-widest">NAME</p>
                  <p className="font-black text-slate-900 text-base sm:text-lg truncate">{lead.patientName}</p>
                </div>

                {/* Phone & Call button */}
                <div className="space-y-0.5">
                  <p className="text-[10px] text-slate-400 uppercase font-black tracking-widest">PHONE</p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-bold text-slate-800 text-sm">{lead.patientPhone}</p>
                    {lead.patientPhone && (
                      <button
                        onClick={handleExotelCall}
                        disabled={isCallingPatient}
                        title="Click-to-Call patient via Exotel"
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-bold text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer bg-gradient-to-r from-violet-600 to-indigo-600 shadow-sm shadow-violet-500/20 active:scale-95"
                      >
                        {isCallingPatient ? (
                          <><Loader2 size={11} className="animate-spin" />&nbsp;Calling…</>
                        ) : (
                          <><PhoneCall size={11} />&nbsp;Call</>
                        )}
                      </button>
                    )}
                  </div>
                </div>

              </div>

              {/* Treatment / Care Package Badge */}
              <div className="pt-2">
                <p className="text-[10px] text-slate-400 uppercase font-black tracking-widest mb-1.5">
                  TREATMENT REQUIRED / PACKAGE
                </p>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-black text-indigo-700 bg-indigo-50 py-1 px-3 rounded-lg text-xs inline-block border border-indigo-100 uppercase tracking-tighter shadow-sm">
                    {lead.opheServiceDetails?.packageName || lead.treatmentRequired || lead.disease || 'DFX / OPHE Care'}
                  </span>
                  {lead.opheServiceDetails?.duration && (
                    <span className="text-[11px] font-bold text-teal-800 bg-teal-50 px-2 py-0.5 rounded border border-teal-200">
                      {lead.opheServiceDetails.duration}
                    </span>
                  )}
                </div>
              </div>

              {/* ── Call & Follow-up Status Section ── */}
              <div className="pt-4 border-t border-slate-100 space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-[10px] text-slate-400 uppercase font-black tracking-widest flex items-center gap-1.5">
                    <PhoneCall size={12} className="text-violet-600" />
                    CALL & FOLLOW-UP STATUS
                  </p>
                  {latestCall && (
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <button
                        type="button"
                        onClick={handleOpenQuickDispModal}
                        className="inline-flex items-center gap-1 text-[10px] font-bold text-violet-600 hover:text-violet-700 bg-violet-50 hover:bg-violet-100 px-2 py-0.5 rounded border border-violet-200 transition-all cursor-pointer"
                        title="Update Follow-up Status"
                      >
                        <Edit size={10} /> Update Status
                      </button>
                      {lead?.status !== 'not_interested' && (
                        <button
                          type="button"
                          onClick={() => setShowLostModal(true)}
                          className="inline-flex items-center gap-1 text-[10px] font-bold text-rose-600 hover:text-rose-700 bg-rose-50 hover:bg-rose-100 px-2 py-0.5 rounded border border-rose-200 transition-all cursor-pointer"
                          title="Mark as Lost Lead and go to OPHE Lost Leads"
                        >
                          <XCircle size={10} /> Lost Lead
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => setShowCallHistory(prev => !prev)}
                        className="text-[10px] font-bold text-slate-500 hover:text-slate-700 hover:underline cursor-pointer"
                      >
                        {showCallHistory ? 'Hide' : 'History'}
                      </button>
                    </div>
                  )}
                </div>

                {latestCall ? (
                  <div className="bg-slate-50/90 border border-slate-200 rounded-xl p-3.5 space-y-2.5">
                    {/* Call Status */}
                    <div className="flex items-center justify-between gap-2 text-xs">
                      <span className="text-[11px] font-semibold text-slate-500">Call Status:</span>
                      {(() => {
                        const badge = getLightStatusBadge(latestCall.status);
                        return (
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-md text-[11px] font-bold border ${badge.bg}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${badge.dot}`} />
                            {badge.label}
                          </span>
                        );
                      })()}
                    </div>

                    {/* Follow-up Status */}
                    <div className="flex items-center justify-between gap-2 text-xs">
                      <span className="text-[11px] font-semibold text-slate-500">Follow-up:</span>
                      {(() => {
                        const eff = getEffectiveDisposition(latestCall);
                        const disp = getLightDispositionBadge(eff);
                        return (
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-md text-[11px] font-bold border ${disp.bg}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${disp.dot}`} />
                            {disp.label}
                          </span>
                        );
                      })()}
                    </div>

                    {/* Timestamp & Duration */}
                    <div className="flex items-center justify-between text-[10px] text-slate-500 pt-1.5 border-t border-slate-200/60">
                      <span className="flex items-center gap-1">
                        <Clock size={10} className="text-slate-400" />
                        {latestCall?.createdAt || latestCall?.startTime
                          ? new Date(latestCall.createdAt || latestCall.startTime).toLocaleString('en-IN', {
                            day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true
                          })
                          : 'Recent'}
                      </span>
                      <span className="capitalize font-bold text-slate-600">
                        {latestCall.direction || 'Inbound'} {latestCall.duration > 0 ? `• ${Math.floor(latestCall.duration / 60)}m ${latestCall.duration % 60}s` : '• 0m 4s'}
                      </span>
                    </div>

                    {/* Notes if any */}
                    {(latestCall.dispositionNotes || latestCall.notes) && (
                      <div className="bg-white p-2.5 rounded-lg border border-slate-200 text-[11px] text-slate-600">
                        <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-0.5">Note</p>
                        <p className="italic">"{latestCall.dispositionNotes || latestCall.notes}"</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="bg-slate-50 border border-dashed border-slate-200 rounded-xl p-3 flex items-center justify-between text-xs text-slate-500">
                    <span className="flex items-center gap-1.5 text-[11px] text-slate-500">
                      <PhoneOff size={13} className="text-slate-400" /> No call records yet
                    </span>
                    {lead.patientPhone && (
                      <button
                        onClick={handleExotelCall}
                        disabled={isCallingPatient}
                        className="text-[10px] font-bold text-violet-600 hover:text-violet-700 hover:underline cursor-pointer"
                      >
                        Call Now
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* ── Exotel Full Call History Expandable Box ── */}
              <div className="pt-2">
                <button
                  type="button"
                  onClick={() => setShowCallHistory(prev => !prev)}
                  className="w-full flex items-center justify-between px-4 py-2.5 bg-slate-50 hover:bg-slate-100 rounded-xl border border-slate-200 text-xs font-bold text-slate-700 transition-all cursor-pointer shadow-sm"
                >
                  <span className="flex items-center gap-2">
                    <PhoneCall size={14} className="text-violet-500" />
                    Full Call History
                  </span>
                  <span className="text-[10px] text-slate-400 font-normal">
                    {showCallHistory ? 'Hide' : 'Show All'}
                  </span>
                </button>
                {showCallHistory && (
                  <div className="mt-3">
                    <CallHistoryPanel
                      leadId={id}
                      refreshTrigger={callHistoryRefresh}
                      onLatestCallChange={setLatestCall}
                    />
                  </div>
                )}
              </div>

              {/* ── Case Management Section ── */}
              <div className="pt-4 border-t border-slate-100 space-y-2">
                <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">CASE MANAGEMENT</h3>
                <div className="flex items-center justify-between p-4 bg-slate-50 border border-slate-200 rounded-2xl">
                  <div>
                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">ASSIGNED AGENT</p>
                    <p className="text-sm font-bold text-slate-800">{assignedAgentName}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => toast('Agent is assigned to this lead.', { icon: 'ℹ️' })}
                    className="px-3 py-1.5 bg-indigo-600 text-white text-[10px] font-black uppercase rounded-lg shadow-sm active:scale-95 transition-all cursor-pointer"
                  >
                    REASSIGN
                  </button>
                </div>
              </div>

              {/* ── Patient Profile & Vitals Section ── */}
              <div className="pt-4 border-t border-slate-100 space-y-3">
                <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] flex items-center gap-1.5">
                  <Activity size={12} className="text-teal-600" />
                  PATIENT VITALS & INTAKE
                </h3>
                <div className="grid grid-cols-2 gap-2 text-xs bg-slate-50 p-3.5 rounded-xl border border-slate-200">
                  <div>
                    <span className="text-[10px] text-slate-400 block font-bold">AGE / GENDER</span>
                    <span className="font-bold text-slate-800">{lead.patientAge ? `${lead.patientAge} yrs` : '—'} ({lead.patientGender || 'Other'})</span>
                  </div>
                  <div>
                    <span className="text-[10px] text-slate-400 block font-bold">BLOOD PRESSURE</span>
                    <span className="font-bold text-teal-800">{lead.opheMedicalInfo?.vitalBloodPressure || '120/80'}</span>
                  </div>
                  <div className="pt-1.5 border-t border-slate-200/60">
                    <span className="text-[10px] text-slate-400 block font-bold">BLOOD SUGAR</span>
                    <span className="font-bold text-slate-800">{lead.opheMedicalInfo?.vitalSugar || 'Normal'}</span>
                  </div>
                  <div className="pt-1.5 border-t border-slate-200/60">
                    <span className="text-[10px] text-slate-400 block font-bold">SPO2 OXYGEN</span>
                    <span className="font-bold text-teal-800">{lead.opheMedicalInfo?.vitalOxygenSpo2 || '98%'}</span>
                  </div>
                  <div className="col-span-2 pt-1.5 border-t border-slate-200/60">
                    <span className="text-[10px] text-slate-400 block font-bold">MOBILITY STATUS</span>
                    <span className="font-bold text-indigo-700">{lead.opheMedicalInfo?.mobilityStatus || 'Independent'}</span>
                  </div>
                  {lead.patientAddress && (
                    <div className="col-span-2 pt-1.5 border-t border-slate-200/60">
                      <span className="text-[10px] text-slate-400 block font-bold">ADDRESS</span>
                      <span className="text-[11px] text-slate-700">{lead.patientAddress}</span>
                    </div>
                  )}
                  {(lead.opheMedicalInfo?.currentMedication || currentMedication) && (
                    <div className="col-span-2 pt-1.5 border-t border-slate-200/60">
                      <span className="text-[10px] text-emerald-700 font-bold flex items-center gap-1">
                        <Pill size={11} className="text-emerald-600" />
                        CURRENT MEDICATION (POST-PAYMENT)
                      </span>
                      <span className="text-[11px] text-slate-800 font-medium whitespace-pre-wrap block mt-0.5">
                        {lead.opheMedicalInfo?.currentMedication || currentMedication}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* ── Patient Photos & Documents Vault ── */}
              <div className="pt-4 border-t border-slate-100 space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] flex items-center gap-1.5">
                    <Image size={12} className="text-teal-600" />
                    PATIENT DOCUMENTS & PHOTOS
                  </h3>
                  <button
                    type="button"
                    onClick={() => setIsEditingVault(prev => !prev)}
                    className={`px-2.5 py-0.5 rounded text-[10px] font-bold border transition-all cursor-pointer ${isEditingVault
                        ? 'bg-amber-500 text-white border-amber-600 shadow-sm'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200 border-slate-200'
                      }`}
                  >
                    {isEditingVault ? 'Done Editing' : 'Edit Vault'}
                  </button>
                </div>

                {/* Document Group Grid */}
                <div className="space-y-4">
                  {[
                    { type: 'patient_photo', label: 'Patient Photo', color: 'teal', icon: <Image size={12} className="text-teal-600" /> },
                    { type: 'medical_report', label: 'Medical Reports / Notes', color: 'purple', icon: <FileText size={12} className="text-purple-600" /> },
                    { type: 'id_proof', label: 'Identity Proof', color: 'blue', icon: <UserCheck size={12} className="text-blue-600" /> }
                  ].map(cat => {
                    const catDocs = (lead.documents || []).filter(d => d.type === cat.type);
                    if (catDocs.length === 0 && !isEditingVault && cat.type !== 'patient_photo') return null;

                    return (
                      <div key={cat.type} className="space-y-2">
                        <div className="flex items-center justify-between text-[10px] font-bold">
                          <span className="flex items-center gap-1.5 uppercase tracking-wider text-slate-700">
                            {cat.icon} {cat.label} ({catDocs.length})
                          </span>
                        </div>

                        <div className="grid grid-cols-3 gap-2">
                          {catDocs.map((doc, idx) => (
                            <div key={idx} className="relative aspect-square group">
                              <button
                                type="button"
                                onClick={() => handleFilePreview(doc)}
                                className="w-full h-full bg-slate-50 border border-slate-200 rounded-xl flex flex-col items-center justify-center p-1 hover:border-teal-500 hover:shadow-md transition-all overflow-hidden cursor-pointer"
                              >
                                {doc.url?.toLowerCase().endsWith('.pdf') ? (
                                  <div className="flex flex-col items-center justify-center p-2 text-center">
                                    <FileText className="w-6 h-6 text-red-500 mb-1" />
                                    <span className="text-[8px] font-bold text-slate-600 truncate w-full">{doc.name || 'PDF'}</span>
                                  </div>
                                ) : (
                                  <div className="absolute inset-0 w-full h-full">
                                    <img
                                      src={doc.url}
                                      alt={doc.name || 'Patient Photo'}
                                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                      loading="lazy"
                                    />
                                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                                      <Eye className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                    </div>
                                  </div>
                                )}
                              </button>

                              {isEditingVault && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteDocument(doc);
                                  }}
                                  className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-rose-600 hover:bg-rose-700 text-white rounded-full flex items-center justify-center shadow-md z-20 transition-all cursor-pointer border border-white"
                                  title="Delete Photo/Doc"
                                >
                                  <Trash2 className="w-2.5 h-2.5" />
                                </button>
                              )}
                            </div>
                          ))}

                          {/* Quick Upload Tile */}
                          <label className="aspect-square border-2 border-dashed border-slate-300 hover:border-teal-500 bg-slate-50 hover:bg-teal-50/50 rounded-xl flex flex-col items-center justify-center p-1.5 cursor-pointer transition-all gap-1 text-slate-400 hover:text-teal-600">
                            <input
                              type="file"
                              multiple
                              accept="image/*,.pdf"
                              hidden
                              disabled={uploadingDoc}
                              onChange={(e) => handleVaultUpload(e, cat.type)}
                            />
                            {uploadingDoc ? (
                              <Loader2 className="w-4 h-4 animate-spin text-teal-600" />
                            ) : (
                              <Plus className="w-4 h-4" />
                            )}
                            <span className="text-[8px] font-bold uppercase tracking-wider text-center">
                              {uploadingDoc ? 'Uploading...' : 'Upload'}
                            </span>
                          </label>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

            </div>

          </div>

          {/* Activity / Audit Log */}
          {(() => {
            const rawHistory = Array.isArray(lead?.statusHistory) ? lead.statusHistory : [];
            // Deduplicate status history so repeated notes only display once
            const auditList = rawHistory.filter((item, idx, arr) => {
              const noteText = (item.notes || item.status || '').trim();
              if (!noteText) return false;
              return arr.findIndex(other => (other.notes || other.status || '').trim() === noteText) === idx;
            });

            if (auditList.length === 0) return null;

            return (
              <div className="bg-white rounded-2xl shadow-sm p-6 border border-slate-200 space-y-3">
                <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-wider">
                  STAGE AUDIT & TIMELINE
                </h3>
                <div className="space-y-3">
                  {auditList.map((item, idx) => (
                    <div key={idx} className="flex items-start gap-3 text-xs">
                      <div className="w-2 h-2 rounded-full bg-teal-500 mt-1.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-slate-800 leading-tight">{item.notes || `Status: ${item.status}`}</p>
                        <span className="text-[10px] text-slate-400">
                          {item.changedAt || lead.updatedAt ? new Date(item.changedAt || lead.updatedAt).toLocaleString('en-IN') : 'N/A'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

        </div>

        {/* ─── RIGHT COLUMN: WORKSPACE & PIPELINE STAGES ──────────────────────── */}
        <div className="lg:col-span-2 space-y-6 min-w-0">

          {/* Workspace Wrapper Card */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 sm:p-6 space-y-6 min-w-0">

            {/* Workspace Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-100 pb-3">
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <span className="w-1.5 h-4 rounded-full bg-teal-600 inline-block" />
                  <h2 className="text-xs font-black text-slate-900 tracking-[0.2em] uppercase">WORKSPACE</h2>
                </div>

                {/* 2. Extension Payment (Extend Care) - Elevated to Top Header */}
                {!isCaseSettled ? (
                  <button
                    type="button"
                    onClick={() => {
                      const next = !showExtensionSection;
                      setShowExtensionSection(next);
                      if (next) {
                        setPaymentOptionMode('extension');
                        setActiveStageTab(2);
                      } else {
                        setPaymentOptionMode('standard');
                      }
                    }}
                    className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-2 cursor-pointer border shadow-2xs ${showExtensionSection
                        ? 'bg-emerald-600 text-white border-emerald-600 shadow-sm ring-2 ring-emerald-500/20'
                        : 'bg-emerald-50 hover:bg-emerald-100 text-emerald-800 border-emerald-300'
                      }`}
                    title="Extend care days or upgrade shift for this patient (Available across all stages)"
                  >
                    <RotateCw className={`w-3.5 h-3.5 ${showExtensionSection ? 'text-white' : 'text-emerald-700'}`} />
                    <span>Extend Care / Upgrade Shift</span>
                    <span className={`text-[9px] px-1.5 py-0.2 rounded font-black ${showExtensionSection ? 'bg-white text-emerald-900' : 'bg-emerald-200/80 text-emerald-900'
                      }`}>
                      {showExtensionSection ? 'Close ✕' : '+ Extend'}
                    </span>
                  </button>
                ) : (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold bg-emerald-50 text-emerald-800 border border-emerald-300 shadow-2xs select-none">
                    <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
                    <span>Case Completed &amp; Settled (All Stages Locked)</span>
                  </span>
                )}
              </div>

              <div className="flex items-center gap-2 text-xs text-slate-500">
                <span>Case Stage:</span>
                <span className="font-bold text-teal-800">
                  Stage {activeStageTab || currentStep}
                  {activeStageTab === 3 && (isStage3Done ? ' (Completed)' : ' (Payout & Closure)')}
                  {activeStageTab === 2 && (isProviderAssigned ? (allScheduleDaysCompleted ? ' (Care Completed ✓)' : ' (Active Care In-Progress)') : ' (Provider Allocation)')}
                  {activeStageTab === 1 && (isStage1Done ? ' (Payment Verified)' : ' (Payment Pending)')}
                </span>
              </div>
            </div>

            {/* ── EXPANDABLE EXTENSION PAYMENT (EXTENDED CARE) SECTION ── */}
            {/* Shows only when clicked from top header ("if they click then only exten show otherwise not") */}
            {showExtensionSection && (
              <div className="bg-white border-2 border-emerald-400 rounded-2xl shadow-md overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                {/* Header with Close Button */}
                <div className="px-6 py-4 border-b border-emerald-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-gradient-to-r from-emerald-50/90 via-teal-50/70 to-emerald-50/90">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-emerald-600 text-white flex items-center justify-center font-bold text-sm shadow-xs shrink-0">
                      <RotateCw className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-black text-emerald-950">Extend Care or Upgrade Shift</h3>
                        <span className="text-[10px] bg-emerald-200 text-emerald-900 font-bold px-2 py-0.5 rounded-full">
                          Available Across All Stages
                        </span>
                      </div>
                      <p className="text-xs text-emerald-800 mt-0.5">
                        Add extra days to care or switch from 12-hour to 24-hour live-in care · Generates link for remaining balance only.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setShowTransactionsModal(true)}
                      className="px-3 py-1.5 rounded-xl text-xs font-bold bg-slate-900 hover:bg-slate-800 text-white flex items-center gap-1.5 shadow-xs transition-all cursor-pointer"
                      title="Open full payment & transaction ledger table"
                    >
                      <FileText className="w-3.5 h-3.5 text-teal-400" />
                      <span>Ledger</span>
                      {Array.isArray(lead?.ophePaymentHistory) && lead.ophePaymentHistory.length > 0 && (
                        <span className="px-1.5 py-0.2 rounded-full text-[10px] bg-teal-500 text-slate-950 font-black">
                          {lead.ophePaymentHistory.length}
                        </span>
                      )}
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        setShowExtensionSection(false);
                        setPaymentOptionMode('standard');
                      }}
                      className="px-3 py-1.5 rounded-xl bg-white hover:bg-slate-100 text-slate-700 font-bold text-xs border border-slate-300 flex items-center gap-1.5 shadow-2xs transition-all cursor-pointer"
                      title="Close Extension Payment Section"
                    >
                      <X className="w-4 h-4 text-slate-500" />
                      <span>Close</span>
                    </button>
                  </div>
                </div>

                {/* Payment Received Confirmed Banner (If Extension Mode Active and Paid) */}
                {(lead?.ophePaymentData?.status === 'paid' || totalPaidCollected > 0) && (
                  <div className="mx-6 mt-4 bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4 shadow-xs animate-in fade-in">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl bg-emerald-600 text-white flex items-center justify-center flex-shrink-0 shadow-sm">
                        <CheckCircle2 className="w-5 h-5" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="text-xs font-black text-emerald-900 uppercase tracking-wide">Active Care Booking in Progress</h4>
                          <span className="text-[10px] bg-emerald-200/80 text-emerald-900 font-bold px-2 py-0.5 rounded">Already Paid: ₹{safeInr(totalPaidCollected || lead?.ophePaymentData?.amount || 0)}</span>
                        </div>
                        <p className="text-xs text-emerald-800 mt-0.5">
                          Patient has already paid <strong>₹{safeInr(totalPaidCollected || lead?.ophePaymentData?.amount || 0)}</strong>. Choose whether to <strong>Switch to 24-Hour Care</strong> or <strong>Add Extra Days</strong> below. The generated payment link will charge only the remaining balance.
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setActiveStageTab(2)}
                      className="px-4 py-2 rounded-xl bg-emerald-700 hover:bg-emerald-800 text-white font-bold text-xs shadow-sm transition-all flex items-center gap-1.5 cursor-pointer flex-shrink-0"
                    >
                      <span>View Assigned Caregiver (Stage 2)</span>
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                )}

                {/* Extension Mode Sub-Tabs: Shift Upgrade vs Add Extra Days */}
                <div className="px-6 pt-4">
                  <div className="p-1.5 bg-slate-100 rounded-2xl border border-slate-200 grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {/* Sub-Mode 1: Mid-Care Shift Upgrade */}
                    <button
                      type="button"
                      onClick={() => setExtensionSubMode('shift_upgrade')}
                      className={`p-3 rounded-xl transition-all flex items-center gap-3 cursor-pointer text-left ${extensionSubMode === 'shift_upgrade'
                          ? 'bg-white text-emerald-950 shadow-md border border-emerald-500/40 ring-2 ring-emerald-500/10'
                          : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200/60'
                        }`}
                    >
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-xs shrink-0 ${extensionSubMode === 'shift_upgrade' ? 'bg-emerald-600 text-white' : 'bg-slate-200 text-slate-600'
                        }`}>
                        <RotateCw className="w-4 h-4" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-black">Option A: Switch to 24-Hour Live-in Care</span>
                          {extensionSubMode === 'shift_upgrade' && (
                            <span className="text-[9px] bg-emerald-100 text-emerald-800 font-bold px-1.5 py-0.2 rounded">Active</span>
                          )}
                        </div>
                        <p className="text-[11px] text-slate-500 mt-0.5 leading-snug">
                          Upgrade remaining care days to 24-hour round-the-clock care. Any unused 12h days will be credited automatically.
                        </p>
                      </div>
                    </button>

                    {/* Sub-Mode 2: Add Extra Days */}
                    <button
                      type="button"
                      onClick={() => setExtensionSubMode('extra_days')}
                      className={`p-3 rounded-xl transition-all flex items-center gap-3 cursor-pointer text-left ${extensionSubMode === 'extra_days'
                          ? 'bg-white text-teal-950 shadow-md border border-teal-500/40 ring-2 ring-teal-500/10'
                          : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200/60'
                        }`}
                    >
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-xs shrink-0 ${extensionSubMode === 'extra_days' ? 'bg-teal-600 text-white' : 'bg-slate-200 text-slate-600'
                        }`}>
                        <Plus className="w-4 h-4" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-black">Option B: Add Extra Days (Extend Care)</span>
                          {extensionSubMode === 'extra_days' && (
                            <span className="text-[9px] bg-teal-100 text-teal-800 font-bold px-1.5 py-0.2 rounded">Active</span>
                          )}
                        </div>
                        <p className="text-[11px] text-slate-500 mt-0.5 leading-snug">
                          Keep the same shift timings and extend the care duration at the agreed daily rate.
                        </p>
                      </div>
                    </button>
                  </div>
                </div>

                <div className="p-6 space-y-6">
                  {/* Case A: Mid-Care Shift Upgrade Calculator */}
                  {extensionSubMode === 'shift_upgrade' ? (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold text-emerald-950 uppercase tracking-wider flex items-center gap-1.5">
                          <span className="w-5 h-5 rounded-full bg-emerald-600 text-white text-[11px] font-bold flex items-center justify-center">1</span>
                          <span>Step 1: Calculate Shift Upgrade & Unused Day Credit</span>
                        </span>
                        <span className="text-[11px] text-emerald-700 font-medium bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
                          Unused 12h days credited automatically
                        </span>
                      </div>

                      {/* 3-Column Interactive Shift Upgrade Matrix */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Card 1: Original Booking (12h Care) */}
                        <div className="bg-slate-50 border border-slate-200/90 rounded-2xl p-4 space-y-3 shadow-2xs">
                          <div className="flex items-center justify-between border-b border-slate-200 pb-2">
                            <span className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                              <span className="w-4 h-4 rounded bg-slate-200 text-slate-700 text-[10px] font-black flex items-center justify-center">1</span>
                              <span>1. Initial 12-Hour Care Booking</span>
                            </span>
                            <span className="text-[10px] font-bold text-slate-500 bg-slate-200 px-1.5 py-0.5 rounded">12h Shift</span>
                          </div>

                          <div className="space-y-2">
                            <div>
                              <label className="block text-[11px] font-semibold text-slate-600 mb-0.5">Total Days Initially Booked</label>
                              <div className="relative">
                                <input
                                  type="number"
                                  min="1"
                                  value={upgradeForm.originalDays}
                                  onChange={(e) => handleUpgradeFormChange('originalDays', e.target.value)}
                                  placeholder="e.g. 7"
                                  className="w-full text-xs font-black px-3 py-1.5 rounded-lg border border-slate-300 bg-white text-slate-900 focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                                />
                              </div>
                            </div>

                            <div>
                              <label className="block text-[11px] font-semibold text-slate-600 mb-0.5">Daily Rate for 12h Care (₹ / Day)</label>
                              <div className="relative">
                                <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 font-bold text-xs">₹</span>
                                <input
                                  type="number"
                                  min="0"
                                  value={upgradeForm.originalDailyRate}
                                  onChange={(e) => handleUpgradeFormChange('originalDailyRate', e.target.value)}
                                  placeholder="e.g. 1000"
                                  className="w-full text-xs font-black pl-6 pr-3 py-1.5 rounded-lg border border-slate-300 bg-white text-slate-900 focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                                />
                              </div>
                            </div>

                            <div className="pt-1 border-t border-slate-200 flex justify-between items-center text-[11px]">
                              <span className="text-slate-500">Initial Booking Value:</span>
                              <span className="font-bold text-slate-800">₹{safeInr((upgradeForm?.originalDays || 0) * (upgradeForm?.originalDailyRate || 0))}</span>
                            </div>
                          </div>
                        </div>

                        {/* Card 2: Care Consumed & Unused Credit */}
                        <div className="bg-amber-50/50 border border-amber-200/90 rounded-2xl p-4 space-y-3 shadow-2xs">
                          <div className="flex items-center justify-between border-b border-amber-200/80 pb-2">
                            <span className="text-xs font-bold text-amber-900 flex items-center gap-1.5">
                              <span className="w-4 h-4 rounded bg-amber-200 text-amber-900 text-[10px] font-black flex items-center justify-center">2</span>
                              <span>2. Care Days Used & Unused Credit</span>
                            </span>
                            <span className="text-[10px] font-bold text-amber-800 bg-amber-100 px-1.5 py-0.5 rounded">Completed</span>
                          </div>

                          <div className="space-y-2">
                            <div>
                              <div className="flex items-center justify-between mb-0.5">
                                <label className="block text-[11px] font-semibold text-amber-900">Days of Care Completed So Far *</label>
                                <span className="text-[10px] font-bold text-amber-800 bg-amber-100 px-1.5 py-0.2 rounded">
                                  {upgradeForm.daysServed} Day{upgradeForm.daysServed === 1 ? '' : 's'} done
                                </span>
                              </div>
                              <input
                                type="number"
                                min="0"
                                max={Math.max(0, (upgradeForm?.originalDays || 1) - 1)}
                                value={upgradeForm.daysServed}
                                onChange={(e) => handleUpgradeFormChange('daysServed', e.target.value)}
                                className="w-full text-xs font-black px-3 py-1.5 rounded-lg border border-amber-300 bg-white text-amber-950 focus:ring-2 focus:ring-amber-500 focus:outline-none"
                              />
                            </div>

                            <div className="bg-white/80 rounded-xl p-2.5 border border-amber-200/80 space-y-1 text-[11px]">
                              <div className="flex justify-between text-slate-600">
                                <span>Value of Completed Days:</span>
                                <span className="font-bold text-slate-800">₹{safeInr(currentBilling.consumedBaseCost)}</span>
                              </div>
                              <div className="flex justify-between text-slate-600">
                                <span>Remaining Unused Days:</span>
                                <span className="font-bold text-emerald-800">{currentBilling.remainingUnusedDays || ((upgradeForm?.originalDays || 0) - (upgradeForm?.daysServed || 0))} Days</span>
                              </div>
                            </div>

                            <div className="pt-1 flex flex-col gap-0.5 bg-emerald-50 px-2 py-1.5 rounded-lg border border-emerald-200">
                              <div className="flex justify-between items-center text-[11px]">
                                <span className="font-bold text-emerald-900">Credit for Unused Days:</span>
                                <span className="font-black text-emerald-700 text-xs">
                                  ₹{safeInr(currentBilling.unusedCredit)}
                                </span>
                              </div>
                              <span className="text-[9.5px] text-emerald-700 font-medium">
                                (Deducted automatically from the new 24h care bill)
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Card 3: Upgraded 24h Shift Plan */}
                        <div className="bg-emerald-50/50 border border-emerald-300 rounded-2xl p-4 space-y-3 shadow-2xs">
                          <div className="flex items-center justify-between border-b border-emerald-200 pb-2">
                            <span className="text-xs font-bold text-emerald-950 flex items-center gap-1.5">
                              <span className="w-4 h-4 rounded bg-emerald-200 text-emerald-900 text-[10px] font-black flex items-center justify-center">3</span>
                              <span>3. New 24-Hour Live-in Plan</span>
                            </span>
                            <span className="text-[10px] font-bold text-emerald-800 bg-emerald-100 px-1.5 py-0.5 rounded">24h Live-in</span>
                          </div>

                          <div className="space-y-2">
                            <div>
                              <label className="block text-[11px] font-semibold text-emerald-950 mb-0.5">Number of Days for 24h Care *</label>
                              <input
                                type="number"
                                min="1"
                                value={upgradeForm.daysToUpgrade}
                                onChange={(e) => handleUpgradeFormChange('daysToUpgrade', e.target.value)}
                                className="w-full text-xs font-black px-3 py-1.5 rounded-lg border border-emerald-300 bg-white text-emerald-950 focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                              />
                            </div>

                            <div>
                              <label className="block text-[11px] font-semibold text-emerald-950 mb-0.5">Daily Rate for 24h Care (₹ / Day) *</label>
                              <div className="relative">
                                <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 font-bold text-xs">₹</span>
                                <input
                                  type="number"
                                  min="0"
                                  value={upgradeForm.upgradedDailyRate}
                                  onChange={(e) => handleUpgradeFormChange('upgradedDailyRate', e.target.value)}
                                  placeholder="e.g. 2000"
                                  className="w-full text-xs font-black pl-6 pr-3 py-1.5 rounded-lg border border-emerald-300 bg-white text-emerald-950 focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                                />
                              </div>
                            </div>

                            <div className="pt-1 border-t border-emerald-200 flex justify-between items-center text-[11px]">
                              <span className="text-emerald-900 font-semibold">Total Cost for 24h Care:</span>
                              <span className="font-bold text-emerald-950">₹{safeInr(currentBilling.newShiftCost)}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Upgrade Calculation Live Summary Bar */}
                      <div className="bg-emerald-950 text-white rounded-2xl p-3.5 border border-emerald-700/60 shadow-sm">
                        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 items-center text-center">
                          <div className="border-r border-emerald-800/80 pr-2">
                            <span className="text-[10px] text-emerald-300 font-bold uppercase tracking-wider block">New 24h Care Total</span>
                            <span className="text-sm font-black text-white block mt-0.5">₹{safeInr(currentBilling.newShiftCost)}</span>
                            <span className="text-[9px] text-slate-300">{currentBilling.days}d @ ₹{upgradeForm?.upgradedDailyRate || 0}/d</span>
                          </div>

                          <div className="border-r border-emerald-800/80 pr-2">
                            <span className="text-[10px] text-amber-300 font-bold uppercase tracking-wider block">Minus: Unused Day Credit</span>
                            <span className="text-sm font-black text-amber-300 block mt-0.5">- ₹{safeInr(currentBilling.unusedCredit)}</span>
                            <span className="text-[9px] text-slate-300">{currentBilling.remainingUnusedDays || ((upgradeForm?.originalDays || 0) - (upgradeForm?.daysServed || 0))}d @ ₹{upgradeForm?.originalDailyRate || 0}/d</span>
                          </div>

                          <div className="border-r border-emerald-800/80 pr-2">
                            <span className="text-[10px] text-teal-200 font-bold uppercase tracking-wider block">Net Cost Before Tax</span>
                            <span className="text-sm font-black text-teal-200 block mt-0.5">₹{safeInr(currentBilling.netUpgradeBase)}</span>
                            <span className="text-[9px] text-slate-300">New care minus credit</span>
                          </div>

                          <div className="border-r border-emerald-800/80 pr-2">
                            <span className="text-[10px] text-emerald-300 font-bold uppercase tracking-wider block">GST ({currentBilling.gstRate}%)</span>
                            <span className="text-sm font-black text-emerald-300 block mt-0.5">+ ₹{safeInr(currentBilling.gstAmount)}</span>
                            <span className="text-[9px] text-slate-300">Tax on difference</span>
                          </div>

                          <div className="col-span-2 sm:col-span-1 bg-emerald-800/80 rounded-xl p-2 text-center border border-emerald-400/30">
                            <span className="text-[9px] text-emerald-200 font-black uppercase tracking-wider block">⚡ Balance to Collect</span>
                            <span className="text-base font-black text-white block mt-0.5">₹{safeInr(currentBilling.balanceDue)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    /* Case B: Add Extra Days Form */
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-xs font-bold text-slate-800 uppercase tracking-wider flex items-center gap-1.5">
                          <span className="w-5 h-5 rounded-full bg-teal-600 text-white text-[11px] font-bold flex items-center justify-center">1</span>
                          <span>Step 1: Choose Service Package & Additional Days</span>
                        </span>
                        <span className="text-[11px] text-slate-400">Select service package and number of extra days to extend</span>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Service Package Selector */}
                        <div className="md:col-span-2">
                          <div className="flex items-center justify-between mb-1">
                            <label className="block text-xs font-bold text-slate-900">Selected Service Package *</label>
                            {paymentForm.catalogPrice > 0 && (
                              <div className="flex items-center gap-1.5">
                                <span className="text-[10px] text-teal-700 font-bold bg-teal-50 border border-teal-200 px-2 py-0.5 rounded-full flex items-center gap-1">
                                  <Sparkles className="w-3 h-3 text-teal-600" />
                                  <span>Catalog Base: ₹{paymentForm.catalogPrice} / Day ({paymentForm.shiftHours}h)</span>
                                </span>
                                {Number(paymentForm.quotedPrice) !== Number(paymentForm.catalogPrice) && (
                                  <button
                                    type="button"
                                    onClick={() => handlePerDayBaseChange(paymentForm.catalogPrice)}
                                    className="text-[10px] text-teal-700 hover:text-teal-900 bg-teal-100/60 px-2 py-0.5 rounded-md font-bold cursor-pointer transition-all"
                                  >
                                    Apply Catalog ₹{paymentForm.catalogPrice}
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                          <select
                            value={paymentForm.serviceId}
                            onChange={(e) => handleServiceSelect(e.target.value)}
                            className="w-full text-xs px-3.5 py-2.5 rounded-xl border border-slate-300 bg-white font-bold text-slate-900 focus:ring-2 focus:ring-teal-500 focus:outline-none shadow-xs"
                          >
                            <option value="">-- Select or Change OPHE Service --</option>
                            {services.map((s) => {
                              const p12 = s.pricing?.hours12 || s.pricing?.perDay || s.durationPricing?.Day || s.basePrice || 0;
                              const p24 = s.pricing?.hours24 || 0;
                              return (
                                <option key={s._id} value={s._id}>
                                  {s.serviceName || s.name} {p12 > 0 ? `(12h: ₹${p12}${p24 > 0 ? ` | 24h: ₹${p24}` : ''})` : ''}
                                </option>
                              );
                            })}
                          </select>
                        </div>

                        {/* Agreed Daily Rate */}
                        <div>
                          <div className="flex items-center justify-between mb-1">
                            <label className="block text-xs font-bold text-slate-900">Agreed Daily Rate *</label>
                            {Number(paymentForm.quotedPrice) !== Number(paymentForm.catalogPrice) && paymentForm.catalogPrice > 0 && (
                              <span className="text-[10px] text-amber-700 font-bold bg-amber-50 px-1.5 py-0.2 rounded">Custom Price</span>
                            )}
                          </div>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold text-xs">₹</span>
                            <input
                              type="number"
                              required
                              value={paymentForm.quotedPrice}
                              onChange={(e) => handlePerDayBaseChange(e.target.value)}
                              placeholder="e.g. 1500"
                              className="w-full text-xs font-black pl-8 pr-3 py-2.5 rounded-xl border border-slate-300 bg-white text-slate-900 focus:ring-2 focus:ring-teal-500 focus:outline-none shadow-xs"
                            />
                          </div>
                        </div>

                        {/* Shift Type */}
                        <div>
                          <label className="block text-xs font-bold text-slate-900 mb-1">Care Shift Timing *</label>
                          <select
                            value={paymentForm.shiftType}
                            onChange={(e) => handleShiftTypeChange(e.target.value)}
                            className="w-full text-xs px-3.5 py-2.5 rounded-xl border border-slate-300 bg-white font-bold text-slate-900 focus:ring-2 focus:ring-teal-500 focus:outline-none shadow-xs"
                          >
                            <option value="12-Hour Day Shift">12-Hour Day Shift (8am - 8pm)</option>
                            <option value="12-Hour Night Shift">12-Hour Night Shift (8pm - 8am)</option>
                            <option value="24 Hours (Full Day Live-in)">24 Hours (Full Day Live-in)</option>
                            <option value="Per-Visit / Procedure Care">Per-Visit / Procedure Care</option>
                          </select>
                        </div>

                        {/* Duration (Days Count) */}
                        <div>
                          <label className="block text-xs font-bold text-slate-900 mb-1">Additional Days to Add *</label>
                          <div className="flex items-center gap-1.5">
                            <input
                              type="number"
                              min="1"
                              max="365"
                              value={paymentForm.daysCount}
                              onChange={(e) => handleDaysChange(e.target.value)}
                              className="w-20 text-xs font-black px-3 py-2.5 rounded-xl border border-slate-300 bg-white text-slate-900 focus:ring-2 focus:ring-teal-500 focus:outline-none shadow-xs"
                            />
                            <div className="flex items-center gap-1 flex-1">
                              {[7, 15, 30].map(d => (
                                <button
                                  key={d}
                                  type="button"
                                  onClick={() => handleDaysChange(d)}
                                  className={`flex-1 py-2 rounded-xl text-[11px] font-bold border transition-all cursor-pointer ${Number(paymentForm.daysCount) === d
                                      ? 'bg-teal-700 text-white border-teal-700 shadow-xs'
                                      : 'bg-slate-100 hover:bg-slate-200 border-slate-200 text-slate-700'
                                    }`}
                                >
                                  {d}d
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>

                        {/* Dates with auto-calculate and showPicker() */}
                        <div>
                          <div className="flex items-center justify-between mb-1">
                            <label className="block text-xs font-bold text-slate-900">Care Start & End Dates</label>
                            <span className="text-[10px] text-teal-700 font-bold bg-teal-50 px-1.5 py-0.2 rounded">
                              {paymentForm.daysCount ? `${paymentForm.daysCount} Day${Number(paymentForm.daysCount) > 1 ? 's' : ''}` : ''}
                            </span>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div className="relative">
                              <input
                                type="date"
                                value={paymentForm.startDate}
                                onClick={(e) => { try { e.target.showPicker?.(); } catch (_) { } }}
                                onChange={(e) => handleStartDateChange(e.target.value)}
                                className="w-full text-xs font-bold px-2.5 py-2.5 rounded-xl border border-slate-300 bg-white cursor-pointer hover:border-teal-500 focus:ring-2 focus:ring-teal-500 outline-none transition-all shadow-xs text-slate-800"
                                title="Click to select Start Date"
                              />
                            </div>
                            <div className="relative">
                              <input
                                type="date"
                                value={paymentForm.endDate}
                                onClick={(e) => { try { e.target.showPicker?.(); } catch (_) { } }}
                                onChange={(e) => handleEndDateChange(e.target.value)}
                                className="w-full text-xs font-bold px-2.5 py-2.5 rounded-xl border border-slate-300 bg-white cursor-pointer hover:border-teal-500 focus:ring-2 focus:ring-teal-500 outline-none transition-all shadow-xs text-slate-800"
                                title="Click to select End Date"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* GST & Discount Section */}
                  <div className="pt-2 border-t border-slate-100">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Discount Mode & Value */}
                      {!(extensionSubMode === 'shift_upgrade') ? (
                        <div className="bg-slate-50/90 border border-slate-200 p-3.5 rounded-2xl space-y-2.5 shadow-2xs">
                          <div className="flex items-center justify-between">
                            <label className="block text-xs font-bold text-slate-800 flex items-center gap-1.5">
                              <Percent className="w-3.5 h-3.5 text-teal-600" />
                              <span>Discount (Before GST)</span>
                            </label>
                            {currentBilling.discountAmount > 0 && (
                              <span className="text-[10px] font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">
                                - ₹{safeInr(currentBilling.discountAmount)} Saved
                              </span>
                            )}
                          </div>

                          <div className="grid grid-cols-3 gap-1.5">
                            {[
                              { id: 'none', label: 'No Discount' },
                              { id: 'percentage', label: 'Percentage (%)' },
                              { id: 'fixed', label: 'Flat (₹)' }
                            ].map((mode) => (
                              <button
                                key={mode.id}
                                type="button"
                                onClick={() => handleDiscountTypeChange(mode.id)}
                                className={`py-1.5 px-2 rounded-xl text-xs font-bold text-center transition-all cursor-pointer ${paymentForm.discountType === mode.id
                                    ? 'bg-teal-700 text-white shadow-xs'
                                    : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-100'
                                  }`}
                              >
                                {mode.label}
                              </button>
                            ))}
                          </div>

                          {paymentForm.discountType !== 'none' && (
                            <div className="space-y-2 pt-1 animate-in fade-in">
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-semibold text-slate-600 shrink-0">
                                  {paymentForm.discountType === 'percentage' ? 'Discount %:' : 'Discount ₹:'}
                                </span>
                                <div className="relative flex-1">
                                  <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 font-bold text-xs">
                                    {paymentForm.discountType === 'percentage' ? '%' : '₹'}
                                  </span>
                                  <input
                                    type="number"
                                    min="0"
                                    max={paymentForm.discountType === 'percentage' ? 100 : currentBilling.rawBaseTotal}
                                    value={paymentForm.discountValue}
                                    onChange={(e) => handleDiscountValueChange(e.target.value)}
                                    placeholder={paymentForm.discountType === 'percentage' ? 'e.g. 10' : 'e.g. 500'}
                                    className="w-full text-xs font-black pl-7 pr-3 py-1.5 rounded-lg border border-slate-300 bg-white focus:ring-2 focus:ring-teal-500 focus:outline-none"
                                  />
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="bg-emerald-50/70 border border-emerald-200 p-3.5 rounded-2xl flex items-center gap-3">
                          <div className="w-8 h-8 rounded-xl bg-emerald-600 text-white flex items-center justify-center shrink-0">
                            <Sparkles className="w-4 h-4" />
                          </div>
                          <div className="text-xs text-emerald-900">
                            <span className="font-bold block">Credit Deduction Applied</span>
                            <span className="text-[11px] text-emerald-800">
                              Unused credit of ₹{safeInr(currentBilling.unusedCredit)} is already deducted from the 24h care cost.
                            </span>
                          </div>
                        </div>
                      )}

                      {/* GST Settings */}
                      <div className="bg-slate-50/90 border border-slate-200 p-3.5 rounded-2xl space-y-2.5 shadow-2xs">
                        <div className="flex items-center justify-between">
                          <label className="block text-xs font-bold text-slate-800">GST on Care Services</label>
                          <label className="flex items-center gap-1.5 cursor-pointer text-xs font-semibold text-teal-700 bg-teal-50 px-2 py-0.5 rounded-lg border border-teal-200/70">
                            <input
                              type="checkbox"
                              checked={paymentForm.applyGst}
                              onChange={(e) => handleGstToggle(e.target.checked)}
                              className="w-3.5 h-3.5 rounded text-teal-600 focus:ring-teal-500 cursor-pointer"
                            />
                            <span className="font-bold">Apply GST</span>
                          </label>
                        </div>

                        <select
                          disabled={!paymentForm.applyGst}
                          value={paymentForm.gstRate}
                          onChange={(e) => handleGstRateChange(e.target.value)}
                          className="w-full text-xs px-3 py-2 rounded-xl border border-slate-300 bg-white focus:ring-2 focus:ring-teal-500 focus:outline-none disabled:bg-slate-100 disabled:text-slate-400 font-bold text-slate-800"
                        >
                          <option value={18}>18% GST (Standard Healthcare / Homecare Services)</option>
                          <option value={12}>12% GST</option>
                          <option value={5}>5% GST</option>
                          <option value={0}>0% GST (Exempt / Nil Rated)</option>
                        </select>
                        <div className="flex items-center justify-between text-[10px] text-slate-500">
                          <span>GST Base: ₹{safeInr(extensionSubMode === 'shift_upgrade' ? currentBilling.netUpgradeBase : currentBilling.discountedBaseTotal)}</span>
                          <span className="font-bold text-teal-800">+₹{safeInr(currentBilling.gstAmount)} GST</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Pricing Breakdown & Balance Due */}
                  <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-teal-950 text-white rounded-2xl p-5 shadow-xl border border-slate-700/60 space-y-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pb-3 border-b border-slate-700/60">
                      <div className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-teal-300" />
                        <span className="text-xs font-black text-teal-300 uppercase tracking-wider">
                          {extensionSubMode === 'shift_upgrade'
                            ? '24-Hour Care Upgrade Breakdown'
                            : 'Care Extension Pricing & Tax Breakdown'}
                        </span>
                        <span className="text-[11px] text-slate-300 font-medium">
                          ({extensionSubMode === 'shift_upgrade' ? `${currentBilling.days} Days @ 24h Shift` : `${paymentForm.shiftType} · ${currentBilling.days} ${currentBilling.days > 1 ? 'Days' : 'Day'}`})
                        </span>
                      </div>

                      <div className="flex items-baseline gap-2">
                        <span className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">
                          Final Balance to Collect:
                        </span>
                        <span className="text-2xl font-black text-emerald-400 drop-shadow-xs">
                          ₹{safeInr(currentBilling.balanceDue, 2)}
                        </span>
                      </div>
                    </div>

                    {/* Breakdown items */}
                    {extensionSubMode === 'shift_upgrade' ? (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div className="bg-slate-800/90 border border-slate-700/60 rounded-xl p-3 shadow-2xs">
                          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">1. New 24-Hour Care Cost</span>
                          <span className="text-sm font-black text-white block mt-1">₹{safeInr(currentBilling.newShiftCost, 2)}</span>
                          <span className="text-[10px] text-slate-400">₹{upgradeForm?.upgradedDailyRate || 0}/day × {currentBilling.days}d</span>
                        </div>
                        <div className="bg-slate-800/90 border border-slate-700/60 rounded-xl p-3 shadow-2xs">
                          <span className="text-[10px] font-bold text-amber-400 uppercase tracking-wider block">2. Credit for Unused Days</span>
                          <span className="text-sm font-black text-amber-300 block mt-1">- ₹{safeInr(currentBilling.unusedCredit, 2)}</span>
                          <span className="text-[10px] text-slate-400">₹{upgradeForm?.originalDailyRate || 0}/day × {currentBilling.remainingUnusedDays || ((upgradeForm?.originalDays || 0) - (upgradeForm?.daysServed || 0))}d</span>
                        </div>
                        <div className="bg-slate-800/90 border border-slate-700/60 rounded-xl p-3 shadow-2xs">
                          <span className="text-[10px] font-bold text-teal-300 uppercase tracking-wider block">3. Net Cost Before Tax</span>
                          <span className="text-sm font-black text-teal-300 block mt-1">₹{safeInr(currentBilling.netUpgradeBase, 2)}</span>
                          <span className="text-[10px] text-slate-400">Difference after credit</span>
                        </div>
                        <div className="bg-slate-800/90 border border-slate-700/60 rounded-xl p-3 shadow-2xs">
                          <span className="text-[10px] font-bold text-emerald-400 uppercase tracking-wider block">4. GST ({currentBilling.gstRate}%)</span>
                          <span className="text-sm font-black text-emerald-300 block mt-1">+ ₹{safeInr(currentBilling.gstAmount, 2)}</span>
                          <span className="text-[10px] text-slate-400">{currentBilling.gstRate === 0 ? 'Tax Exempt' : `${currentBilling.gstRate}% on difference`}</span>
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div className="bg-slate-800/90 border border-slate-700/60 rounded-xl p-3 shadow-2xs">
                          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">1. Gross Base</span>
                          <span className="text-sm font-black text-white block mt-1">₹{safeInr(currentBilling.rawBaseTotal, 2)}</span>
                          <span className="text-[10px] text-slate-400">₹{currentBilling.dailyRate}/day × {currentBilling.days}d</span>
                        </div>
                        <div className="bg-slate-800/90 border border-slate-700/60 rounded-xl p-3 shadow-2xs">
                          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">2. Discount</span>
                          <span className={`text-sm font-black block mt-1 ${currentBilling.discountAmount > 0 ? 'text-amber-400' : 'text-slate-400'}`}>
                            {currentBilling.discountAmount > 0 ? `- ₹${safeInr(currentBilling.discountAmount, 2)}` : '₹0.00'}
                          </span>
                          <span className="text-[10px] text-slate-400">
                            {paymentForm.discountType === 'percentage' ? `${paymentForm.discountValue}% applied` : paymentForm.discountType === 'fixed' ? 'Flat rebate' : 'No discount'}
                          </span>
                        </div>
                        <div className="bg-slate-800/90 border border-slate-700/60 rounded-xl p-3 shadow-2xs">
                          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">3. Net Taxable</span>
                          <span className="text-sm font-black text-teal-300 block mt-1">₹{safeInr(currentBilling.discountedBaseTotal, 2)}</span>
                          <span className="text-[10px] text-slate-400">Base after discount</span>
                        </div>
                        <div className="bg-slate-800/90 border border-slate-700/60 rounded-xl p-3 shadow-2xs">
                          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">4. GST ({currentBilling.gstRate}%)</span>
                          <span className="text-sm font-black text-emerald-300 block mt-1">+ ₹{safeInr(currentBilling.gstAmount, 2)}</span>
                          <span className="text-[10px] text-slate-400">{currentBilling.gstRate === 0 ? 'Tax Exempt' : `${currentBilling.gstRate}% standard`}</span>
                        </div>
                      </div>
                    )}

                    {/* Balance calculation banner */}
                    <div className="bg-emerald-950/80 border border-emerald-500/50 rounded-xl p-3.5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-0.5 rounded bg-emerald-800 text-emerald-100 text-[10px] font-bold uppercase tracking-wider">
                          {extensionSubMode === 'shift_upgrade' ? 'Care Package Summary' : 'Extension Summary'}
                        </span>
                        <p className="text-xs text-emerald-200">
                          {extensionSubMode === 'shift_upgrade' ? (
                            <>Total Overall Care Package: <strong>₹{safeInr(currentBilling.revisedGrossTotal)}</strong> (Care Completed: ₹{safeInr(currentBilling.consumedBaseCost)} + Upgraded 24h Care: ₹{safeInr(currentBilling.newShiftCost)} + GST)</>
                          ) : (
                            <>New Total Package: <strong>₹{safeInr(currentBilling.totalAmount)}</strong> − Already Paid: <strong className="text-emerald-400">₹{safeInr(currentBilling.alreadyPaidAmount)}</strong></>
                          )}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 self-end sm:self-auto shrink-0">
                        <span className="text-xs font-bold text-emerald-300 uppercase">
                          {extensionSubMode === 'shift_upgrade' ? 'Balance to Collect:' : 'Balance to Collect:'}
                        </span>
                        <span className="text-lg font-black text-emerald-300">
                          ₹{safeInr(currentBilling.balanceDue, 2)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Generate Extension Link Button */}
                  <div className="bg-slate-50/90 border border-slate-200/90 rounded-2xl p-5 space-y-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-xl bg-emerald-600 text-white flex items-center justify-center font-black text-xs shadow-xs">
                          <CreditCard className="w-4 h-4" />
                        </div>
                        <div>
                          <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider">
                            {extensionSubMode === 'shift_upgrade'
                              ? 'Step 2: Generate & Send Balance Payment Link'
                              : 'Step 2: Generate & Send Extension Payment Link'}
                          </h4>
                          <p className="text-[11px] text-slate-500">
                            Balance amount to collect: <strong className="text-teal-900">₹{safeInr(currentBilling.balanceDue, 2)}</strong> ({extensionSubMode === 'shift_upgrade' ? `${currentBilling.days} Days upgraded to 24-Hour Live-in Care` : `${currentBilling.days} Extra ${currentBilling.days > 1 ? 'Days' : 'Day'}, ${paymentForm.shiftType}`})
                          </p>
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleCreatePaymentLink('extension')}
                          disabled={generatingPayment}
                          className="px-5 py-2.5 rounded-xl text-white font-bold text-xs shadow-md transition-all flex items-center gap-2 cursor-pointer bg-emerald-600 hover:bg-emerald-700 shadow-emerald-600/20"
                        >
                          {generatingPayment ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              <span>Generating Razorpay Link...</span>
                            </>
                          ) : (
                            <>
                              <CreditCard className="w-4 h-4" />
                              <span>
                                {extensionSubMode === 'shift_upgrade'
                                  ? `⚡ Send Upgrade Payment Link (₹${safeInr(currentBilling.balanceDue)})`
                                  : `⚡ Send Extension Payment Link (₹${safeInr(currentBilling.balanceDue)})`}
                              </span>
                            </>
                          )}
                        </button>

                        <button
                          type="button"
                          onClick={handleSavePackageAndVitals}
                          disabled={saving}
                          className="px-4 py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs shadow-sm transition-all cursor-pointer flex items-center gap-1.5"
                        >
                          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                          <span>Save Booking Details</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* 3-Step Stepper Header */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {[
                { step: 1, title: 'Stage 1: OPHE Payment Link & Collection', desc: 'Generate link & notify via WhatsApp/Email', isDone: isStage1Done },
                { step: 2, title: 'Stage 2: Care Provider Assignment & Active Care', desc: isProviderAssigned ? (allScheduleDaysCompleted ? 'All duty days completed ✓' : 'Provider assigned • Active care in-progress') : 'Attendant/Nurse allocation & active care', isDone: isStage2Done },
                { step: 3, title: 'Stage 3: Completion & Provider Payout', desc: 'Care settlement & provider compensation', isDone: isStage3Done }
              ].map((s) => {
                const isDone = s.isDone;
                const isCurrent = currentStep === s.step;
                const isTabActive = activeStageTab === s.step;

                return (
                  <button
                    key={s.step}
                    onClick={() => {
                      setActiveStageTab(s.step);
                      if (s.step === 3) {
                        setShowExtensionSection(false);
                        setPaymentOptionMode('standard');
                      }
                    }}
                    className={`p-3.5 rounded-xl border text-left transition-all relative overflow-hidden cursor-pointer ${isTabActive
                        ? 'bg-teal-50 border-teal-600 shadow-sm ring-2 ring-teal-500/20'
                        : isDone
                          ? 'bg-emerald-50/50 border-emerald-200 hover:bg-emerald-50'
                          : 'bg-white border-slate-200 hover:border-slate-300'
                      }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className={`text-xs font-bold ${isTabActive ? 'text-teal-900' : isDone ? 'text-emerald-800' : 'text-slate-700'
                        }`}>
                        {s.title}
                      </span>
                      {isDone ? (
                        <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                      ) : isCurrent ? (
                        <span className="w-2 h-2 rounded-full bg-teal-600 animate-ping" />
                      ) : null}
                    </div>
                    <p className="text-[11px] text-slate-500 font-medium mt-1 leading-snug">{s.desc}</p>
                  </button>
                );
              })}
            </div>

            {/* ─── STAGE 1 CONTENT: PAYMENT & BREAKDOWN HUB ───────────────── */}
            {activeStageTab === 1 && (
              <div className="space-y-6 animate-in fade-in duration-200 pt-2">
                {isCaseSettled && (
                  <div className="flex items-center gap-2.5 px-4 py-3 bg-amber-50 rounded-xl border border-amber-200/90 text-amber-900 text-xs shadow-2xs">
                    <Lock className="w-4 h-4 text-amber-600 shrink-0" />
                    <span className="font-semibold leading-tight">
                      This case has been completed and settled. All package configurations, billing rates, and payment records are permanently locked in read-only mode.
                    </span>
                  </div>
                )}

                {/* Unified Stage 1 Card */}
                <div className="bg-white border border-slate-200/90 rounded-2xl shadow-sm overflow-hidden min-w-0">

                  {/* Card Header with Status Badge */}
                  <div className="px-6 py-4 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-slate-50/60">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full bg-teal-600 animate-pulse"></span>
                        <h3 className="text-sm font-bold text-slate-900">OPHE Payment Link & Care Package Hub</h3>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">
                        Select package & shift → adjust duration & discounts → live GST calculation → generate Razorpay link.
                      </p>
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                      {lead?.ophePaymentData?.status === 'paid' || totalPaidCollected > 0 ? (
                        <span className="px-3 py-1 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800 flex items-center gap-1.5 shadow-xs">
                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                          <span>● Payment Received (₹{safeInr(totalPaidCollected || lead?.ophePaymentData?.amount || lead?.opheServiceDetails?.totalAmount || 0)})</span>
                        </span>
                      ) : lead?.ophePaymentData?.paymentLink ? (
                        <span className="px-3 py-1 rounded-full text-xs font-bold bg-amber-100 text-amber-800 flex items-center gap-1.5 shadow-xs">
                          <Clock className="w-3.5 h-3.5 text-amber-600" />
                          <span>○ Payment Link Active (Pending)</span>
                        </span>
                      ) : (
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                          Draft (No Link Generated)
                        </span>
                      )}

                      <button
                        type="button"
                        onClick={() => setShowTransactionsModal(true)}
                        className="px-3 py-1 rounded-xl text-xs font-bold bg-slate-900 hover:bg-slate-800 text-white flex items-center gap-1.5 shadow-xs transition-all cursor-pointer"
                        title="Open full payment & transaction ledger table"
                      >
                        <FileText className="w-3.5 h-3.5 text-teal-400" />
                        <span>Transaction Ledger</span>
                        {Array.isArray(lead?.ophePaymentHistory) && lead.ophePaymentHistory.length > 0 && (
                          <span className="px-1.5 py-0.2 rounded-full text-[10px] bg-teal-500 text-slate-950 font-black">
                            {lead.ophePaymentHistory.length}
                          </span>
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Stage 1 Standard Booking Subheader */}
                  <div className="px-6 pt-4 pb-2 flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-100">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-xl bg-teal-600 text-white flex items-center justify-center font-black text-xs shadow-xs">
                        <CreditCard className="w-4 h-4" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-black text-slate-900 uppercase tracking-wider">1. Payment (Standard Booking)</span>
                          <span className="text-[9px] bg-teal-100 text-teal-800 font-bold px-1.5 py-0.2 rounded">Fresh Booking</span>
                        </div>
                        <p className="text-[11px] text-slate-500 font-medium">
                          Configure care package & dispatch standard Razorpay link for the full service package.
                        </p>
                      </div>
                    </div>

                    {!showExtensionSection && !isCaseSettled && (
                      <button
                        type="button"
                        onClick={() => {
                          setShowExtensionSection(true);
                          setPaymentOptionMode('extension');
                          setActiveStageTab(2);
                        }}
                        className="px-3 py-1.5 rounded-xl bg-emerald-50 hover:bg-emerald-100 text-emerald-800 font-bold text-xs border border-emerald-300 flex items-center gap-1.5 transition-all cursor-pointer shadow-2xs"
                        title="Extend care days or upgrade shift for this patient"
                      >
                        <RotateCw className="w-3.5 h-3.5 text-emerald-700" />
                        <span>Need to add extra days or upgrade shift? Click <strong>2. Extend Care / Upgrade Shift</strong> above</span>
                      </button>
                    )}
                  </div>

                  <fieldset disabled={isCaseSettled} className={`p-4 sm:p-6 space-y-6 border-0 m-0 w-full min-w-0 [min-inline-size:0] block ${isCaseSettled ? 'opacity-90 select-none' : ''}`}>

                    {/* ── Standard Fresh Booking Configuration ── */}
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-xs font-bold text-slate-800 uppercase tracking-wider flex items-center gap-1.5">
                          <span className="w-5 h-5 rounded-full bg-teal-600 text-white text-[11px] font-bold flex items-center justify-center">1</span>
                          <span>Service Package, Shift & Duration</span>
                        </span>
                        <span className="text-[11px] text-slate-400">Auto-loads catalog price · Shift hours adjust per day rate</span>
                      </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          {/* Service Package Selector */}
                          <div className="md:col-span-2">
                            <div className="flex items-center justify-between mb-1">
                              <label className="block text-xs font-bold text-slate-900">Selected Service Package *</label>
                              {paymentForm.catalogPrice > 0 && (
                                <div className="flex items-center gap-1.5">
                                  <span className="text-[10px] text-teal-700 font-bold bg-teal-50 border border-teal-200 px-2 py-0.5 rounded-full flex items-center gap-1">
                                    <Sparkles className="w-3 h-3 text-teal-600" />
                                    <span>Catalog Base: ₹{paymentForm.catalogPrice} / Day ({paymentForm.shiftHours}h)</span>
                                  </span>
                                  {Number(paymentForm.quotedPrice) !== Number(paymentForm.catalogPrice) && (
                                    <button
                                      type="button"
                                      onClick={() => handlePerDayBaseChange(paymentForm.catalogPrice)}
                                      className="text-[10px] text-teal-700 hover:text-teal-900 bg-teal-100/60 px-2 py-0.5 rounded-md font-bold cursor-pointer transition-all"
                                    >
                                      Apply Catalog ₹{paymentForm.catalogPrice}
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                            <select
                              value={paymentForm.serviceId || ''}
                              onChange={(e) => handleServiceSelect(e.target.value)}
                              className="w-full text-xs px-3.5 py-2.5 rounded-xl border border-slate-300 bg-white font-bold text-slate-900 focus:ring-2 focus:ring-teal-500 focus:outline-none shadow-xs"
                            >
                              <option value="">-- Select or Change OPHE Service --</option>
                              {services.map((s) => {
                                const p12 = s.pricing?.hours12 || s.pricing?.perDay || s.durationPricing?.Day || s.basePrice || 0;
                                const p24 = s.pricing?.hours24 || 0;
                                return (
                                  <option key={s._id} value={s._id}>
                                    {s.serviceName || s.name} {p12 > 0 ? `(12h: ₹${p12}${p24 > 0 ? ` | 24h: ₹${p24}` : ''})` : ''}
                                  </option>
                                );
                              })}
                            </select>
                          </div>

                          {/* Category (Optional Switcher) */}
                          <div>
                            <label className="block text-xs font-semibold text-slate-700 mb-1">Service Category</label>
                            <select
                              value={paymentForm.categoryId || ''}
                              onChange={(e) => setPaymentForm(p => ({ ...p, categoryId: e.target.value }))}
                              className="w-full text-xs px-3 py-2.5 rounded-xl border border-slate-300 bg-white text-slate-800 focus:ring-2 focus:ring-teal-500 focus:outline-none"
                            >
                              <option value="">All Categories</option>
                              {categories.map((c) => (
                                <option key={c._id} value={c._id}>{c.name}</option>
                              ))}
                            </select>
                          </div>

                          {/* Shift Duration Selector */}
                          <div className="md:col-span-2">
                            <div className="flex items-center justify-between mb-1.5">
                              <label className="block text-xs font-bold text-slate-900">
                                Daily Shift Duration & Hours *
                              </label>
                              <span className="text-[11px] text-slate-500 font-medium">
                                Select shift to auto-load standard package rates
                              </span>
                            </div>

                            {(() => {
                              const curServ = services.find(s => s._id === paymentForm.serviceId);
                              const r12 = curServ?.pricing?.hours12 || curServ?.pricing?.perDay || curServ?.durationPricing?.Day || curServ?.basePrice || 0;
                              const r24 = curServ?.pricing?.hours24 || (r12 ? Math.round(r12 * 1.8) : 0);
                              const rCustom = curServ?.pricing?.customHoursRate || 0;

                              const shiftOptions = [
                                {
                                  hours: 12,
                                  label: '12 Hours (Day/Night)',
                                  desc: 'Standard 12h day or night shift',
                                  rateLabel: r12 > 0 ? `₹${safeInr(r12)}/day` : 'Standard Rate'
                                },
                                {
                                  hours: 24,
                                  label: '24 Hours (Full Day Live-in)',
                                  desc: '24/7 dedicated patient care attendant',
                                  rateLabel: r24 > 0 ? `₹${safeInr(r24)}/day` : 'Live-in Rate'
                                },
                                {
                                  hours: 8,
                                  label: 'Custom Hours',
                                  desc: 'Custom daily shift timing',
                                  rateLabel: rCustom > 0 ? `₹${safeInr(rCustom)}/hr base` : 'Hourly / Custom'
                                }
                              ];

                              return (
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                                  {shiftOptions.map((shift) => {
                                    const isSelected = Number(paymentForm.shiftHours) === shift.hours || 
                                      (shift.hours === 8 && paymentForm.shiftHours !== 12 && paymentForm.shiftHours !== 24);
                                    return (
                                      <button
                                        key={shift.hours}
                                        type="button"
                                        onClick={() => handleShiftChange(shift.hours, shift.label)}
                                        className={`p-3 rounded-2xl border-2 text-left transition-all cursor-pointer relative overflow-hidden flex flex-col justify-between ${
                                          isSelected
                                            ? 'bg-teal-50/80 border-teal-600 ring-2 ring-teal-500/20 shadow-md'
                                            : 'bg-white border-slate-200/90 hover:border-teal-300 hover:shadow-xs text-slate-700'
                                        }`}
                                      >
                                        <div>
                                          <div className="flex items-center justify-between mb-1">
                                            <span className={`text-xs font-black ${isSelected ? 'text-teal-950' : 'text-slate-800'}`}>
                                              {shift.label}
                                            </span>
                                            {isSelected && (
                                              <span className="w-5 h-5 rounded-full bg-teal-600 text-white flex items-center justify-center shrink-0 shadow-2xs">
                                                <Check className="w-3 h-3 stroke-[3]" />
                                              </span>
                                            )}
                                          </div>
                                          <span className="text-[11px] text-slate-500 font-medium block leading-snug">
                                            {shift.desc}
                                          </span>
                                        </div>

                                        <div className="mt-2.5 pt-2 border-t border-slate-100/80 flex items-center justify-between">
                                          <span className="text-[10px] text-slate-400 font-bold uppercase">Package Price:</span>
                                          <span className={`text-xs font-black ${isSelected ? 'text-teal-800' : 'text-slate-700'}`}>
                                            {shift.rateLabel}
                                          </span>
                                        </div>
                                      </button>
                                    );
                                  })}
                                </div>
                              );
                            })()}

                            {paymentForm.shiftHours !== 12 && paymentForm.shiftHours !== 24 && (
                              <div className="mt-2.5 flex items-center gap-3 bg-slate-50 p-2.5 rounded-xl border border-slate-200">
                                <span className="text-xs font-bold text-slate-700">Custom Shift Hours per Day:</span>
                                <input
                                  type="number"
                                  min="1"
                                  max="24"
                                  value={paymentForm.shiftHours || ''}
                                  onChange={(e) => handleShiftChange(Number(e.target.value), `${e.target.value} Hours Custom Shift`)}
                                  className="w-20 text-xs px-2.5 py-1.5 rounded-lg border border-slate-300 bg-white font-black text-center focus:ring-2 focus:ring-teal-500 focus:outline-none"
                                />
                                <span className="text-xs text-slate-500 font-bold">hours / day</span>
                              </div>
                            )}
                          </div>

                          {/* Final Negotiated Base Rate Per Day */}
                          <div>
                            <div className="flex items-center justify-between mb-1">
                              <label className="block text-xs font-bold text-slate-900">Agreed Base Rate (₹ / Day) *</label>
                              <span className="text-[10px] text-amber-700 font-bold bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded">Excl. GST</span>
                            </div>
                            <div className="relative">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold text-sm">₹</span>
                              <input
                                type="number"
                                value={paymentForm.quotedPrice ?? ''}
                                onChange={(e) => handlePerDayBaseChange(e.target.value)}
                                placeholder="e.g. 1200"
                                className="w-full text-base font-black pl-7 pr-3 py-2 rounded-xl border-2 border-amber-500 bg-white text-amber-950 focus:ring-2 focus:ring-amber-500 focus:outline-none shadow-sm"
                              />
                            </div>
                            <div className="flex items-center justify-between mt-1 text-[10px]">
                              <span className="text-slate-400">
                                {paymentForm.catalogPrice > 0 ? `Catalog: ₹${paymentForm.catalogPrice}/day (${paymentForm.shiftHours}h)` : 'Enter agreed daily rate'}
                              </span>
                              {Number(paymentForm.quotedPrice) !== Number(paymentForm.catalogPrice) && paymentForm.catalogPrice > 0 && (
                                <span className="font-bold text-amber-600">
                                  Negotiated ({Number(paymentForm.quotedPrice) < Number(paymentForm.catalogPrice) ? `₹${paymentForm.catalogPrice - Number(paymentForm.quotedPrice)} discount` : 'custom rate'})
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Duration in Days with Clean Quick Presets */}
                          <div>
                            <div className="flex items-center justify-between mb-1">
                              <label className="block text-xs font-semibold text-slate-700">Care Duration (Days) *</label>
                              <span className="text-[11px] text-teal-700 font-bold bg-teal-50 px-2 py-0.5 rounded-full border border-teal-200">
                                {paymentForm.daysCount || 1} {Number(paymentForm.daysCount) > 1 ? 'Days' : 'Day'}
                              </span>
                            </div>
                            <div className="space-y-1.5">
                              <input
                                type="number"
                                min="1"
                                value={paymentForm.daysCount ?? ''}
                                onChange={(e) => handleDaysChange(e.target.value)}
                                onBlur={() => {
                                  if (!paymentForm.daysCount || Number(paymentForm.daysCount) < 1) {
                                    handleDaysChange(1);
                                  }
                                }}
                                placeholder="e.g. 7"
                                className="w-full text-xs px-3 py-2 rounded-xl border border-slate-300 bg-white font-black text-slate-900 focus:ring-2 focus:ring-teal-500 focus:outline-none"
                              />
                              {/* Quick Days Selector Chips */}
                              <div className="grid grid-cols-4 gap-1">
                                {[
                                  { count: 1, label: '1 Day' },
                                  { count: 7, label: '7 Days' },
                                  { count: 15, label: '15 Days' },
                                  { count: 30, label: '30 Days' }
                                ].map(item => (
                                  <button
                                    key={item.count}
                                    type="button"
                                    onClick={() => handleDaysChange(item.count)}
                                    className={`py-1 rounded-lg text-[10px] font-bold text-center transition-all cursor-pointer ${
                                      Number(paymentForm.daysCount) === item.count
                                        ? 'bg-teal-700 text-white shadow-xs'
                                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                    }`}
                                  >
                                    {item.label}
                                  </button>
                                ))}
                              </div>
                            </div>
                          </div>

                          {/* Care Start Date */}
                          <div>
                            <label className="block text-xs font-semibold text-slate-700 mb-1">Care Start Date</label>
                            <input
                              type="date"
                              value={paymentForm.startDate || ''}
                              onChange={(e) => handleStartDateChange(e.target.value)}
                              className="w-full text-xs px-3 py-2.5 rounded-xl border border-slate-300 bg-white focus:ring-2 focus:ring-teal-500 focus:outline-none font-medium cursor-pointer"
                            />
                          </div>

                          {/* Care End Date */}
                          <div>
                            <label className="block text-xs font-semibold text-slate-700 mb-1">Care End Date</label>
                            <input
                              type="date"
                              value={paymentForm.endDate || ''}
                              onChange={(e) => handleEndDateChange(e.target.value)}
                              className="w-full text-xs px-3 py-2.5 rounded-xl border border-slate-300 bg-white focus:ring-2 focus:ring-teal-500 focus:outline-none font-medium cursor-pointer"
                            />
                          </div>
                        </div>
                    </div>


                    {/* ── STEP 2: DISCOUNT & GST SETTINGS ── */}
                    <div className="pt-2 border-t border-slate-100">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                        {/* Discount Mode & Value (Only in standard/extra_days mode) */}
                        <div className="bg-slate-50/90 border border-slate-200 p-3.5 rounded-2xl space-y-2.5 shadow-2xs">
                          <div className="flex items-center justify-between">
                            <label className="block text-xs font-bold text-slate-800 flex items-center gap-1.5">
                              <Percent className="w-3.5 h-3.5 text-teal-600" />
                              <span>Discount (Before GST)</span>
                            </label>
                            {currentBilling.discountAmount > 0 && (
                              <span className="text-[10px] font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">
                                - ₹{safeInr(currentBilling.discountAmount)} Saved
                              </span>
                            )}
                          </div>

                          <div className="grid grid-cols-3 gap-1.5">
                            {[
                              { id: 'none', label: 'No Discount' },
                              { id: 'percentage', label: 'Percentage (%)' },
                              { id: 'fixed', label: 'Flat (₹)' }
                            ].map((mode) => (
                              <button
                                key={mode.id}
                                type="button"
                                onClick={() => handleDiscountTypeChange(mode.id)}
                                className={`py-1.5 px-2 rounded-xl text-xs font-bold text-center transition-all cursor-pointer ${paymentForm.discountType === mode.id
                                    ? 'bg-teal-700 text-white shadow-xs'
                                    : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-100'
                                  }`}
                              >
                                {mode.label}
                              </button>
                            ))}
                          </div>

                          {paymentForm.discountType !== 'none' && (
                            <div className="space-y-2 pt-1 animate-in fade-in">
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-semibold text-slate-600 shrink-0">
                                  {paymentForm.discountType === 'percentage' ? 'Discount %:' : 'Discount ₹:'}
                                </span>
                                <div className="relative flex-1">
                                  <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 font-bold text-xs">
                                    {paymentForm.discountType === 'percentage' ? '%' : '₹'}
                                  </span>
                                  <input
                                    type="number"
                                    min="0"
                                    max={paymentForm.discountType === 'percentage' ? 100 : currentBilling.rawBaseTotal}
                                    value={paymentForm.discountValue}
                                    onChange={(e) => handleDiscountValueChange(e.target.value)}
                                    placeholder={paymentForm.discountType === 'percentage' ? 'e.g. 10' : 'e.g. 500'}
                                    className="w-full text-xs font-black pl-7 pr-3 py-1.5 rounded-lg border border-slate-300 bg-white focus:ring-2 focus:ring-teal-500 focus:outline-none"
                                  />
                                </div>
                              </div>

                              {/* Quick Discount Presets */}
                              <div className="flex items-center gap-1.5 pt-0.5">
                                <span className="text-[10px] text-slate-400 font-bold">Quick:</span>
                                {paymentForm.discountType === 'percentage'
                                  ? [5, 10, 15, 20].map(pct => (
                                    <button
                                      key={pct}
                                      type="button"
                                      onClick={() => handleDiscountValueChange(pct)}
                                      className={`px-2 py-0.5 rounded text-[10px] font-bold cursor-pointer transition-all ${Number(paymentForm.discountValue) === pct
                                          ? 'bg-teal-700 text-white'
                                          : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-100'
                                        }`}
                                    >
                                      {pct}%
                                    </button>
                                  ))
                                  : [200, 500, 1000, 2000].map(amt => (
                                    <button
                                      key={amt}
                                      type="button"
                                      onClick={() => handleDiscountValueChange(amt)}
                                      className={`px-2 py-0.5 rounded text-[10px] font-bold cursor-pointer transition-all ${Number(paymentForm.discountValue) === amt
                                          ? 'bg-teal-700 text-white'
                                          : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-100'
                                        }`}
                                    >
                                      ₹{amt}
                                    </button>
                                  ))
                                }
                              </div>
                            </div>
                          )}
                        </div>

                        {/* GST Settings */}
                        <div className="bg-slate-50/90 border border-slate-200 p-3.5 rounded-2xl space-y-2.5 shadow-2xs">
                          <div className="flex items-center justify-between">
                            <label className="block text-xs font-bold text-slate-800">GST on Care Services</label>
                            <label className="flex items-center gap-1.5 cursor-pointer text-xs font-semibold text-teal-700 bg-teal-50 px-2 py-0.5 rounded-lg border border-teal-200/70">
                              <input
                                type="checkbox"
                                checked={paymentForm.applyGst}
                                onChange={(e) => handleGstToggle(e.target.checked)}
                                className="w-3.5 h-3.5 rounded text-teal-600 focus:ring-teal-500 cursor-pointer"
                              />
                              <span className="font-bold">Apply GST</span>
                            </label>
                          </div>

                          <select
                            disabled={!paymentForm.applyGst}
                            value={paymentForm.gstRate}
                            onChange={(e) => handleGstRateChange(e.target.value)}
                            className="w-full text-xs px-3 py-2 rounded-xl border border-slate-300 bg-white focus:ring-2 focus:ring-teal-500 focus:outline-none disabled:bg-slate-100 disabled:text-slate-400 font-bold text-slate-800"
                          >
                            <option value={18}>18% GST (Standard Healthcare / Homecare Services)</option>
                            <option value={12}>12% GST</option>
                            <option value={5}>5% GST</option>
                            <option value={0}>0% GST (Exempt / Nil Rated)</option>
                          </select>
                          <div className="flex items-center justify-between text-[10px] text-slate-500">
                            <span>GST Base: ₹{safeInr(currentBilling.discountedBaseTotal)}</span>
                            <span className="font-bold text-teal-800">+₹{safeInr(currentBilling.gstAmount)} GST</span>
                          </div>
                        </div>

                      </div>
                    </div>

                    {/* ── STEP 3: LIVE NEGOTIATED PRICING & GST BREAKDOWN CARD ── */}
                    <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-teal-950 text-white rounded-2xl p-5 shadow-xl border border-slate-700/60 space-y-4">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pb-3 border-b border-slate-700/60">
                        <div className="flex items-center gap-2">
                          <Sparkles className="w-4 h-4 text-teal-300" />
                          <span className="text-xs font-black text-teal-300 uppercase tracking-wider">
                            Dynamic Pricing & Tax Breakdown
                          </span>
                          <span className="text-[11px] text-slate-300 font-medium">
                            ({paymentForm.shiftType} · {currentBilling.days} {currentBilling.days > 1 ? 'Days' : 'Day'})
                          </span>
                        </div>

                        <div className="flex items-baseline gap-2">
                          <span className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">
                            Total Payable:
                          </span>
                          <span className="text-2xl font-black text-emerald-400 drop-shadow-xs">
                            ₹{safeInr(currentBilling.totalAmount, 2)}
                          </span>
                        </div>
                      </div>

                      {/* Standard 4-Grid Breakdown */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div className="bg-slate-800/90 border border-slate-700/60 rounded-xl p-3 shadow-2xs">
                          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">1. Gross Base</span>
                          <span className="text-sm font-black text-white block mt-1">
                            ₹{safeInr(currentBilling.rawBaseTotal, 2)}
                          </span>
                          <span className="text-[10px] text-slate-400">₹{currentBilling.dailyRate}/day × {currentBilling.days}d</span>
                        </div>

                        <div className="bg-slate-800/90 border border-slate-700/60 rounded-xl p-3 shadow-2xs">
                          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">2. Discount</span>
                          <span className="text-sm font-black block mt-1 text-amber-400">
                            {currentBilling.discountAmount > 0 ? ('- ₹' + safeInr(currentBilling.discountAmount, 2)) : '₹0.00'}
                          </span>
                          <span className="text-[10px] text-slate-400">
                            {paymentForm.discountType === 'percentage' ? (paymentForm.discountValue + '% applied') : paymentForm.discountType === 'fixed' ? 'Flat rebate' : 'No discount'}
                          </span>
                        </div>

                        <div className="bg-slate-800/90 border border-slate-700/60 rounded-xl p-3 shadow-2xs">
                          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">3. Net Taxable</span>
                          <span className="text-sm font-black text-teal-300 block mt-1">
                            ₹{safeInr(currentBilling.discountedBaseTotal, 2)}
                          </span>
                          <span className="text-[10px] text-slate-400">Base after discount</span>
                        </div>

                        <div className="bg-slate-800/90 border border-slate-700/60 rounded-xl p-3 shadow-2xs">
                          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">4. GST ({currentBilling.gstRate}%)</span>
                          <span className="text-sm font-black text-emerald-300 block mt-1">
                            + ₹{safeInr(currentBilling.gstAmount, 2)}
                          </span>
                          <span className="text-[10px] text-slate-400">{currentBilling.gstRate === 0 ? 'Tax Exempt' : (currentBilling.gstRate + '% standard')}</span>
                        </div>
                      </div>
                    </div>

                    {/* ── STEP 4: GENERATE LINK & 1-CLICK DISPATCH ── */}
                    <div className="bg-slate-50/90 border border-slate-200/90 rounded-2xl p-5 space-y-4">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 rounded-xl bg-teal-600 text-white flex items-center justify-center font-black text-xs shadow-xs">
                            <CreditCard className="w-4 h-4" />
                          </div>
                          <div>
                            <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider">
                              Step 2: Generate & Dispatch Razorpay Link
                            </h4>
                            <p className="text-[11px] text-slate-500">
                              Amount to collect: <strong className="text-teal-900">₹{safeInr(currentBilling.totalAmount, 2)}</strong> ({currentBilling.days} {currentBilling.days > 1 ? 'Days' : 'Day'}, {paymentForm.shiftType})
                            </p>
                          </div>
                        </div>

                        {isCaseSettled ? (
                          <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-slate-100 border border-slate-300 text-slate-600 font-bold text-xs select-none cursor-not-allowed shadow-2xs">
                            <Lock className="w-3.5 h-3.5 text-slate-500" />
                            <span>Stage 1 Finalized &amp; Locked (Read-Only)</span>
                          </div>
                        ) : (
                          <div className="flex flex-wrap items-center gap-2">
                            <button
                              type="button"
                              onClick={() => handleCreatePaymentLink('standard')}
                              disabled={generatingPayment}
                              className="px-5 py-2.5 rounded-xl text-white font-bold text-xs shadow-md transition-all flex items-center gap-2 cursor-pointer bg-teal-600 hover:bg-teal-700 shadow-teal-600/20"
                            >
                              {generatingPayment ? (
                                <>
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                  <span>Generating Razorpay Link...</span>
                                </>
                              ) : (
                                <>
                                  <CreditCard className="w-4 h-4" />
                                  <span>
                                    {lead?.ophePaymentData?.paymentLink
                                      ? ('Re-generate Link (₹' + safeInr(currentBilling.totalAmount) + ')')
                                      : ('⚡ Generate Razorpay Link (₹' + safeInr(currentBilling.totalAmount) + ')')}
                                  </span>
                                </>
                              )}
                            </button>

                            <button
                              type="button"
                              onClick={handleSavePackageAndVitals}
                              disabled={saving}
                              className="px-4 py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs shadow-sm transition-all cursor-pointer flex items-center gap-1.5"
                            >
                              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                              <span>Save Details</span>
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Active Generated Link & Multi-Channel Dispatch Box */}
                      {(() => {
                        const activePaymentLink = lead?.ophePaymentData?.paymentLink || (Array.isArray(lead?.ophePaymentHistory) && lead?.ophePaymentHistory[0]?.paymentLink) || '';
                        if (!activePaymentLink) return null;

                        const linkStatus = lead?.ophePaymentData?.status || (Array.isArray(lead?.ophePaymentHistory) && lead?.ophePaymentHistory[0]?.status) || 'pending';
                        const isPaid = linkStatus === 'paid';
                        const linkAmount = lead?.ophePaymentData?.amount || (Array.isArray(lead?.ophePaymentHistory) && lead?.ophePaymentHistory[0]?.amount) || currentBilling.totalAmount;

                        return (
                          <div className="pt-4 border-t border-teal-100/90 space-y-4 animate-in fade-in">
                            {/* Top Link Banner & URL Bar */}
                            <div className="bg-gradient-to-r from-teal-50/90 via-emerald-50/60 to-slate-50 border border-teal-200/90 rounded-2xl p-4 shadow-sm space-y-3">
                              <div className="flex flex-wrap items-center justify-between gap-2">
                                <div className="flex items-center gap-2">
                                  <span className="flex h-2.5 w-2.5 rounded-full bg-emerald-500 animate-pulse" />
                                  <h5 className="text-xs font-black uppercase tracking-wider text-teal-950 flex items-center gap-1.5">
                                    <Link2 className="w-4 h-4 text-teal-700" />
                                    Active Payment Link ({linkStatus === 'paid' ? 'Paid' : `₹${safeInr(linkAmount)}`})
                                  </h5>
                                  {isPaid ? (
                                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-300 flex items-center gap-1">
                                      <CheckCircle2 className="w-3 h-3 text-emerald-600" /> PAID
                                    </span>
                                  ) : (
                                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-300 flex items-center gap-1">
                                      <Clock className="w-3 h-3 text-amber-600" /> PENDING PAYMENT
                                    </span>
                                  )}
                                </div>

                                <div className="flex items-center gap-1.5">
                                  <button
                                    type="button"
                                    onClick={() => handleSyncPayment(false)}
                                    disabled={syncingPayment}
                                    className="px-3 py-1.5 rounded-xl bg-white hover:bg-slate-50 text-emerald-800 border border-emerald-200 font-bold text-xs flex items-center gap-1.5 shadow-sm transition-all cursor-pointer"
                                  >
                                    <RefreshCw className={`w-3.5 h-3.5 text-emerald-600 ${syncingPayment ? 'animate-spin' : ''}`} />
                                    <span>Sync Status</span>
                                  </button>
                                </div>
                              </div>

                              {/* URL Input with Copy & Open */}
                              <div className="flex flex-wrap sm:flex-nowrap items-center gap-2 p-1.5 bg-white rounded-xl border border-teal-200/80 shadow-inner">
                                <span className="text-[10px] font-black uppercase text-teal-800 bg-teal-50 px-2.5 py-1 rounded">PAYMENT URL</span>
                                <input
                                  type="text"
                                  readOnly
                                  value={activePaymentLink}
                                  className="flex-1 min-w-0 text-xs font-mono text-teal-900 bg-transparent px-2 py-1 border-none focus:outline-none select-all truncate"
                                />
                                <button
                                  type="button"
                                  onClick={() => {
                                    navigator.clipboard.writeText(activePaymentLink);
                                    toast.success('Payment Link copied to clipboard!');
                                  }}
                                  className="px-3 py-1.5 rounded-lg bg-teal-50 hover:bg-teal-100 text-teal-800 font-bold text-xs flex items-center gap-1.5 border border-teal-200 cursor-pointer transition-all flex-shrink-0"
                                >
                                  <Copy className="w-3.5 h-3.5 text-teal-600" />
                                  <span>Copy Link</span>
                                </button>
                                <a
                                  href={activePaymentLink}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="px-3 py-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs flex items-center gap-1.5 cursor-pointer transition-all flex-shrink-0"
                                >
                                  <ExternalLink className="w-3.5 h-3.5" />
                                  <span>Open Link</span>
                                </a>
                              </div>
                            </div>

                            {/* Multi-Channel Dispatch Hub (Primary & Alternative) */}
                            <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm space-y-4">
                              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pb-2.5 border-b border-slate-100">
                                <div className="flex items-center gap-2">
                                  <Send className="w-4 h-4 text-teal-600" />
                                  <h5 className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                                    Send Payment Link (Primary & Alternative Dispatch)
                                  </h5>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => handleSendNotification('both', activePaymentLink, 'all')}
                                  disabled={sendingNotification}
                                  className="px-3.5 py-1.5 rounded-xl bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-700 hover:to-emerald-700 text-white font-bold text-xs shadow-sm flex items-center gap-1.5 transition-all cursor-pointer"
                                >
                                  {sendingNotification ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                                  <span>⚡ Dispatch to All (Primary & Alt)</span>
                                </button>
                              </div>

                              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                {/* Box 1: Primary Contact (Patient) */}
                                <div className="p-3.5 bg-slate-50/90 rounded-xl border border-slate-200/80 space-y-3">
                                  <div className="flex items-center justify-between">
                                    <span className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                                      <User className="w-3.5 h-3.5 text-blue-600" />
                                      1. Primary Contact (Patient)
                                    </span>
                                    <span className="text-[10px] font-bold text-blue-700 bg-blue-50 px-2 py-0.5 rounded border border-blue-200">
                                      Primary Recipient
                                    </span>
                                  </div>

                                  <div className="space-y-2">
                                    <div>
                                      <label className="block text-[11px] font-semibold text-slate-600 mb-0.5">Primary Mobile / WhatsApp</label>
                                      <input
                                        type="tel"
                                        maxLength={10}
                                        value={paymentForm.primaryPhone ?? (lead.patientPhone || '')}
                                        onChange={(e) => {
                                          const cleaned = sanitizePhoneNumber(e.target.value);
                                          setPaymentForm(p => ({ ...p, primaryPhone: cleaned }));
                                        }}
                                        placeholder="e.g. 9876543210"
                                        className="w-full text-xs px-3 py-2 rounded-xl border border-slate-300 bg-white focus:ring-1 focus:ring-teal-500 focus:outline-none"
                                      />
                                    </div>

                                    <div>
                                      <label className="block text-[11px] font-semibold text-slate-600 mb-0.5">Primary Email Address</label>
                                      <input
                                        type="email"
                                        value={paymentForm.primaryEmail ?? (lead.patientEmail || '')}
                                        onChange={(e) => setPaymentForm(p => ({ ...p, primaryEmail: e.target.value }))}
                                        placeholder="patient@example.com"
                                        className="w-full text-xs px-3 py-2 rounded-xl border border-slate-300 bg-white focus:ring-1 focus:ring-teal-500 focus:outline-none"
                                      />
                                    </div>
                                  </div>

                                  {/* Send Buttons for Primary */}
                                  <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-slate-200/70">
                                    <button
                                      type="button"
                                      onClick={() => handleSendNotification('whatsapp', activePaymentLink, 'primary')}
                                      disabled={sendingNotification}
                                      className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs flex items-center gap-1.5 shadow-sm cursor-pointer transition-all"
                                    >
                                      <Send className="w-3 h-3" />
                                      <span>Send WhatsApp</span>
                                    </button>

                                    <button
                                      type="button"
                                      onClick={() => handleSendNotification('email', activePaymentLink, 'primary')}
                                      disabled={sendingNotification}
                                      className="px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs flex items-center gap-1.5 shadow-sm cursor-pointer transition-all"
                                    >
                                      <Mail className="w-3 h-3" />
                                      <span>Send Email</span>
                                    </button>

                                    <button
                                      type="button"
                                      onClick={() => handleSendNotification('both', activePaymentLink, 'primary')}
                                      disabled={sendingNotification}
                                      className="px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-900 text-white font-bold text-xs flex items-center gap-1.5 shadow-sm cursor-pointer transition-all ml-auto"
                                    >
                                      <span>Send Both</span>
                                    </button>
                                  </div>
                                </div>

                                {/* Box 2: Alternative Contact (Family / Relative) */}
                                <div className="p-3.5 bg-amber-50/40 rounded-xl border border-amber-200/70 space-y-3">
                                  <div className="flex items-center justify-between">
                                    <span className="text-xs font-bold text-amber-950 flex items-center gap-1.5">
                                      <Users className="w-3.5 h-3.5 text-amber-700" />
                                      2. Alternative Contact (Family / Attendant)
                                    </span>
                                    <span className="text-[10px] font-bold text-amber-800 bg-amber-100/80 px-2 py-0.5 rounded border border-amber-300">
                                      Alternate Recipient
                                    </span>
                                  </div>

                                  <div className="space-y-2">
                                    <div>
                                      <label className="block text-[11px] font-semibold text-slate-600 mb-0.5">Alternative Mobile / WhatsApp</label>
                                      <input
                                        type="tel"
                                        maxLength={10}
                                        value={paymentForm.alternateWhatsapp || ''}
                                        onChange={(e) => {
                                          const cleaned = sanitizePhoneNumber(e.target.value);
                                          setPaymentForm(p => ({ ...p, alternateWhatsapp: cleaned }));
                                        }}
                                        placeholder="e.g. 9812345678"
                                        className="w-full text-xs px-3 py-2 rounded-xl border border-amber-200 bg-white focus:ring-1 focus:ring-amber-500 focus:outline-none"
                                      />
                                    </div>

                                    <div>
                                      <label className="block text-[11px] font-semibold text-slate-600 mb-0.5">Alternative Email Address</label>
                                      <input
                                        type="email"
                                        value={paymentForm.alternateEmail || ''}
                                        onChange={(e) => setPaymentForm(p => ({ ...p, alternateEmail: e.target.value }))}
                                        placeholder="relative@example.com"
                                        className="w-full text-xs px-3 py-2 rounded-xl border border-amber-200 bg-white focus:ring-1 focus:ring-amber-500 focus:outline-none"
                                      />
                                    </div>
                                  </div>

                                  {/* Send Buttons for Alternative */}
                                  <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-amber-200/70">
                                    <button
                                      type="button"
                                      onClick={() => handleSendNotification('whatsapp', activePaymentLink, 'alternate')}
                                      disabled={sendingNotification}
                                      className="px-3 py-1.5 rounded-lg bg-emerald-700 hover:bg-emerald-800 text-white font-bold text-xs flex items-center gap-1.5 shadow-sm cursor-pointer transition-all"
                                    >
                                      <Send className="w-3 h-3" />
                                      <span>Send Alt WhatsApp</span>
                                    </button>

                                    <button
                                      type="button"
                                      onClick={() => handleSendNotification('email', activePaymentLink, 'alternate')}
                                      disabled={sendingNotification}
                                      className="px-3 py-1.5 rounded-lg bg-sky-600 hover:bg-sky-700 text-white font-bold text-xs flex items-center gap-1.5 shadow-sm cursor-pointer transition-all"
                                    >
                                      <Mail className="w-3 h-3" />
                                      <span>Send Alt Email</span>
                                    </button>

                                    <button
                                      type="button"
                                      onClick={() => handleSendNotification('both', activePaymentLink, 'alternate')}
                                      disabled={sendingNotification}
                                      className="px-3 py-1.5 rounded-lg bg-amber-900 hover:bg-amber-950 text-white font-bold text-xs flex items-center gap-1.5 shadow-sm cursor-pointer transition-all ml-auto"
                                    >
                                      <span>Send Both (Alt)</span>
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* If Payment is Paid: Post-Payment Current Medication Box */}
                            {isPaid && (
                              <div className="bg-emerald-50/80 border-2 border-emerald-300 rounded-2xl p-4 shadow-sm space-y-2 animate-in fade-in">
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pb-2 border-b border-emerald-200">
                                  <div className="flex items-center gap-2">
                                    <div className="w-8 h-8 rounded-xl bg-emerald-600 text-white flex items-center justify-center font-bold text-sm shadow-xs">
                                      <Pill className="w-4 h-4" />
                                    </div>
                                    <div>
                                      <div className="flex items-center gap-2">
                                        <h6 className="text-xs font-black text-emerald-950 uppercase tracking-wide">
                                          Patient Current Medication (Post-Payment)
                                        </h6>
                                        <span className="text-[10px] bg-emerald-200/80 text-emerald-900 font-bold px-2 py-0.2 rounded">
                                          Payment Verified
                                        </span>
                                      </div>
                                      <p className="text-[11px] text-emerald-800">
                                        Record patient medications for care protocols before dispatching caregiver / attendant.
                                      </p>
                                    </div>
                                  </div>
                                </div>
                                <div className="flex flex-col sm:flex-row gap-2 pt-1">
                                  <textarea
                                    rows={2}
                                    value={currentMedication}
                                    onChange={(e) => setCurrentMedication(e.target.value)}
                                    placeholder="Current medication information (e.g. Metformin 500mg, Telmisartan 40mg, Insulin, dosages and timings)..."
                                    className="flex-1 px-3 py-2 bg-white border border-emerald-300 focus:border-emerald-600 rounded-xl outline-none font-bold text-xs transition-all resize-none text-slate-800 placeholder:text-slate-400"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => handleSaveCurrentMedication()}
                                    disabled={savingMedication}
                                    className="self-end sm:self-auto px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-sm transition-all cursor-pointer disabled:opacity-50 shrink-0"
                                  >
                                    {savingMedication ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                                    <span>Save Medication</span>
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>

                    {/* ── STEP 3: LIVE PAYMENT LINKS & TRANSACTIONS LIST (CRUD) ── */}
                    <div className="pt-4 border-t border-slate-200 space-y-3">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <span className="w-5 h-5 rounded-full bg-slate-800 text-white text-[11px] font-bold flex items-center justify-center">3</span>
                          <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider">
                            Sent Payment Links & Transaction Records
                          </h4>
                          <span className="text-[10px] bg-slate-100 text-slate-700 font-bold px-2 py-0.5 rounded-full border border-slate-200">
                            {(() => {
                              const list = Array.isArray(lead.ophePaymentHistory) && lead.ophePaymentHistory.length > 0
                                ? lead.ophePaymentHistory
                                : (lead.ophePaymentData?.paymentLink ? [lead.ophePaymentData] : []);
                              return `${list.length} ${list.length === 1 ? 'Link' : 'Links'} Created`;
                            })()}
                          </span>
                        </div>

                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => handleSyncPayment(false)}
                            disabled={syncingPayment}
                            className="px-3 py-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold flex items-center gap-1.5 transition-all cursor-pointer"
                          >
                            <RefreshCw className={`w-3.5 h-3.5 ${syncingPayment ? 'animate-spin text-teal-600' : ''}`} />
                            <span>Refresh / Sync Status</span>
                          </button>
                        </div>
                      </div>

                      {/* Render Table */}
                      {(() => {
                        const historyList = Array.isArray(lead?.ophePaymentHistory) && lead.ophePaymentHistory.length > 0
                          ? lead.ophePaymentHistory
                          : lead?.ophePaymentData?.paymentLink
                            ? [{
                              _id: 'active_link',
                              amount: lead?.ophePaymentData?.amount || lead?.opheServiceDetails?.totalAmount || 0,
                              isExtension: Boolean(lead?.ophePaymentData?.isExtension),
                              paymentType: lead?.ophePaymentData?.isExtension ? 'Extension Payment' : 'Standard Payment',
                              description: lead?.ophePaymentData?.description || 'OPHE Care Package Payment',
                              paymentLink: lead?.ophePaymentData?.paymentLink,
                              paymentLinkId: lead?.ophePaymentData?.paymentLinkId,
                              razorpayPaymentId: lead?.ophePaymentData?.razorpayPaymentId,
                              status: lead?.ophePaymentData?.status || 'pending',
                              createdAt: lead?.ophePaymentData?.paidAt || lead?.createdAt,
                              paidAt: lead?.ophePaymentData?.paidAt
                            }]
                            : [];

                        if (historyList.length === 0) {
                          return (
                            <div className="p-6 text-center bg-slate-50/80 rounded-2xl border border-dashed border-slate-200">
                              <CreditCard className="w-8 h-8 text-slate-300 mx-auto mb-1.5" />
                              <p className="text-xs font-bold text-slate-700">No payment links generated yet</p>
                              <p className="text-[11px] text-slate-400 mt-0.5">Click "Generate Razorpay Link" above to send the first payment invoice.</p>
                            </div>
                          );
                        }

                        return (
                          <div className="overflow-x-auto rounded-2xl border border-slate-200 shadow-2xs bg-white">
                            <table className="w-full text-left border-collapse text-xs">
                              <thead>
                                <tr className="bg-slate-50 text-slate-600 font-bold uppercase text-[10px] tracking-wider border-b border-slate-200">
                                  <th className="py-2.5 px-3.5">#</th>
                                  <th className="py-2.5 px-3.5">Type</th>
                                  <th className="py-2.5 px-3.5">Date & Time</th>
                                  <th className="py-2.5 px-3.5">Shift / Description</th>
                                  <th className="py-2.5 px-3.5">Amount</th>
                                  <th className="py-2.5 px-3.5">Status</th>
                                  <th className="py-2.5 px-3.5">Payment Link</th>
                                  <th className="py-2.5 px-3.5 text-right">Actions (CRUD)</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-100">
                                {historyList.map((item, idx) => {
                                  const isPaid = item.status === 'paid';
                                  const isExt = item.isExtension || (item.paymentType && item.paymentType.includes('Extension'));
                                  const linkId = item._id || item.paymentLinkId || idx;

                                  return (
                                    <tr key={linkId} className="hover:bg-slate-50/80 transition-colors">
                                      <td className="py-3 px-3.5 font-bold text-slate-400 text-[11px]">{idx + 1}</td>

                                      <td className="py-3 px-3.5">
                                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-black uppercase ${isExt ? 'bg-purple-100 text-purple-800' : 'bg-teal-100 text-teal-800'
                                          }`}>
                                          {isExt ? <RotateCw className="w-3 h-3" /> : <CreditCard className="w-3 h-3" />}
                                          <span>{isExt ? 'Extension' : 'Standard'}</span>
                                        </span>
                                      </td>

                                      <td className="py-3 px-3.5 text-slate-600 text-[11px] whitespace-nowrap">
                                        <div className="font-bold text-slate-800">
                                          {item.paidAt ? new Date(item.paidAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : (item.createdAt ? new Date(item.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-')}
                                        </div>
                                        <div className="text-[10px] text-slate-400 font-medium">
                                          {item.paidAt ? new Date(item.paidAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : (item.createdAt ? new Date(item.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '')}
                                        </div>
                                      </td>

                                      <td className="py-3 px-3.5 text-slate-700 max-w-xs">
                                        <span className="font-semibold block truncate text-xs" title={item.description || 'OPHE Care Package'}>
                                          {item.description || 'OPHE Care Package'}
                                        </span>
                                        {item.razorpayPaymentId && (
                                          <span className="text-[10px] text-emerald-700 font-mono font-bold block mt-0.5">
                                            ID: {item.razorpayPaymentId}
                                          </span>
                                        )}
                                      </td>

                                      <td className="py-3 px-3.5 whitespace-nowrap">
                                        <span className="font-black text-sm text-slate-900">
                                          ₹{safeInr(item.amount, 2)}
                                        </span>
                                      </td>

                                      <td className="py-3 px-3.5 whitespace-nowrap">
                                        {isPaid ? (
                                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800">
                                            <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                                            <span>PAID</span>
                                          </span>
                                        ) : (
                                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800">
                                            <Clock className="w-3 h-3 text-amber-600" />
                                            <span>PENDING</span>
                                          </span>
                                        )}
                                      </td>

                                      <td className="py-3 px-3.5">
                                        {item.paymentLink ? (
                                          <div className="flex items-center gap-1.5">
                                            <button
                                              type="button"
                                              onClick={() => {
                                                navigator.clipboard.writeText(item.paymentLink);
                                                toast.success('Payment Link copied!');
                                              }}
                                              className="px-2 py-1 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-[11px] font-bold flex items-center gap-1 cursor-pointer"
                                              title="Copy Link"
                                            >
                                              <Copy className="w-3 h-3 text-teal-600" />
                                              <span>Copy</span>
                                            </button>
                                            <a
                                              href={item.paymentLink}
                                              target="_blank"
                                              rel="noreferrer"
                                              className="p-1 rounded-lg bg-teal-50 hover:bg-teal-100 text-teal-700 cursor-pointer"
                                              title="Open Link"
                                            >
                                              <ExternalLink className="w-3.5 h-3.5" />
                                            </a>
                                          </div>
                                        ) : (
                                          <span className="text-slate-400 text-[11px]">-</span>
                                        )}
                                      </td>

                                      <td className="py-3 px-3.5 text-right whitespace-nowrap">
                                        <div className="flex items-center justify-end gap-1.5">
                                          {/* WhatsApp Send (Primary) */}
                                          <button
                                            type="button"
                                            onClick={() => handleSendNotification('whatsapp', item.paymentLink, 'primary')}
                                            disabled={sendingNotification}
                                            className="p-1.5 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-700 cursor-pointer transition-all border border-emerald-200"
                                            title="Send to Primary Patient WhatsApp"
                                          >
                                            <Send className="w-3.5 h-3.5 text-emerald-600" />
                                          </button>

                                          {/* Email Send (Primary) */}
                                          <button
                                            type="button"
                                            onClick={() => handleSendNotification('email', item.paymentLink, 'primary')}
                                            disabled={sendingNotification}
                                            className="p-1.5 rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-700 cursor-pointer transition-all border border-blue-200"
                                            title="Send to Primary Patient Email"
                                          >
                                            <Mail className="w-3.5 h-3.5 text-blue-600" />
                                          </button>

                                          {/* Send to Alternative */}
                                          <button
                                            type="button"
                                            onClick={() => handleSendNotification('both', item.paymentLink, 'alternate')}
                                            disabled={sendingNotification}
                                            className="px-2 py-1 rounded-lg bg-amber-50 hover:bg-amber-100 text-amber-800 cursor-pointer transition-all border border-amber-200 text-[10px] font-bold flex items-center gap-1"
                                            title="Send to Alternative Contact (WhatsApp + Email)"
                                          >
                                            <Users className="w-3 h-3 text-amber-700" />
                                            <span>Alt Send</span>
                                          </button>

                                          {/* Delete / Cancel Item */}
                                          {!isPaid && item._id && item._id !== 'active_link' && (
                                            <button
                                              type="button"
                                              onClick={() => handleDeletePaymentRecord(item._id)}
                                              className="p-1.5 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 cursor-pointer transition-all border border-red-200"
                                              title="Delete/Cancel Link"
                                            >
                                              <Trash2 className="w-3.5 h-3.5 text-red-500" />
                                            </button>
                                          )}
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        );
                      })()}
                    </div>

                  </fieldset>
                </div>

              </div>
            )}

            {/* ─── STAGE 2 CONTENT: PROVIDER ASSIGNMENT & ACTIVE CARE ───────── */}
            {activeStageTab === 2 && (() => {
              const bookedDays = Number(lead?.opheServiceDetails?.daysCount || paymentForm.daysCount || 7);
              const packageName = lead?.opheServiceDetails?.packageName || lead?.opheServiceDetails?.serviceName || lead?.treatmentRequired || 'OPHE Care Package';
              const activeShift = providerForm.shiftType || lead?.opheServiceDetails?.shiftType || paymentForm.shiftType || '12-Hour Day Shift';

              const extensionPayments = Array.isArray(lead?.ophePaymentHistory)
                ? lead.ophePaymentHistory.filter(p => p.isExtension || p.paymentType === 'Extension Payment')
                : [];
              const isExtended = extensionPayments.length > 0 || Boolean(lead?.ophePaymentData?.isExtension);

              const scheduleList = (Array.isArray(providerForm.dailyDutySchedule) && providerForm.dailyDutySchedule.length > 0)
                ? providerForm.dailyDutySchedule
                : (Array.isArray(lead?.opheProviderData?.dailyDutySchedule) ? lead.opheProviderData.dailyDutySchedule : []);
              const totalDaysCount = scheduleList.length > 0 ? scheduleList.length : bookedDays;
              const completedDaysCount = scheduleList.filter(d => d.status === 'Completed').length;
              const inProgressDaysCount = scheduleList.filter(d => d.status === 'In-Progress').length;
              const scheduledDaysCount = scheduleList.filter(d => d.status === 'Scheduled').length;
              const pendingDaysCount = Math.max(0, totalDaysCount - completedDaysCount);
              const progressPct = totalDaysCount > 0 ? Math.round((completedDaysCount / totalDaysCount) * 100) : 0;
              const allDaysCompleted = totalDaysCount > 0 && completedDaysCount >= totalDaysCount;

              return (
                <div className="space-y-6 animate-in fade-in duration-200 pt-2">
                  {isCaseSettled && (
                    <div className="flex items-center gap-2.5 px-4 py-3 bg-amber-50 rounded-xl border border-amber-200/90 text-amber-900 text-xs shadow-2xs">
                      <Lock className="w-4 h-4 text-amber-600 shrink-0" />
                      <span className="font-semibold leading-tight">
                        This case has been completed and settled. All care provider assignments, daily shifts, and duty logs are permanently locked in read-only mode.
                      </span>
                    </div>
                  )}

                  {/* Top Header & Overview Banner */}
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-slate-100">
                    <div>
                      <h3 className="text-sm font-bold text-slate-900">Stage 2: Care Provider Assignment & Execution</h3>
                      <p className="text-xs text-slate-500">Track booked service days, caregiver daily shift work timings (start & end), and execute care until all days complete.</p>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${lead?.opheProviderData?.providerName?.trim() ? 'bg-teal-100 text-teal-800' : 'bg-slate-100 text-slate-700'
                        }`}>
                        {lead?.opheProviderData?.providerName?.trim() ? `Provider: ${lead.opheProviderData.providerName}` : 'No Provider Assigned'}
                      </span>
                    </div>
                  </div>

                  {/* ── 1. BOOKED SERVICE DETAILS & EXTENSION CHECK CARD ── */}
                  <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-indigo-950 text-white rounded-2xl p-5 shadow-md space-y-4">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 pb-3 border-b border-white/10">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-teal-400/20 text-teal-300 border border-teal-400/30">
                            Booked Service Summary
                          </span>
                          {isExtended ? (
                            <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-emerald-400/20 text-emerald-300 border border-emerald-400/30 flex items-center gap-1">
                              <Sparkles className="w-3 h-3 text-emerald-400" /> Extension Active
                            </span>
                          ) : (
                            <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-white/10 text-slate-300 border border-white/10">
                              Standard Booking
                            </span>
                          )}
                        </div>
                        <h4 className="text-base font-bold text-white flex items-center gap-2">
                          <span>{packageName}</span>
                          <span className="text-xs font-normal text-slate-300">({activeShift})</span>
                        </h4>
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setShowExtensionSection(true);
                            setPaymentOptionMode('extension');
                            setActiveStageTab(2);
                          }}
                          className="px-3 py-1.5 rounded-xl bg-white/10 hover:bg-white/20 text-white text-xs font-bold transition-all flex items-center gap-1.5 border border-white/20 cursor-pointer"
                        >
                          <RotateCw className="w-3.5 h-3.5 text-teal-400" />
                          <span>🔄 Extend Care / Upgrade Shift</span>
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                      <div className="p-3 bg-white/5 rounded-xl border border-white/10">
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Total Booked Days</span>
                        <span className="text-lg font-black text-teal-300 mt-0.5 block">
                          {totalDaysCount} Days
                        </span>
                        <span className="text-[10px] text-slate-300">
                          {isExtended ? `(Includes extension days)` : `(Standard Package)`}
                        </span>
                      </div>

                      <div className="p-3 bg-white/5 rounded-xl border border-white/10">
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Care Schedule</span>
                        <span className="text-xs font-bold text-white mt-1 block truncate">
                          {providerForm.careStartDate || lead?.opheProviderData?.careStartDate || lead?.opheServiceDetails?.startDate || 'Not set'}
                        </span>
                        <span className="text-[10px] text-slate-400 block">to {providerForm.expectedEndDate || lead?.opheProviderData?.expectedEndDate || lead?.opheServiceDetails?.endDate || 'Not set'}</span>
                      </div>

                      <div className="p-3 bg-white/5 rounded-xl border border-white/10">
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Payment Verified</span>
                        <span className="text-lg font-black text-emerald-300 mt-0.5 block">
                          ₹{safeInr(totalPaidCollected || lead?.ophePaymentData?.amount || lead?.opheServiceDetails?.totalAmount || currentBilling.totalAmount)}
                        </span>
                        <span className="text-[10px] text-emerald-400 font-semibold">
                          {lead?.ophePaymentData?.status === 'paid' || totalPaidCollected > 0 ? '● Verified Paid' : '○ Pending'}
                        </span>
                      </div>

                      <div className="p-3 bg-white/5 rounded-xl border border-white/10">
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Delivery Progress</span>
                        <span className="text-lg font-black text-white mt-0.5 block">
                          {completedDaysCount} / {totalDaysCount} Days
                        </span>
                        <span className="text-[10px] text-amber-300 font-bold">
                          {allDaysCompleted ? '✓ All Days Completed' : `${pendingDaysCount} Days Pending`}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* ── 1.5 POST-PAYMENT CURRENT MEDICATION CARD ── */}
                  <div className="bg-white border-2 border-emerald-200/90 rounded-2xl p-5 shadow-sm space-y-3">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pb-3 border-b border-emerald-100">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-xl bg-emerald-600 text-white flex items-center justify-center font-bold text-sm shadow-xs">
                          <Pill className="w-4 h-4" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="text-sm font-bold text-slate-900">Current Medication</h4>
                            <span className="text-[10px] bg-emerald-100 text-emerald-800 font-black px-2 py-0.5 rounded-full uppercase border border-emerald-200">
                              Post-Payment
                            </span>
                          </div>
                          <p className="text-xs text-slate-500">
                            Medication information gathered after payment confirmation (e.g. Metformin 500mg, Telmisartan 40mg, Insulin).
                          </p>
                        </div>
                      </div>

                      {currentMedication ? (
                        <span className="text-[10px] bg-emerald-50 text-emerald-700 font-bold px-2.5 py-1 rounded-lg border border-emerald-200 flex items-center gap-1 shrink-0 self-start sm:self-auto">
                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                          Medication Recorded
                        </span>
                      ) : (
                        <span className="text-[10px] bg-amber-50 text-amber-700 font-bold px-2.5 py-1 rounded-lg border border-amber-200 flex items-center gap-1 shrink-0 self-start sm:self-auto">
                          <Clock className="w-3.5 h-3.5 text-amber-600" />
                          Pending Entry
                        </span>
                      )}
                    </div>

                    <div className="space-y-2">
                      <textarea
                        rows={3}
                        value={currentMedication || lead?.opheMedicalInfo?.currentMedication || ''}
                        onChange={(e) => setCurrentMedication(e.target.value)}
                        placeholder="Current medication information (e.g. Metformin 500mg, Telmisartan 40mg, Insulin, dosages and timings)..."
                        disabled={isCaseSettled}
                        readOnly={isCaseSettled}
                        className={`w-full px-4 py-2.5 rounded-xl outline-none font-bold text-xs sm:text-sm transition-all resize-none ${isCaseSettled
                            ? 'bg-slate-100 border border-slate-200 text-slate-700 cursor-not-allowed select-none'
                            : 'bg-slate-50 border-2 border-slate-200 focus:border-emerald-600 focus:bg-white text-slate-800 placeholder:text-slate-400'
                          }`}
                      />
                      <div className="flex items-center justify-between pt-1">
                        <span className="text-[11px] text-slate-400">
                          {currentMedication ? `${currentMedication.length} characters recorded` : 'No medication added yet'}
                        </span>
                        {!isCaseSettled ? (
                          <button
                            type="button"
                            onClick={() => handleSaveCurrentMedication()}
                            disabled={savingMedication}
                            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-sm transition-all cursor-pointer disabled:opacity-50"
                          >
                            {savingMedication ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                            <span>Save Medication</span>
                          </button>
                        ) : (
                          <span className="text-[11px] font-bold text-slate-400 italic">Locked (Read-Only)</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* ── 2. CARE PROVIDER DETAILS & REASSIGNMENT SECTION ── */}
                  {Boolean(lead?.opheProviderData?.providerName?.trim()) && !isEditingProvider ? (
                    /* ASSIGNED PROVIDER PROFILE CARD (WITH CHANGE PROVIDER OPTION) */
                    <div className="bg-white border-2 border-teal-200/80 rounded-2xl p-5 space-y-4 shadow-sm">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-slate-100">
                        <div className="flex items-center gap-2.5">
                          <div className="w-9 h-9 rounded-xl bg-teal-600 text-white flex items-center justify-center font-black text-sm shadow-xs">
                            <UserCheck className="w-5 h-5" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h4 className="text-sm font-bold text-slate-900">
                                {lead?.opheProviderData?.providerName}
                              </h4>
                              <span className="text-[10px] bg-emerald-100 text-emerald-800 font-black px-2 py-0.5 rounded-full border border-emerald-200 uppercase">
                                Active Provider
                              </span>
                            </div>
                            <p className="text-xs text-slate-500">
                              {(lead?.opheProviderData?.providerRole || 'Care Provider')} • {(lead?.opheProviderData?.shiftType || '12-Hour Shift')}
                            </p>
                          </div>
                        </div>

                        {/* Quick Contact & Change Provider Action */}
                        <div className="flex items-center gap-2 flex-wrap">
                          {lead?.opheProviderData?.providerPhone && (
                            <>
                              <a
                                href={`tel:${lead.opheProviderData.providerPhone}`}
                                className="px-3 py-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 text-xs font-bold flex items-center gap-1.5 transition-all"
                              >
                                <Phone className="w-3.5 h-3.5 text-slate-600" />
                                <span>{lead.opheProviderData.providerPhone}</span>
                              </a>
                              <a
                                href={`https://wa.me/${(lead.opheProviderData.providerPhone || '').replace(/[^0-9]/g, '')}`}
                                target="_blank"
                                rel="noreferrer"
                                className="px-3 py-1.5 rounded-xl bg-emerald-50 hover:bg-emerald-100 text-emerald-800 text-xs font-bold flex items-center gap-1.5 border border-emerald-200 transition-all"
                              >
                                <Send className="w-3.5 h-3.5 text-emerald-600" />
                                <span>WhatsApp</span>
                              </a>
                            </>
                          )}

                          {!isCaseSettled && (
                            <button
                              type="button"
                              onClick={() => setIsEditingProvider(true)}
                              className="px-3.5 py-1.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold flex items-center gap-1.5 shadow-sm transition-all cursor-pointer"
                            >
                              <RotateCw className="w-3.5 h-3.5" />
                              <span>🔄 Change / Reassign Provider</span>
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Provider Details Summary Grid */}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                        <div className="p-3 bg-slate-50 rounded-xl border border-slate-200/80">
                          <span className="text-[10px] font-bold text-slate-500 uppercase block">Provider Phone</span>
                          <span className="font-bold text-slate-800 font-mono mt-0.5 block truncate">
                            {lead?.opheProviderData?.providerPhone || 'Not provided'}
                          </span>
                        </div>

                        <div className="p-3 bg-slate-50 rounded-xl border border-slate-200/80">
                          <span className="text-[10px] font-bold text-slate-500 uppercase block">Provider Email</span>
                          <span className="font-bold text-slate-800 mt-0.5 block truncate">
                            {lead?.opheProviderData?.providerEmail || 'Not provided'}
                          </span>
                        </div>

                        <div className="p-3 bg-slate-50 rounded-xl border border-slate-200/80">
                          <span className="text-[10px] font-bold text-slate-500 uppercase block">Work Timing</span>
                          <span className="font-bold text-indigo-700 font-mono mt-0.5 block truncate">
                            {lead?.opheProviderData?.defaultStartTime || '08:00 AM'} → {lead?.opheProviderData?.defaultEndTime || '08:00 PM'}
                          </span>
                        </div>

                        <div className="p-3 bg-slate-50 rounded-xl border border-slate-200/80">
                          <span className="text-[10px] font-bold text-slate-500 uppercase block">Care Dates</span>
                          <span className="font-bold text-slate-800 mt-0.5 block truncate">
                            {lead?.opheProviderData?.careStartDate || 'Start'} to {lead?.opheProviderData?.expectedEndDate || 'End'}
                          </span>
                        </div>
                      </div>

                      {/* Attached Photos / Identity Docs */}
                      {((lead.documents || []).filter(d => d.type === 'provider_photo')).length > 0 && (
                        <div className="pt-2 border-t border-slate-100">
                          <span className="text-[11px] font-bold text-slate-600 block mb-1.5">Attached Provider Identity / Photos:</span>
                          <div className="flex items-center gap-2 flex-wrap">
                            {((lead.documents || []).filter(d => d.type === 'provider_photo')).map((doc, idx) => (
                              <div key={idx} className="relative w-16 h-16 rounded-lg overflow-hidden border border-slate-200 shadow-2xs group cursor-pointer" onClick={() => handleFilePreview(doc)}>
                                <img src={doc.url} alt={doc.name || 'Provider Photo'} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 flex items-center justify-center transition-colors">
                                  <Eye className="w-4 h-4 text-white opacity-0 group-hover:opacity-100" />
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Master Care Notes */}
                      {lead?.opheProviderData?.providerNotes && (
                        <div className="p-3 bg-amber-50/70 border border-amber-200 rounded-xl text-xs text-amber-900">
                          <span className="font-bold block text-[10px] uppercase text-amber-800 mb-0.5">Care Instructions:</span>
                          <p>{lead.opheProviderData.providerNotes}</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    /* ASSIGN / CHANGE CARE PROVIDER FORM */
                    <form onSubmit={(e) => { e.preventDefault(); handleAssignProvider(); }} className="bg-slate-50 border-2 border-indigo-200 rounded-2xl p-5 md:p-6 space-y-5 shadow-sm">
                      {/* Top Header */}
                      <div className="flex items-center justify-between pb-3 border-b border-slate-200">
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 rounded-xl bg-indigo-600 text-white flex items-center justify-center font-bold shadow-xs">
                            <UserCheck className="w-4 h-4" />
                          </div>
                          <div>
                            <h4 className="text-xs font-black text-slate-900 uppercase tracking-wider">
                              {isEditingProvider ? 'Change / Reassign Care Provider' : 'Assign Care Provider to Lead'}
                            </h4>
                            <p className="text-[11px] text-slate-500">Fill in caregiver details, shift timings, and active care schedule.</p>
                          </div>
                        </div>
                        {isEditingProvider && (
                          <button
                            type="button"
                            onClick={() => setIsEditingProvider(false)}
                            className="px-3 py-1.5 rounded-xl bg-slate-200 hover:bg-slate-300 text-slate-700 text-xs font-bold cursor-pointer transition-all"
                          >
                            ✕ Cancel
                          </button>
                        )}
                      </div>

                      {/* ── ROW 1: PROVIDER IDENTITY & CONTACT (3 COLUMNS) ── */}
                      <div>
                        <div className="flex items-center gap-1.5 mb-2">
                          <User className="w-3.5 h-3.5 text-indigo-600" />
                          <span className="text-[11px] font-black text-slate-700 uppercase tracking-wider">1. Care Provider Identity & Contact</span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3.5">
                          {/* Provider Name */}
                          <div>
                            <label className="flex items-center text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1.5 whitespace-nowrap">
                              Provider Full Name <span className="text-rose-500 ml-0.5">*</span>
                            </label>
                            <div className="relative">
                              <input
                                type="text"
                                value={providerForm.providerName}
                                onChange={(e) => setProviderForm(p => ({ ...p, providerName: e.target.value }))}
                                placeholder="e.g. Sunita Sharma"
                                required
                                className="w-full h-11 pl-10 pr-3.5 py-2.5 rounded-xl border border-slate-300 bg-white text-xs font-semibold text-slate-800 placeholder:text-slate-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none transition-all"
                              />
                              <User className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                            </div>
                          </div>

                          {/* Provider Phone */}
                          <div>
                            <label className="flex items-center text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1.5 whitespace-nowrap">
                              Provider Phone Number <span className="text-rose-500 ml-0.5">*</span>
                            </label>
                            <div className="relative">
                              <input
                                type="tel"
                                maxLength={10}
                                value={providerForm.providerPhone}
                                onChange={(e) => {
                                  const cleaned = sanitizePhoneNumber(e.target.value);
                                  setProviderForm(p => ({ ...p, providerPhone: cleaned }));
                                }}
                                placeholder="e.g. 9812345678"
                                required
                                className="w-full h-11 pl-10 pr-3.5 py-2.5 rounded-xl border border-slate-300 bg-white text-xs font-semibold text-slate-800 placeholder:text-slate-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none transition-all font-mono"
                              />
                              <Phone className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                            </div>
                          </div>

                          {/* Provider Email */}
                          <div>
                            <label className="flex items-center text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1.5 whitespace-nowrap">
                              Provider Email Address
                            </label>
                            <div className="relative">
                              <input
                                type="email"
                                value={providerForm.providerEmail}
                                onChange={(e) => setProviderForm(p => ({ ...p, providerEmail: e.target.value }))}
                                placeholder="e.g. sunita.provider@gmail.com"
                                className="w-full h-11 pl-10 pr-3.5 py-2.5 rounded-xl border border-slate-300 bg-white text-xs font-semibold text-slate-800 placeholder:text-slate-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none transition-all"
                              />
                              <Mail className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* ── ROW 2: SHIFT LOGISTICS & DAILY WORK TIMINGS (3 COLUMNS - IN PURE SEQUENCE!) ── */}
                      <div>
                        <div className="flex items-center gap-1.5 mb-2">
                          <Clock className="w-3.5 h-3.5 text-indigo-600" />
                          <span className="text-[11px] font-black text-slate-700 uppercase tracking-wider">2. Shift Type & Daily Work Hours (Start & End Time)</span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3.5">
                          {/* Shift Type */}
                          <div>
                            <label className="flex items-center text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1.5 whitespace-nowrap">
                              Assigned Shift Type
                            </label>
                            <div className="relative">
                              <select
                                value={providerForm.shiftType}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  let defStart = providerForm.defaultStartTime;
                                  let defEnd = providerForm.defaultEndTime;
                                  if (val.includes('Day')) {
                                    defStart = '08:00 AM';
                                    defEnd = '08:00 PM';
                                  } else if (val.includes('Night')) {
                                    defStart = '08:00 PM';
                                    defEnd = '08:00 AM';
                                  } else if (val.includes('24-Hour')) {
                                    defStart = '08:00 AM';
                                    defEnd = '08:00 AM';
                                  } else if (val.includes('Single')) {
                                    defStart = '10:00 AM';
                                    defEnd = '12:00 PM';
                                  }
                                  setProviderForm(p => ({
                                    ...p,
                                    shiftType: val,
                                    defaultStartTime: defStart,
                                    defaultEndTime: defEnd
                                  }));
                                }}
                                className="w-full h-11 pl-10 pr-3.5 py-2.5 rounded-xl border border-slate-300 bg-white text-xs font-semibold text-slate-800 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none cursor-pointer transition-all"
                              >
                                <option value="12-Hour Day Shift">12-Hour Day Shift (8am - 8pm)</option>
                                <option value="12-Hour Night Shift">12-Hour Night Shift (8pm - 8am)</option>
                                <option value="24-Hour Live-in Care">24-Hour Live-in Care</option>
                                <option value="Single Visit (1-2 Hours)">Single Visit (1-2 Hours)</option>
                              </select>
                              <Clock className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                            </div>
                          </div>

                          {/* Start Work Time */}
                          <div>
                            <label className="flex items-center text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1.5 whitespace-nowrap">
                              Start Work Time <span className="text-rose-500 ml-0.5">*</span>
                            </label>
                            <div className="relative">
                              <input
                                type="text"
                                value={providerForm.defaultStartTime}
                                onChange={(e) => setProviderForm(p => ({ ...p, defaultStartTime: e.target.value }))}
                                placeholder="08:00 AM"
                                required
                                className="w-full h-11 pl-10 pr-3.5 py-2.5 rounded-xl border border-slate-300 bg-white text-xs font-bold font-mono text-indigo-700 placeholder:text-slate-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none transition-all"
                              />
                              <Clock className="w-4 h-4 text-indigo-500 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                            </div>
                          </div>

                          {/* End Work Time */}
                          <div>
                            <label className="flex items-center text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1.5 whitespace-nowrap">
                              End Work Time <span className="text-rose-500 ml-0.5">*</span>
                            </label>
                            <div className="relative">
                              <input
                                type="text"
                                value={providerForm.defaultEndTime}
                                onChange={(e) => setProviderForm(p => ({ ...p, defaultEndTime: e.target.value }))}
                                placeholder="08:00 PM"
                                required
                                className="w-full h-11 pl-10 pr-3.5 py-2.5 rounded-xl border border-slate-300 bg-white text-xs font-bold font-mono text-indigo-700 placeholder:text-slate-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none transition-all"
                              />
                              <Clock className="w-4 h-4 text-indigo-500 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* ── ROW 3: ROLE & SERVICE CARE DATES (3 COLUMNS) ── */}
                      <div>
                        <div className="flex items-center gap-1.5 mb-2">
                          <Calendar className="w-3.5 h-3.5 text-indigo-600" />
                          <span className="text-[11px] font-black text-slate-700 uppercase tracking-wider">3. Provider Role & Service Care Dates</span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3.5">
                          {/* Provider Role */}
                          <div>
                            <label className="flex items-center text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1.5 whitespace-nowrap">
                              Provider Role / Designation
                            </label>
                            <div className="relative">
                              <select
                                value={providerForm.providerRole}
                                onChange={(e) => setProviderForm(p => ({ ...p, providerRole: e.target.value }))}
                                className="w-full h-11 pl-10 pr-3.5 py-2.5 rounded-xl border border-slate-300 bg-white text-xs font-semibold text-slate-800 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none cursor-pointer transition-all"
                              >
                                <option value="Registered Nurse (GNM/BSc)">Registered Nurse (GNM/BSc)</option>
                                <option value="Trained Care Attendant">Trained Care Attendant</option>
                                <option value="Elder Care Companion">Elder Care Companion</option>
                                <option value="Physiotherapist">Physiotherapist</option>
                                <option value="Medical Equipment Technician">Medical Equipment Technician</option>
                              </select>
                              <UserCheck className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                            </div>
                          </div>

                          {/* Care Start Date */}
                          <div>
                            <label className="flex items-center text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1.5 whitespace-nowrap">
                              Care Start Date
                            </label>
                            <div className="relative">
                              <input
                                type="date"
                                onClick={(e) => { try { e.target.showPicker?.(); } catch (_) { } }}
                                value={providerForm.careStartDate}
                                onChange={(e) => {
                                  const newStart = e.target.value;
                                  const bookedDays = Number(lead?.opheServiceDetails?.daysCount || paymentForm.daysCount || 7);
                                  let newEnd = providerForm.expectedEndDate;
                                  if (newStart && bookedDays > 0) {
                                    const d = new Date(newStart);
                                    d.setDate(d.getDate() + (bookedDays - 1));
                                    newEnd = d.toISOString().split('T')[0];
                                  }
                                  setProviderForm(p => ({
                                    ...p,
                                    careStartDate: newStart,
                                    expectedEndDate: newEnd
                                  }));
                                }}
                                className="w-full h-11 px-3.5 py-2.5 rounded-xl border border-slate-300 bg-white text-xs font-semibold text-slate-800 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none transition-all cursor-pointer"
                              />
                            </div>
                          </div>

                          {/* Expected End Date */}
                          <div>
                            <label className="flex items-center text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1.5 whitespace-nowrap">
                              Expected End Date
                            </label>
                            <div className="relative">
                              <input
                                type="date"
                                onClick={(e) => { try { e.target.showPicker?.(); } catch (_) { } }}
                                value={providerForm.expectedEndDate}
                                onChange={(e) => setProviderForm(p => ({ ...p, expectedEndDate: e.target.value }))}
                                className="w-full h-11 px-3.5 py-2.5 rounded-xl border border-slate-300 bg-white text-xs font-semibold text-slate-800 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none transition-all cursor-pointer"
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* ── ROW 4: PHOTO / ID & MASTER CARE INSTRUCTIONS ── */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3.5 pt-1">
                        {/* Upload Photo/ID */}
                        <div>
                          <label className="flex items-center text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1.5 whitespace-nowrap">
                            Provider Photo / ID Document
                          </label>
                          <label className="h-11 px-3.5 border-2 border-dashed border-slate-300 hover:border-indigo-500 bg-white hover:bg-indigo-50/40 rounded-xl flex items-center justify-center gap-2 cursor-pointer transition-all text-slate-600 text-xs font-bold shadow-2xs">
                            <input
                              type="file"
                              multiple
                              accept="image/*,.pdf"
                              hidden
                              disabled={uploadingDoc}
                              onChange={(e) => handleVaultUpload(e, 'provider_photo')}
                            />
                            {uploadingDoc ? (
                              <Loader2 className="w-4 h-4 animate-spin text-indigo-600" />
                            ) : (
                              <Upload className="w-4 h-4 text-indigo-600" />
                            )}
                            <span className="truncate">
                              {uploadingDoc
                                ? 'Uploading Document...'
                                : ((lead.documents || []).filter(d => d.type === 'provider_photo')).length > 0
                                  ? `${((lead.documents || []).filter(d => d.type === 'provider_photo')).length} Photo(s) Attached ✓`
                                  : '+ Upload Photo / ID'}
                            </span>
                          </label>
                        </div>

                        {/* Care Notes (Spans 2 columns) */}
                        <div className="md:col-span-2">
                          <label className="flex items-center text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1.5 whitespace-nowrap">
                            Master Care Instructions / Clinical Notes
                          </label>
                          <textarea
                            rows={1}
                            value={providerForm.providerNotes}
                            onChange={(e) => setProviderForm(p => ({ ...p, providerNotes: e.target.value }))}
                            placeholder="Care instructions, patient routine, medication timings, emergency contacts..."
                            className="w-full h-11 px-3.5 py-2.5 rounded-xl border border-slate-300 bg-white text-xs font-medium text-slate-800 placeholder:text-slate-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none resize-none transition-all"
                          />
                        </div>
                      </div>

                      {/* Save / Assign Action Buttons */}
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-3 border-t border-slate-200">
                        <div className="text-[11px] text-slate-500 font-medium">
                          Fields marked with <span className="text-rose-500 font-bold">*</span> are mandatory before assigning.
                        </div>

                        <div className="flex items-center gap-2">
                          {isEditingProvider && (
                            <button
                              type="button"
                              onClick={() => setIsEditingProvider(false)}
                              className="px-4 py-2.5 rounded-xl bg-slate-200 hover:bg-slate-300 text-slate-700 text-xs font-bold transition-all cursor-pointer"
                            >
                              Cancel
                            </button>
                          )}
                          <button
                            type="submit"
                            disabled={saving}
                            className="px-6 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold shadow-sm transition-all flex items-center gap-2 cursor-pointer disabled:opacity-50"
                          >
                            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                            <span>{isEditingProvider ? '✓ Update & Assign Provider' : '✓ Assign Provider & Save Roster'}</span>
                          </button>
                        </div>
                      </div>
                    </form>
                  )}

                  {/* ── 3. PER-DAY CARE DUTY & SHIFT TIMINGS ROSTER ── */}
                  <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-4 shadow-sm">

                    {/* Header & Controls */}
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 pb-3 border-b border-slate-200">
                      <div>
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-indigo-700" />
                          <h4 className="text-xs font-black text-slate-900 uppercase tracking-wider">
                            Daily Shift Work Timing & Execution Roster (Day 1 to Day {totalDaysCount})
                          </h4>
                        </div>
                        <p className="text-[11px] text-slate-500 mt-0.5">
                          Track daily caregiver work timings (start & end) and mark days completed as care is delivered.
                        </p>
                      </div>

                      {/* Quick Presets & Bulk Actions */}
                      {isCaseSettled ? (
                        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-xl bg-slate-100 border border-slate-300 text-slate-600 font-bold text-xs select-none cursor-not-allowed shadow-2xs">
                          <Lock className="w-3.5 h-3.5 text-slate-500" />
                          <span>Duty Roster Finalized (Read-Only)</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 flex-wrap">
                          <button
                            type="button"
                            onClick={() => {
                              const todayStr = getTodayDateStr();
                              const todayIdx = scheduleList.findIndex(d => getNormalizedDate(d.date) === todayStr);
                              if (todayIdx === -1) {
                                return toast.error(`No scheduled shift found for today (${todayStr}).`);
                              }
                              handleUpdateDutyDay(todayIdx, 'status', 'Completed');
                              toast.success(`Today's shift marked as Completed ✓`);
                            }}
                            className="px-3 py-1.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold transition-all cursor-pointer flex items-center gap-1 shadow-2xs"
                            title="Quick complete today's shift"
                          >
                            <CheckCircle2 className="w-3.5 h-3.5 text-white" />
                            <span>Mark Today Done ✓</span>
                          </button>

                          <button
                            type="button"
                            onClick={handleMarkAllDaysCompleted}
                            className="px-3 py-1.5 rounded-xl bg-emerald-50 hover:bg-emerald-100 text-emerald-800 border border-emerald-200 text-xs font-bold transition-all cursor-pointer flex items-center gap-1"
                          >
                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                            <span>Mark All Done ✓</span>
                          </button>

                          <button
                            type="button"
                            onClick={handleSaveDutyRoster}
                            disabled={saving}
                            className="px-3.5 py-1.5 rounded-xl bg-teal-600 hover:bg-teal-700 text-white text-xs font-bold shadow-sm transition-all cursor-pointer flex items-center gap-1.5"
                            title="Persist all shift changes to server"
                          >
                            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                            <span>Save Roster</span>
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Progress Bar & Status Counters */}
                    <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-200 space-y-2.5">
                      <div className="flex items-center justify-between text-xs">
                        <span className="font-bold text-slate-800 flex items-center gap-1.5">
                          <span>Care Execution Progress:</span>
                          <strong className={allDaysCompleted ? 'text-emerald-700' : 'text-indigo-700'}>
                            {completedDaysCount} of {totalDaysCount} Days Completed ({progressPct}%)
                          </strong>
                        </span>

                        <div className="flex items-center gap-2">
                          <span className="px-2.5 py-0.5 rounded-md text-[11px] font-bold bg-emerald-100 text-emerald-800">
                            {completedDaysCount} Done
                          </span>
                          {inProgressDaysCount > 0 && (
                            <span className="px-2.5 py-0.5 rounded-md text-[11px] font-bold bg-blue-100 text-blue-800">
                              {inProgressDaysCount} Active
                            </span>
                          )}
                          {pendingDaysCount > 0 && (
                            <span className="px-2.5 py-0.5 rounded-md text-[11px] font-bold bg-amber-100 text-amber-800">
                              {pendingDaysCount} Left
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="w-full bg-slate-200 h-2.5 rounded-full overflow-hidden">
                        <div
                          className={`h-full transition-all duration-300 ${allDaysCompleted ? 'bg-emerald-500' : 'bg-gradient-to-r from-teal-500 to-indigo-600'
                            }`}
                          style={{ width: `${Math.min(100, Math.max(5, progressPct))}%` }}
                        />
                      </div>

                      {/* Shift Status Policy Notice */}
                      <div className="flex items-center justify-between text-xs px-3.5 py-2 bg-indigo-50/70 border border-indigo-200/80 rounded-xl text-indigo-950 font-medium">
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-indigo-600 shrink-0" />
                          <span>
                            <strong>Shift Status Policy:</strong> Caregiver assignments &amp; shift timings are pre-configurable for all days. Only <strong>execution status</strong> (In-Progress / Completed) is locked until each shift's scheduled date arrives.
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* ── CRUD DATA TABLE ── */}
                    {(() => {
                      const primaryProviderName = (lead?.opheProviderData?.providerName || providerForm.providerName || '').trim();
                      const primaryProviderPhone = (lead?.opheProviderData?.providerPhone || providerForm.providerPhone || '').trim();

                      const filteredList = scheduleList.filter((day) => {
                        if (dutyStatusFilter !== 'all' && day.status !== dutyStatusFilter) return false;
                        if (dutySearchQuery.trim()) {
                          const q = dutySearchQuery.toLowerCase();
                          const matchDate = day.date?.toLowerCase().includes(q);
                          const matchCC = (day.assignedCCName || '').toLowerCase().includes(q);
                          const matchName = (day.providerName || '').toLowerCase().includes(q);
                          const matchNotes = (day.notes || '').toLowerCase().includes(q);
                          const matchStatus = (day.status || '').toLowerCase().includes(q);
                          if (!matchDate && !matchCC && !matchName && !matchNotes && !matchStatus) return false;
                        }
                        return true;
                      });

                      if (scheduleList.length === 0) {
                        return (
                          <div className="p-8 border-2 border-dashed border-slate-200 rounded-2xl text-center space-y-3 bg-slate-50/60">
                            <Calendar className="w-9 h-9 text-indigo-400 mx-auto" />
                            <div>
                              <p className="text-xs font-bold text-slate-800">No Duty Schedule Roster Created Yet</p>
                              <p className="text-[11px] text-slate-500 mt-0.5">
                                Assign a provider above or click below to generate the {bookedDays}-day work schedule matching this booked package.
                              </p>
                            </div>
                            <div className="flex items-center justify-center gap-2">
                              <button
                                type="button"
                                onClick={() => handleGenerateDailySchedule(bookedDays)}
                                className="px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs inline-flex items-center gap-1.5 cursor-pointer shadow-sm transition-all"
                              >
                                <Plus size={13} />
                                <span>Auto-Generate {bookedDays}-Day Schedule</span>
                              </button>
                            </div>
                          </div>
                        );
                      }

                      return (
                        <div className="overflow-x-auto rounded-xl border border-slate-200 shadow-xs">
                          <table className="w-full text-left text-xs border-collapse">
                            <thead>
                              <tr className="bg-slate-100/90 text-slate-700 font-bold border-b border-slate-200 text-[11px] uppercase tracking-wider">
                                <th className="py-3 px-3.5 whitespace-nowrap"># Day</th>
                                <th className="py-3 px-3.5 whitespace-nowrap">Duty Date</th>
                                <th className="py-3 px-3.5 whitespace-nowrap">Assigned CC (Coordinator)</th>
                                <th className="py-3 px-3.5 whitespace-nowrap">Care Provider (Attendant)</th>
                                <th className="py-3 px-3.5 whitespace-nowrap">Work Timing (Start → End)</th>
                                <th className="py-3 px-3.5 whitespace-nowrap">Status (Quick Toggle)</th>
                                <th className="py-3 px-3.5">Care & Vitals Notes</th>
                                <th className="py-3 px-3.5 text-right whitespace-nowrap">Actions</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-200/80 bg-white">
                              {filteredList.map((day, filteredIdx) => {
                                const actualIdx = scheduleList.indexOf(day);
                                const isCompleted = day.status === 'Completed';
                                const dayProvName = (day.providerName || '').trim();
                                const isDayPrimary = !dayProvName || (primaryProviderName && dayProvName.toLowerCase() === primaryProviderName.toLowerCase()) || dayProvName === 'Primary Provider';

                                const todayLocalStr = getTodayDateStr();
                                const dayNormalized = getNormalizedDate(day.date);
                                const isToday = Boolean(dayNormalized && dayNormalized === todayLocalStr);
                                const isPast = Boolean(dayNormalized && dayNormalized < todayLocalStr);
                                const isFuture = Boolean(dayNormalized && dayNormalized > todayLocalStr);
                                const isEditable = isToday;

                                return (
                                  <tr
                                    key={actualIdx}
                                    className={`transition-colors ${isToday
                                        ? 'bg-indigo-50/70 hover:bg-indigo-50/90 border-l-4 border-l-indigo-600 shadow-2xs'
                                        : isCompleted
                                          ? 'bg-emerald-50/30 hover:bg-emerald-50/60'
                                          : isPast
                                            ? 'bg-slate-50/40 hover:bg-slate-50/70 opacity-90'
                                            : 'bg-white hover:bg-slate-50/60 opacity-80'
                                      }`}
                                  >
                                    {/* Day # Badge */}
                                    <td className="py-3 px-3.5 whitespace-nowrap">
                                      <span className={`inline-flex items-center justify-center w-7 h-7 rounded-lg text-xs font-black shadow-2xs ${isToday
                                          ? 'bg-indigo-600 text-white ring-2 ring-indigo-300 ring-offset-1'
                                          : isCompleted
                                            ? 'bg-emerald-600 text-white'
                                            : 'bg-slate-800 text-white'
                                        }`}>
                                        D{day.dayNumber || actualIdx + 1}
                                      </span>
                                    </td>

                                    {/* Duty Date */}
                                    <td className="py-3 px-3.5 whitespace-nowrap font-bold text-slate-800">
                                      <div className="flex items-center gap-1.5">
                                        <Calendar className={`w-3.5 h-3.5 ${isToday ? 'text-indigo-600' : 'text-slate-400'}`} />
                                        <span className={isToday ? 'font-black text-indigo-950' : ''}>{day.date || '-'}</span>
                                        {isToday && (
                                          <span className="px-1.5 py-0.5 rounded text-[9px] font-black uppercase bg-indigo-600 text-white tracking-wider animate-pulse shadow-2xs">
                                            Today
                                          </span>
                                        )}
                                        {isPast && (
                                          <span className="text-[10px] text-slate-400 font-semibold">(Past)</span>
                                        )}
                                        {isFuture && (
                                          <span className="text-[10px] text-amber-600/80 font-medium">(Upcoming)</span>
                                        )}
                                      </div>
                                    </td>

                                    {/* Assigned CC (Care Coordinator) */}
                                    <td className="py-3 px-3.5 whitespace-nowrap">
                                      <div className="flex items-center gap-1.5 font-medium">
                                        <span className="w-5 h-5 rounded-full bg-teal-100 text-teal-800 font-black text-[9px] flex items-center justify-center flex-shrink-0 border border-teal-200">
                                          CC
                                        </span>
                                        <div>
                                          <span className="font-bold text-slate-900 text-xs block leading-tight">
                                            {day.assignedCCName || lead?.assignedCustomerCare?.name || 'Lead CC'}
                                          </span>
                                          {(day.assignedCCPhone || lead?.assignedCustomerCare?.phone) && (
                                            <span className="text-[10px] text-slate-400 font-mono block">
                                              {day.assignedCCPhone || lead?.assignedCustomerCare?.phone}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    </td>

                                    {/* Caregiver Provider - Fully interactive on all days */}
                                    <td className="py-3 px-3.5 whitespace-nowrap min-w-[270px]">
                                      <div className="space-y-1.5">
                                        <div className="flex items-center justify-between gap-2">
                                          <div className="flex items-center gap-1.5 min-w-0">
                                            <span className={`w-5 h-5 rounded-full flex items-center justify-center font-black text-[9px] flex-shrink-0 border ${isDayPrimary
                                                ? 'bg-teal-100 text-teal-800 border-teal-200'
                                                : 'bg-indigo-100 text-indigo-800 border-indigo-200'
                                              }`}>
                                              CP
                                            </span>
                                            <div className="min-w-0">
                                              <span className="font-bold text-slate-900 text-xs block leading-tight truncate" title={day.providerName || primaryProviderName || 'Primary Provider'}>
                                                {day.providerName || primaryProviderName || 'Primary Provider'}
                                              </span>
                                              {(day.providerPhone || (isDayPrimary ? primaryProviderPhone : '')) && (
                                                <span className="text-[10px] text-slate-400 font-mono block leading-none mt-0.5">
                                                  {day.providerPhone || primaryProviderPhone}
                                                </span>
                                              )}
                                            </div>
                                          </div>
                                          <span className={`px-2 py-0.5 rounded-md text-[9px] font-bold border shrink-0 ${isDayPrimary
                                              ? 'bg-teal-50 text-teal-700 border-teal-200'
                                              : 'bg-indigo-50 text-indigo-700 border-indigo-200'
                                            }`}>
                                            {isDayPrimary ? 'Primary Provider' : 'New Provider'}
                                          </span>
                                        </div>

                                        {!isCaseSettled ? (
                                          <div className="flex items-center gap-1.5">
                                            {/* Quick Selector: Switch to Primary Caregiver or Select Existing or Assign New */}
                                            <select
                                              value={isDayPrimary ? '__primary__' : (day.providerName ? `existing-${day.providerName}` : '__primary__')}
                                              onChange={(e) => {
                                                const val = e.target.value;
                                                if (val === '__primary__') {
                                                  handleUpdateDutyDay(actualIdx, 'providerName', primaryProviderName || 'Primary Provider');
                                                  handleUpdateDutyDay(actualIdx, 'providerPhone', primaryProviderPhone);
                                                  toast.success(`Day ${day.dayNumber || actualIdx + 1} set to Primary Provider: ${primaryProviderName || 'Primary'}`);
                                                } else if (val === '__assign_new__') {
                                                  handleOpenEditDutyModal({ ...day, providerOption: 'new' }, actualIdx, 'new');
                                                } else {
                                                  const found = availableProviders.find(p => p.name === val.replace('existing-', ''));
                                                  if (found) {
                                                    handleUpdateDutyDay(actualIdx, 'providerName', found.name);
                                                    handleUpdateDutyDay(actualIdx, 'providerPhone', found.phone);
                                                    toast.success(`Day ${day.dayNumber || actualIdx + 1} assigned to ${found.name}`);
                                                  }
                                                }
                                              }}
                                              className="flex-1 h-7 text-[11px] font-medium bg-slate-50 hover:bg-slate-100 border border-slate-300 rounded-lg px-2 text-slate-700 outline-none cursor-pointer focus:border-indigo-500 transition-all truncate"
                                              title="Quick switch caregiver for this day"
                                            >
                                              <option value="__primary__">✓ Primary: {primaryProviderName || 'Primary'}</option>
                                              {availableProviders.filter(p => !p.isPrimary).map(p => (
                                                <option key={p.id} value={`existing-${p.name}`}>🔄 {p.name}</option>
                                              ))}
                                              <option value="__assign_new__">➕ Assign New Provider...</option>
                                            </select>

                                            <button
                                              type="button"
                                              onClick={() => handleOpenEditDutyModal({ ...day, providerOption: 'new' }, actualIdx, 'new')}
                                              className="h-7 px-2.5 rounded-lg bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-bold text-[10px] border border-indigo-200 cursor-pointer transition-colors shrink-0 flex items-center gap-1 shadow-2xs"
                                              title="Assign New Provider for this Day"
                                            >
                                              <Plus className="w-3 h-3" />
                                              <span>New</span>
                                            </button>
                                          </div>
                                        ) : (
                                          <div className="text-[11px] text-slate-400 font-medium italic">
                                            {isDayPrimary ? 'Primary Provider Allocation' : 'Daily Provider Allocation'}
                                          </div>
                                        )}
                                      </div>
                                    </td>

                                    {/* Work Timing */}
                                    <td className="py-3 px-3.5 whitespace-nowrap">
                                      <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border font-mono font-bold text-[11px] ${isToday
                                          ? 'bg-indigo-50 border-indigo-200 text-indigo-900'
                                          : 'bg-slate-100 border-slate-200 text-slate-800'
                                        }`}>
                                        <Clock className={`w-3 h-3 ${isToday ? 'text-indigo-600' : 'text-slate-500'}`} />
                                        <span>{day.startTime || '08:00 AM'}</span>
                                        <span className="text-slate-400 font-sans">→</span>
                                        <span>{day.endTime || '08:00 PM'}</span>
                                      </div>
                                    </td>

                                    {/* Status: ONLY THIS COLUMN IS LOCKED FOR FUTURE DAYS */}
                                    <td className="py-3 px-3.5 whitespace-nowrap">
                                      {isCaseSettled ? (
                                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold border select-none ${day.status === 'Completed'
                                            ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
                                            : day.status === 'In-Progress'
                                              ? 'bg-blue-100 text-blue-800 border-blue-300'
                                              : day.status === 'Cancelled'
                                                ? 'bg-rose-100 text-rose-800 border-rose-300'
                                                : 'bg-amber-100 text-amber-800 border-amber-300'
                                          }`}>
                                          <Lock className="w-3 h-3 text-slate-500" />
                                          <span>{day.status || 'Completed'}</span>
                                        </span>
                                      ) : isToday ? (
                                        <select
                                          value={day.status || 'Scheduled'}
                                          onChange={(e) => handleUpdateDutyDay(actualIdx, 'status', e.target.value)}
                                          className={`px-2.5 py-1 rounded-full text-[11px] font-bold border outline-none cursor-pointer transition-all ${day.status === 'Completed'
                                              ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
                                              : day.status === 'In-Progress'
                                                ? 'bg-blue-100 text-blue-800 border-blue-300'
                                                : day.status === 'Cancelled' || day.status === 'Missed'
                                                  ? 'bg-rose-100 text-rose-800 border-rose-300'
                                                  : 'bg-amber-100 text-amber-800 border-amber-300'
                                            }`}
                                        >
                                          <option value="Scheduled">🟡 Scheduled</option>
                                          <option value="In-Progress">🔵 In-Progress</option>
                                          <option value="Completed">🟢 Completed</option>
                                          <option value="Cancelled">🔴 Cancelled</option>
                                          <option value="Leave">⚪ Leave / Off</option>
                                        </select>
                                      ) : isFuture ? (
                                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold bg-amber-50 text-amber-800 border border-amber-200 cursor-not-allowed" title={`Status locks to Scheduled until ${day.date} arrives`}>
                                          <Lock className="w-3 h-3 text-amber-600" />
                                          <span>{day.status || 'Scheduled'} (Locked)</span>
                                        </span>
                                      ) : (
                                        <select
                                          value={day.status || 'Completed'}
                                          onChange={(e) => handleUpdateDutyDay(actualIdx, 'status', e.target.value)}
                                          className={`px-2.5 py-1 rounded-full text-[11px] font-bold border outline-none cursor-pointer transition-all ${day.status === 'Completed'
                                              ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
                                              : day.status === 'In-Progress'
                                                ? 'bg-blue-100 text-blue-800 border-blue-300'
                                                : day.status === 'Cancelled' || day.status === 'Missed'
                                                  ? 'bg-rose-100 text-rose-800 border-rose-300'
                                                  : 'bg-slate-100 text-slate-700 border-slate-300'
                                            }`}
                                        >
                                          <option value="Completed">🟢 Completed</option>
                                          <option value="In-Progress">🔵 In-Progress</option>
                                          <option value="Scheduled">🟡 Scheduled</option>
                                          <option value="Cancelled">🔴 Cancelled</option>
                                          <option value="Leave">⚪ Leave / Off</option>
                                        </select>
                                      )}
                                    </td>

                                    {/* Care Notes */}
                                    <td className="py-3 px-3.5">
                                      <p className="text-[11px] text-slate-600 italic line-clamp-1 max-w-[200px]" title={day.notes}>
                                        {day.notes || <span className="text-slate-400 not-italic">No notes recorded</span>}
                                      </p>
                                    </td>

                                    {/* Action Buttons: Fully available on all days */}
                                    <td className="py-3 px-3.5 text-right whitespace-nowrap">
                                      {isCaseSettled ? (
                                        <div className="inline-flex items-center gap-1 text-[11px] font-bold text-slate-400 italic">
                                          <Lock className="w-3 h-3 text-slate-400" />
                                          <span>Locked</span>
                                        </div>
                                      ) : (
                                        <div className="flex items-center justify-end gap-1.5">
                                          {/* Edit Button (Opens Modal) */}
                                          <button
                                            type="button"
                                            onClick={() => handleOpenEditDutyModal(day, actualIdx)}
                                            className="p-1.5 rounded-lg bg-indigo-50 hover:bg-indigo-100 text-indigo-700 cursor-pointer transition-all border border-indigo-200"
                                            title="Edit Duty Shift"
                                          >
                                            <Edit className="w-3.5 h-3.5" />
                                          </button>

                                          {/* Duplicate / Clone Day Button */}
                                          <button
                                            type="button"
                                            onClick={() => handleDuplicateDutyDay(actualIdx)}
                                            className="p-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 cursor-pointer transition-all border border-slate-200"
                                            title="Clone Shift to Next Day"
                                          >
                                            <Copy className="w-3.5 h-3.5" />
                                          </button>

                                          {/* Delete Button */}
                                          <button
                                            type="button"
                                            onClick={() => handleRemoveDutyDay(actualIdx)}
                                            className="p-1.5 rounded-lg text-rose-600 hover:bg-rose-50 cursor-pointer transition-all border border-transparent hover:border-rose-200"
                                            title="Delete Duty Shift"
                                          >
                                            <Trash2 className="w-3.5 h-3.5" />
                                          </button>
                                        </div>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      );
                    })()}

                    {/* Table Footer Actions */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-2 border-t border-slate-100 text-xs">
                      {isCaseSettled ? (
                        <div className="flex items-center justify-between w-full">
                          <span className="text-[11px] text-slate-500 font-medium">
                            Showing {scheduleList.length} total scheduled duty day(s) (Archived)
                          </span>
                          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-slate-100 border border-slate-300 text-slate-600 font-bold text-xs select-none cursor-not-allowed shadow-2xs">
                            <Lock className="w-3.5 h-3.5 text-slate-500" />
                            <span>Roster Locked (Read-Only)</span>
                          </span>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={handleAddDutyDay}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white border border-slate-300 hover:border-indigo-500 text-slate-700 hover:text-indigo-600 font-bold transition-all cursor-pointer shadow-2xs"
                            >
                              <Plus size={13} />
                              <span>+ Add Extra Duty Day</span>
                            </button>
                            <button
                              type="button"
                              onClick={handleApplyDefaultTimingsToAllDays}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold transition-all cursor-pointer"
                            >
                              <Clock size={13} />
                              <span>Apply Times to All Days</span>
                            </button>
                          </div>

                          <div className="flex items-center gap-3">
                            <span className="text-[11px] text-slate-500 font-medium">
                              Showing {scheduleList.length} total scheduled duty day(s)
                            </span>
                            <button
                              type="button"
                              onClick={handleSaveDutyRoster}
                              disabled={saving}
                              className="px-4 py-1.5 rounded-xl bg-teal-600 hover:bg-teal-700 text-white font-bold text-xs shadow-sm transition-all flex items-center gap-1.5 cursor-pointer"
                            >
                              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                              <span>Save Changes</span>
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* ── 4. COMPLETION STATUS & STAGE 3 GATE ── */}
                  <div className={`p-4 rounded-2xl border transition-all ${allDaysCompleted
                      ? 'bg-emerald-50 border-emerald-300 shadow-sm'
                      : 'bg-slate-50 border-slate-200'
                    }`}>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div>
                        {allDaysCompleted ? (
                          <div className="flex items-center gap-2">
                            <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                            <div>
                              <h5 className="text-xs font-bold text-emerald-950">
                                🎉 All {totalDaysCount} Booked Service Days Completed!
                              </h5>
                              <p className="text-[11px] text-emerald-700">
                                All scheduled shifts have been delivered. You can now proceed to Stage 3 for Caregiver Compensation and Case Closure.
                              </p>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <Clock className="w-5 h-5 text-amber-600 flex-shrink-0" />
                            <div>
                              <h5 className="text-xs font-bold text-slate-900">
                                ⏳ Active Care in Progress ({completedDaysCount}/{totalDaysCount} Days Delivered)
                              </h5>
                              <p className="text-[11px] text-slate-500">
                                Continue logging caregiver start/end timings for each day. Once complete, proceed to Stage 3.
                              </p>
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
                        <button
                          type="button"
                          onClick={handleSaveDutyRoster}
                          disabled={saving}
                          className="px-4 py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs shadow-sm transition-all flex items-center gap-1.5 cursor-pointer"
                        >
                          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserCheck className="w-3.5 h-3.5" />}
                          <span>Save Duty Roster</span>
                        </button>

                        <button
                          type="button"
                          onClick={handleProceedToStage3}
                          className={`px-5 py-2.5 rounded-xl font-bold text-xs shadow-md transition-all flex items-center gap-1.5 cursor-pointer ${allDaysCompleted
                              ? 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-emerald-600/20 ring-2 ring-emerald-400/40'
                              : 'bg-teal-600 hover:bg-teal-700 text-white shadow-teal-600/20'
                            }`}
                        >
                          <span>Go to Stage 3: Payout</span>
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>

                </div>
              );
            })()}

            {/* ─── STAGE 3 CONTENT: COMPLETION & PROVIDER PAYOUT ───────────── */}
            {activeStageTab === 3 && (
              <div className="space-y-6 animate-in fade-in duration-200 pt-2">

                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-slate-100">
                  <div>
                    <h3 className="text-sm font-bold text-slate-900">Stage 3: Care Completion & Provider Payout</h3>
                    <p className="text-xs text-slate-500">Record final care settlement, caregiver compensation, and close the OPHE case.</p>
                  </div>

                  <span className={`px-3 py-1 rounded-full text-xs font-bold self-start sm:self-auto ${lead.status === 'completed'
                      ? 'bg-emerald-100 text-emerald-800 border border-emerald-200'
                      : 'bg-indigo-100 text-indigo-800 border border-indigo-200'
                    }`}>
                    {lead.status === 'completed' ? '✓ Case Completed & Paid Out' : 'Ready for Payout Closure'}
                  </span>
                </div>

                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Total Patient Payment</span>
                    <span className="text-lg font-black text-slate-900 block mt-1">
                      ₹{safeInr(totalPaidCollected || lead?.ophePaymentData?.amount || lead?.opheServiceDetails?.totalAmount || lead?.opheServiceDetails?.quotedPrice || payoutForm.finalAmountReceived || 0)}
                    </span>
                    <span className="text-[11px] text-emerald-700 font-semibold mt-0.5 block">
                      {lead?.ophePaymentData?.status === 'paid' || totalPaidCollected > 0 ? '● Verified Paid' : '○ Pending'}
                    </span>
                  </div>

                  <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Assigned Provider</span>
                    <span className="text-sm font-bold text-slate-900 block mt-1">
                      {lead?.opheProviderData?.providerName || 'N/A'}
                    </span>
                    <span className="text-[11px] text-slate-500 block">
                      {(lead?.opheProviderData?.providerRole || providerForm?.providerRole || 'Care Provider')} • {(lead?.opheProviderData?.shiftType || providerForm?.shiftType || '12-Hour Shift')}
                    </span>
                  </div>

                  <div className="bg-teal-50/60 p-4 rounded-2xl border border-teal-200">
                    <span className="text-[10px] font-bold text-teal-700 uppercase tracking-wider block">Net Revenue Margin</span>
                    <span className="text-lg font-black text-teal-900 block mt-1">
                      ₹{safeInr(
                        (Number(totalPaidCollected || lead?.ophePaymentData?.amount || lead?.opheServiceDetails?.totalAmount || lead?.opheServiceDetails?.quotedPrice || payoutForm.finalAmountReceived || 0)) -
                        (Number(payoutForm?.providerPayoutAmount || lead?.ophePayoutData?.providerPayoutAmount || lead?.ophePayoutData?.providerPayout || 0))
                      )}
                    </span>
                    <span className="text-[11px] text-teal-700 font-medium">Auto-calculated</span>
                  </div>
                </div>

                {(() => {

                  const displayPatientPaid = Number(totalPaidCollected || payoutForm.finalAmountReceived || lead?.ophePayoutData?.finalAmountReceived || lead?.ophePaymentData?.amount || 0);
                  const displayProviderPaid = Number(payoutForm.providerPayoutAmount || lead?.ophePayoutData?.providerPayoutAmount || lead?.ophePayoutData?.providerPayout || lead?.ophePayoutData?.payoutAmount || 0);
                  const displayMargin = displayPatientPaid - displayProviderPaid;

                  return (
                    <div className="space-y-5">
                      {/* Completed Case Settlement Records Summary Card */}
                      {isCaseSettled && (
                        <div className="bg-gradient-to-r from-emerald-50 via-teal-50 to-emerald-50 border-2 border-emerald-300 rounded-2xl p-5 shadow-sm space-y-3">
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-emerald-200">
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 rounded-xl bg-emerald-600 text-white flex items-center justify-center font-bold shadow-xs shrink-0">
                                <CheckCircle2 className="w-5 h-5" />
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <h4 className="text-sm font-black text-emerald-950">Settlement & Case Completion Record</h4>
                                  <span className="text-[10px] bg-emerald-200 text-emerald-900 font-bold px-2 py-0.5 rounded-full uppercase">
                                    Completed & Settled
                                  </span>
                                </div>
                                <p className="text-xs text-emerald-800 mt-0.5">
                                  All scheduled care shifts delivered, patient payment verified, and provider compensation processed.
                                </p>
                              </div>
                            </div>
                            <span className="text-xs font-black text-emerald-800 bg-white px-3 py-1.5 rounded-xl border border-emerald-200 shadow-2xs self-start sm:self-auto flex items-center gap-1.5">
                              <Lock className="w-3.5 h-3.5 text-emerald-600" />
                              <span>Settled on {payoutForm.payoutDate || (lead?.ophePayoutData?.processedAt ? new Date(lead.ophePayoutData.processedAt).toLocaleDateString() : 'Recorded')}</span>
                            </span>
                          </div>

                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                            <div className="p-3 bg-white/90 rounded-xl border border-emerald-200">
                              <span className="text-[10px] font-bold text-slate-500 uppercase block">Patient Paid</span>
                              <span className="text-base font-black text-emerald-900 mt-0.5 block">
                                ₹{safeInr(displayPatientPaid)}
                              </span>
                            </div>
                            <div className="p-3 bg-white/90 rounded-xl border border-emerald-200">
                              <span className="text-[10px] font-bold text-slate-500 uppercase block">Provider Paid</span>
                              <span className="text-base font-black text-rose-700 mt-0.5 block">
                                ₹{safeInr(displayProviderPaid)}
                              </span>
                            </div>
                            <div className="p-3 bg-white/90 rounded-xl border border-emerald-200">
                              <span className="text-[10px] font-bold text-slate-500 uppercase block">Net Margin</span>
                              <span className="text-base font-black text-teal-800 mt-0.5 block">
                                ₹{safeInr(displayMargin)}
                              </span>
                            </div>
                            <div className="p-3 bg-white/90 rounded-xl border border-emerald-200">
                              <span className="text-[10px] font-bold text-slate-500 uppercase block">Payout Mode & Ref</span>
                              <span className="text-xs font-bold text-slate-800 mt-0.5 block truncate">
                                {payoutForm.payoutMode || lead?.ophePayoutData?.payoutMode || 'Transfer'}
                              </span>
                              <span className="text-[10px] text-slate-400 font-mono block truncate">
                                {payoutForm.payoutReferenceId || lead?.ophePayoutData?.payoutReferenceId || 'No UTR logged'}
                              </span>
                            </div>
                          </div>

                          {(payoutForm.completionNotes || lead?.ophePayoutData?.completionNotes) && (
                            <div className="p-3 bg-white/80 rounded-xl border border-emerald-200 text-xs text-emerald-950">
                              <span className="font-bold text-[10px] uppercase text-emerald-800 block mb-0.5">Closure Notes:</span>
                              <p>{payoutForm.completionNotes || lead?.ophePayoutData?.completionNotes}</p>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Settlement Form: Read-only once Completed and Settled */}
                      <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6 sm:p-7 space-y-5">

                        {/* Read-Only Status Banner when Settled */}
                        {isCaseSettled && (
                          <div className="flex items-center gap-2.5 px-4 py-3 bg-amber-50 rounded-xl border border-amber-200/90 text-amber-900 text-xs">
                            <Lock className="w-4 h-4 text-amber-600 shrink-0" />
                            <span className="font-semibold leading-tight">
                              This case has been marked as Completed and Settled. All payout and settlement details are permanently locked in read-only mode.
                            </span>
                          </div>
                        )}

                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">

                          {/* 1. Final Patient Amount Collected */}
                          <div>
                            <label className="h-5 flex items-center text-xs font-bold text-slate-700 mb-2 truncate" title="Final Amount Collected from Patient (₹)">
                              Final Collected Amount (₹)
                            </label>
                            <input
                              type="number"
                              value={payoutForm.finalAmountReceived}
                              onChange={(e) => setPayoutForm(p => ({ ...p, finalAmountReceived: e.target.value }))}
                              disabled={isCaseSettled}
                              readOnly={isCaseSettled}
                              className={`w-full h-11 text-xs px-3.5 py-2.5 rounded-xl border transition-all ${isCaseSettled
                                  ? 'border-slate-200 bg-slate-100/90 text-slate-700 font-bold cursor-not-allowed select-none'
                                  : 'border-slate-300 bg-white font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500'
                                }`}
                            />
                          </div>

                          {/* 2. Provider Payout Amount */}
                          <div>
                            <label className="h-5 flex items-center text-xs font-bold text-slate-700 mb-2 truncate" title="Provider Payout Amount (₹)">
                              Provider Payout Amount (₹) {!isCaseSettled && <span className="text-rose-500 ml-0.5">*</span>}
                            </label>
                            <input
                              type="number"
                              value={payoutForm.providerPayoutAmount}
                              onChange={(e) => setPayoutForm(p => ({ ...p, providerPayoutAmount: e.target.value }))}
                              placeholder="Compensation amount"
                              disabled={isCaseSettled}
                              readOnly={isCaseSettled}
                              className={`w-full h-11 text-xs px-3.5 py-2.5 rounded-xl border transition-all ${isCaseSettled
                                  ? 'border-slate-200 bg-slate-100/90 text-rose-800 font-bold cursor-not-allowed select-none'
                                  : 'border-slate-300 bg-white font-bold text-rose-700 focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500'
                                }`}
                            />
                          </div>

                          {/* 3. Payout Mode */}
                          <div>
                            <label className="h-5 flex items-center text-xs font-bold text-slate-700 mb-2 truncate">
                              Payout Mode
                            </label>
                            <select
                              value={payoutForm.payoutMode}
                              onChange={(e) => setPayoutForm(p => ({ ...p, payoutMode: e.target.value }))}
                              disabled={isCaseSettled}
                              className={`w-full h-11 text-xs px-3.5 py-2.5 rounded-xl border transition-all ${isCaseSettled
                                  ? 'border-slate-200 bg-slate-100/90 text-slate-700 font-medium cursor-not-allowed select-none'
                                  : 'border-slate-300 bg-white font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 cursor-pointer'
                                }`}
                            >
                              <option value="Bank Transfer / IMPS">Bank Transfer / IMPS</option>
                              <option value="UPI / QR">UPI / QR Transfer</option>
                              <option value="Cheque">Cheque</option>
                              <option value="Cash Settlement">Cash Settlement</option>
                            </select>
                          </div>

                          {/* 4. Payment Reference / UTR Number */}
                          <div>
                            <label className="h-5 flex items-center text-xs font-bold text-slate-700 mb-2 truncate">
                              Payment Reference / UTR Number
                            </label>
                            <input
                              type="text"
                              value={payoutForm.payoutReferenceId}
                              onChange={(e) => setPayoutForm(p => ({ ...p, payoutReferenceId: e.target.value }))}
                              placeholder="e.g. UTR12389472"
                              disabled={isCaseSettled}
                              readOnly={isCaseSettled}
                              className={`w-full h-11 text-xs px-3.5 py-2.5 rounded-xl border transition-all font-mono ${isCaseSettled
                                  ? 'border-slate-200 bg-slate-100/90 text-slate-700 font-medium cursor-not-allowed select-none'
                                  : 'border-slate-300 bg-white font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500'
                                }`}
                            />
                          </div>

                          {/* 5. Settlement Date */}
                          <div>
                            <label className="h-5 flex items-center text-xs font-bold text-slate-700 mb-2 truncate">
                              Settlement Date
                            </label>
                            <input
                              type="date"
                              value={payoutForm.payoutDate}
                              onChange={(e) => setPayoutForm(p => ({ ...p, payoutDate: e.target.value }))}
                              disabled={isCaseSettled}
                              readOnly={isCaseSettled}
                              className={`w-full h-11 text-xs px-3.5 py-2.5 rounded-xl border transition-all ${isCaseSettled
                                  ? 'border-slate-200 bg-slate-100/90 text-slate-700 font-medium cursor-not-allowed select-none'
                                  : 'border-slate-300 bg-white font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 cursor-pointer'
                                }`}
                            />
                          </div>

                          {/* 6. Patient Experience / Rating */}
                          <div>
                            <label className="h-5 flex items-center text-xs font-bold text-slate-700 mb-2 truncate">
                              Patient Experience / Rating
                            </label>
                            <select
                              value={payoutForm.patientFeedback}
                              onChange={(e) => setPayoutForm(p => ({ ...p, patientFeedback: e.target.value }))}
                              disabled={isCaseSettled}
                              className={`w-full h-11 text-xs px-3.5 py-2.5 rounded-xl border transition-all ${isCaseSettled
                                  ? 'border-slate-200 bg-slate-100/90 text-slate-700 font-medium cursor-not-allowed select-none'
                                  : 'border-slate-300 bg-white font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 cursor-pointer'
                                }`}
                            >
                              <option value="Very Satisfied (5 Star)">Very Satisfied (5 Star)</option>
                              <option value="Satisfied (4 Star)">Satisfied (4 Star)</option>
                              <option value="Neutral (3 Star)">Neutral (3 Star)</option>
                              <option value="Needs Improvement (2 Star)">Needs Improvement (2 Star)</option>
                            </select>
                          </div>

                        </div>

                        {/* Settlement & Case Closure Notes */}
                        <div>
                          <label className="h-5 flex items-center text-xs font-bold text-slate-700 mb-2">
                            Settlement & Case Closure Notes
                          </label>
                          <textarea
                            rows={3}
                            value={payoutForm.completionNotes}
                            onChange={(e) => setPayoutForm(p => ({ ...p, completionNotes: e.target.value }))}
                            placeholder="Summary of service completed, feedback, and payment release confirmation..."
                            disabled={isCaseSettled}
                            readOnly={isCaseSettled}
                            className={`w-full text-xs p-3.5 rounded-xl border transition-all min-h-[84px] ${isCaseSettled
                                ? 'border-slate-200 bg-slate-100/90 text-slate-700 cursor-not-allowed select-none resize-none'
                                : 'border-slate-300 bg-white text-slate-800 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 resize-y'
                              }`}
                          />
                        </div>

                        {/* Bottom Actions: Disabled / Read-Only Badge if Settled */}
                        <div className="flex items-center justify-between pt-3 border-t border-slate-200">
                          {isCaseSettled ? (
                            <>
                              <span className="text-xs font-bold text-emerald-800 flex items-center gap-1.5">
                                <ShieldCheck className="w-4 h-4 text-emerald-600" />
                                <span>Case Closed: Settlement & Payout Finalized</span>
                              </span>
                              <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-slate-100 border border-slate-300 text-slate-600 font-bold text-xs select-none cursor-not-allowed shadow-2xs">
                                <Lock className="w-3.5 h-3.5 text-slate-500" />
                                <span>Settlement Finalized (Read-Only)</span>
                              </div>
                            </>
                          ) : (
                            <div className="w-full flex justify-end">
                              <button
                                type="button"
                                onClick={handleCompletePayout}
                                disabled={saving}
                                className="px-6 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs shadow-md shadow-emerald-600/20 transition-all flex items-center gap-2 cursor-pointer"
                              >
                                <CheckCircle className="w-4 h-4" />
                                <span>{saving ? 'Processing...' : 'Complete Case & Settle Payout'}</span>
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })()}

              </div>
            )}

          </div>

        </div>

      </div>

      {/* ─── FULL-SCREEN PHOTO & DOCUMENT LIGHTBOX MODAL ───────────────────────── */}
      {previewFile && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-150">

            {/* Modal Top Bar */}
            <div className="bg-slate-900 px-6 py-4 text-white flex items-center justify-between">
              <div className="flex items-center gap-2 overflow-hidden">
                <Image size={18} className="text-teal-400 flex-shrink-0" />
                <h3 className="font-bold text-sm truncate">{previewFile.name || 'Document / Photo Viewer'}</h3>
              </div>
              <div className="flex items-center gap-3">
                <a
                  href={previewFile.url}
                  target="_blank"
                  rel="noreferrer"
                  download={previewFile.name || 'download'}
                  className="p-1.5 px-3 rounded-lg bg-slate-800 hover:bg-slate-700 text-teal-400 hover:text-teal-300 transition-colors flex items-center gap-1.5 text-xs font-bold"
                  title="Open / Download"
                >
                  <Download size={14} />
                  <span>Download / Open</span>
                </a>
                <button
                  type="button"
                  onClick={() => setPreviewFile(null)}
                  className="w-8 h-8 rounded-lg bg-slate-800 hover:bg-rose-600 text-white flex items-center justify-center transition-colors font-bold text-sm cursor-pointer"
                  title="Close"
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Content Viewer Body */}
            <div className="flex-1 bg-slate-950 p-4 flex items-center justify-center overflow-auto min-h-[400px]">
              {previewFile.isPDF ? (
                <iframe
                  src={previewFile.url}
                  title={previewFile.name}
                  className="w-full h-[70vh] rounded-xl border-0 bg-white"
                />
              ) : (
                <img
                  src={previewFile.url}
                  alt={previewFile.name}
                  className="max-h-[75vh] max-w-full object-contain rounded-xl shadow-2xl"
                />
              )}
            </div>

            {/* Modal Footer */}
            <div className="bg-slate-900 px-6 py-3 text-slate-400 text-xs flex items-center justify-between border-t border-slate-800">
              <span className="truncate">{previewFile.name}</span>
              <button
                type="button"
                onClick={() => setPreviewFile(null)}
                className="px-4 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-white font-bold text-xs cursor-pointer"
              >
                Close Viewer
              </button>
            </div>

          </div>
        </div>
      )}

      {/* ─── QUICK CALL DISPOSITION MODAL ────────────────────────────────────── */}
      {showQuickDispModal && latestCall && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full border border-slate-200 overflow-hidden animate-in fade-in zoom-in-95 duration-150">
            <div className="bg-gradient-to-r from-violet-600 to-indigo-600 p-4 px-6 text-white flex items-center justify-between">
              <div className="flex items-center gap-2">
                <PhoneCall size={18} />
                <h3 className="font-bold text-sm">Update Call & Follow-up Status</h3>
              </div>
              <button
                type="button"
                onClick={() => setShowQuickDispModal(false)}
                className="w-7 h-7 rounded-lg bg-white/10 hover:bg-white/20 text-white flex items-center justify-center cursor-pointer transition-colors"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSaveQuickDisp} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">Follow-up Disposition *</label>
                <select
                  value={quickDispForm.disposition}
                  onChange={(e) => setQuickDispForm(p => ({ ...p, disposition: e.target.value }))}
                  className="w-full text-xs px-3 py-2.5 rounded-xl border border-slate-300 bg-white font-semibold"
                >
                  <option value="callback_completed">Callback Completed</option>
                  <option value="follow_up_needed">Follow-up Needed</option>
                  <option value="appointment_booked">Appointment / Care Booked</option>
                  <option value="not_interested">Not Interested</option>
                  <option value="invalid_number">Invalid Number</option>
                  <option value="pending">Pending</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">Call Reason / Discussion Topic</label>
                <input
                  type="text"
                  value={quickDispForm.callReason}
                  onChange={(e) => setQuickDispForm(p => ({ ...p, callReason: e.target.value }))}
                  placeholder="e.g. Care package pricing discussion"
                  className="w-full text-xs px-3 py-2 rounded-xl border border-slate-300 bg-white"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">Call Notes</label>
                <textarea
                  rows={3}
                  value={quickDispForm.dispositionNotes}
                  onChange={(e) => setQuickDispForm(p => ({ ...p, dispositionNotes: e.target.value }))}
                  placeholder="Patient wants 12hr day shift nurse starting tomorrow..."
                  className="w-full text-xs p-3 rounded-xl border border-slate-300 bg-white"
                />
              </div>

              <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowQuickDispModal(false)}
                  className="px-4 py-2 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 font-bold text-xs cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSavingQuickDisp}
                  className="px-5 py-2 rounded-xl bg-violet-600 hover:bg-violet-700 text-white font-bold text-xs shadow-md transition-all cursor-pointer flex items-center gap-1.5"
                >
                  {isSavingQuickDisp ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
                  <span>Save Status</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ─── OPHE PAYMENT TRANSACTIONS LEDGER MODAL (POPUP TABLE) ───────────── */}
      {showTransactionsModal && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full border border-slate-200 overflow-hidden animate-in fade-in zoom-in-95 duration-150 flex flex-col max-h-[90vh]">

            {/* Header */}
            <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-teal-950 p-5 px-6 text-white flex items-center justify-between border-b border-slate-800">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-teal-500/20 border border-teal-500/40 text-teal-300 flex items-center justify-center font-bold shadow-xs">
                  <CreditCard className="w-5 h-5" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-black text-sm text-white uppercase tracking-wider">
                      OPHE Payment & Transaction Ledger
                    </h3>
                    <span className="text-[10px] bg-teal-500/30 text-teal-200 border border-teal-500/40 px-2 py-0.5 rounded-full font-bold">
                      {lead?.patientName || 'Patient Ledger'}
                    </span>
                  </div>
                  <p className="text-xs text-slate-400 mt-0.5">
                    Complete audit trail of fresh bookings, extension invoices, Razorpay links & payment timestamps
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setShowTransactionsModal(false)}
                className="w-8 h-8 rounded-xl bg-white/10 hover:bg-white/20 text-white flex items-center justify-center cursor-pointer transition-colors text-sm font-bold"
              >
                ✕
              </button>
            </div>

            {/* Quick Summary Cards */}
            <div className="bg-slate-50 p-4 px-6 border-b border-slate-200/80 grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="bg-white p-3 rounded-xl border border-slate-200/80 shadow-2xs">
                <span className="text-[10px] uppercase font-bold text-slate-400 block">Total Package Value</span>
                <span className="text-sm font-black text-slate-900 block mt-0.5">
                  ₹{safeInr(Math.max(Number(lead?.opheServiceDetails?.totalAmount || currentBilling.totalAmount || 0), totalPaidCollected))}
                </span>
              </div>
              <div className="bg-white p-3 rounded-xl border border-emerald-200/80 shadow-2xs">
                <span className="text-[10px] uppercase font-bold text-emerald-600 block">Total Collected (Paid)</span>
                <span className="text-sm font-black text-emerald-700 block mt-0.5">
                  ₹{safeInr(totalPaidCollected)}
                </span>
              </div>
              <div className="bg-white p-3 rounded-xl border border-amber-200/80 shadow-2xs">
                <span className="text-[10px] uppercase font-bold text-amber-600 block">Balance Pending</span>
                <span className="text-sm font-black text-amber-700 block mt-0.5">
                  ₹{safeInr(Math.max(0, Math.max(Number(lead?.opheServiceDetails?.totalAmount || currentBilling.totalAmount || 0), totalPaidCollected) - totalPaidCollected))}
                </span>
              </div>
              <div className="bg-white p-3 rounded-xl border border-slate-200/80 shadow-2xs">
                <span className="text-[10px] uppercase font-bold text-slate-400 block">Recorded Transactions</span>
                <span className="text-sm font-black text-slate-800 block mt-0.5">
                  {(Array.isArray(lead?.ophePaymentHistory) && lead.ophePaymentHistory.length > 0 ? lead.ophePaymentHistory.length : (lead?.ophePaymentData?.paymentLink ? 1 : 0))} Entries
                </span>
              </div>
            </div>

            {/* Table Container */}
            <div className="p-6 overflow-y-auto flex-1">
              {(() => {
                const historyList = Array.isArray(lead?.ophePaymentHistory) && lead.ophePaymentHistory.length > 0
                  ? lead.ophePaymentHistory
                  : lead?.ophePaymentData?.paymentLink
                    ? [{
                      amount: lead?.ophePaymentData?.amount || lead?.opheServiceDetails?.totalAmount || 0,
                      isExtension: Boolean(lead?.ophePaymentData?.isExtension),
                      paymentType: lead?.ophePaymentData?.isExtension ? 'Extension Payment' : 'Standard Payment',
                      description: lead?.ophePaymentData?.description || 'OPHE Care Service Package',
                      paymentLink: lead?.ophePaymentData?.paymentLink,
                      paymentLinkId: lead?.ophePaymentData?.paymentLinkId,
                      razorpayPaymentId: lead?.ophePaymentData?.razorpayPaymentId,
                      status: lead?.ophePaymentData?.status || 'pending',
                      createdAt: lead?.ophePaymentData?.paidAt || lead?.createdAt,
                      paidAt: lead?.ophePaymentData?.paidAt
                    }]
                    : [];

                if (historyList.length === 0) {
                  return (
                    <div className="text-center py-12 bg-slate-50 rounded-2xl border border-dashed border-slate-200">
                      <CreditCard className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                      <h4 className="text-xs font-bold text-slate-700 uppercase">No Transactions Recorded Yet</h4>
                      <p className="text-xs text-slate-400 mt-1">Generate a Razorpay payment link to create the first transaction.</p>
                    </div>
                  );
                }

                return (
                  <div className="overflow-x-auto rounded-2xl border border-slate-200 shadow-2xs">
                    <table className="w-full text-left border-collapse text-xs">
                      <thead>
                        <tr className="bg-slate-100/80 text-slate-600 font-bold uppercase text-[10px] tracking-wider border-b border-slate-200">
                          <th className="py-3 px-4">#</th>
                          <th className="py-3 px-4">Type</th>
                          <th className="py-3 px-4">Date & Time</th>
                          <th className="py-3 px-4">Details / Shift</th>
                          <th className="py-3 px-4">Amount</th>
                          <th className="py-3 px-4">Reference / Payment ID</th>
                          <th className="py-3 px-4">Status</th>
                          <th className="py-3 px-4 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {historyList.map((item, idx) => {
                          const isPaid = item.status === 'paid';
                          const isExt = item.isExtension || (item.paymentType && item.paymentType.includes('Extension'));

                          return (
                            <tr key={idx} className="hover:bg-slate-50/80 transition-colors">
                              <td className="py-3 px-4 font-bold text-slate-400 text-[11px]">{idx + 1}</td>

                              <td className="py-3 px-4">
                                <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-black uppercase ${isExt ? 'bg-purple-100 text-purple-800' : 'bg-teal-100 text-teal-800'
                                  }`}>
                                  {isExt ? <RotateCw className="w-3 h-3" /> : <CreditCard className="w-3 h-3" />}
                                  <span>{isExt ? 'Extension' : 'Standard'}</span>
                                </span>
                              </td>

                              <td className="py-3 px-4 text-slate-600 text-[11px] whitespace-nowrap">
                                <div className="font-semibold text-slate-800">
                                  {item.paidAt ? new Date(item.paidAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : (item.createdAt ? new Date(item.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-')}
                                </div>
                                <div className="text-[10px] text-slate-400">
                                  {item.paidAt ? new Date(item.paidAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : (item.createdAt ? new Date(item.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '')}
                                </div>
                              </td>

                              <td className="py-3 px-4 text-slate-700 max-w-xs">
                                <span className="font-medium block truncate" title={item.description || 'OPHE Care Package'}>
                                  {item.description || 'OPHE Care Service'}
                                </span>
                              </td>

                              <td className="py-3 px-4 whitespace-nowrap">
                                <span className="font-black text-sm text-slate-900">
                                  ₹{safeInr(item.amount, 2)}
                                </span>
                              </td>

                              <td className="py-3 px-4 font-mono text-[10px] text-slate-600">
                                {item.razorpayPaymentId ? (
                                  <div className="flex items-center gap-1 text-emerald-700 font-bold">
                                    <span>{item.razorpayPaymentId}</span>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        navigator.clipboard.writeText(item.razorpayPaymentId);
                                        toast.success('Payment ID copied!');
                                      }}
                                      className="p-1 hover:bg-slate-100 rounded text-slate-400 hover:text-slate-600 cursor-pointer"
                                      title="Copy ID"
                                    >
                                      <Copy className="w-3 h-3" />
                                    </button>
                                  </div>
                                ) : item.paymentLinkId ? (
                                  <span className="text-slate-400">{item.paymentLinkId}</span>
                                ) : (
                                  <span className="text-slate-300">-</span>
                                )}
                              </td>

                              <td className="py-3 px-4">
                                {isPaid ? (
                                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800">
                                    <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                                    <span>PAID</span>
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800">
                                    <Clock className="w-3 h-3 text-amber-600" />
                                    <span>PENDING</span>
                                  </span>
                                )}
                              </td>

                              <td className="py-3 px-4 text-right whitespace-nowrap">
                                <div className="flex items-center justify-end gap-1.5">
                                  {item.paymentLink && (
                                    <>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          navigator.clipboard.writeText(item.paymentLink);
                                          toast.success('Link copied to clipboard!');
                                        }}
                                        className="p-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 cursor-pointer"
                                        title="Copy Payment Link"
                                      >
                                        <Copy className="w-3.5 h-3.5" />
                                      </button>
                                      <a
                                        href={item.paymentLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="p-1.5 rounded-lg bg-teal-50 hover:bg-teal-100 text-teal-700 cursor-pointer"
                                        title="Open Link in New Tab"
                                      >
                                        <ExternalLink className="w-3.5 h-3.5" />
                                      </a>
                                    </>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr className="bg-slate-50 border-t-2 border-slate-200 font-bold text-slate-800">
                          <td colSpan={4} className="py-3 px-4 text-right uppercase text-[10px] tracking-wider text-slate-500">
                            Total Collected (Paid):
                          </td>
                          <td className="py-3 px-4 whitespace-nowrap">
                            <span className="font-black text-sm text-emerald-700">
                              ₹{safeInr(totalPaidCollected, 2)}
                            </span>
                          </td>
                          <td colSpan={3} className="py-3 px-4 text-[10px] text-slate-400">
                            {historyList.filter(h => h.status === 'paid').length} Paid Transaction(s)
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                );
              })()}
            </div>

            {/* Modal Footer */}
            <div className="bg-slate-100/80 p-4 px-6 border-t border-slate-200 flex items-center justify-between">
              <button
                type="button"
                onClick={() => handleSyncPayment(false)}
                disabled={syncingPayment}
                className="px-4 py-2 rounded-xl bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-xs font-bold flex items-center gap-1.5 shadow-2xs cursor-pointer"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${syncingPayment ? 'animate-spin' : ''}`} />
                <span>Sync Payment Gateway</span>
              </button>

              <button
                type="button"
                onClick={() => setShowTransactionsModal(false)}
                className="px-5 py-2 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold shadow-sm cursor-pointer"
              >
                Close Ledger
              </button>
            </div>

          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* MODAL: Add / Edit Shift Duty Log (CRUD Modal) */}
      {/* ========================================================================= */}
      {showDutyModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs z-50 flex items-center justify-center p-4 animate-in fade-in duration-150">
          <div className="bg-white rounded-3xl shadow-2xl border border-slate-200 w-full max-w-xl max-h-[90vh] flex flex-col overflow-hidden animate-in zoom-in-95 duration-150">

            {/* Modal Header */}
            <div className="p-5 px-6 border-b border-slate-200 flex items-center justify-between bg-gradient-to-r from-teal-50/80 via-emerald-50/40 to-white">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-teal-600 text-white flex items-center justify-center shadow-md shadow-teal-500/20">
                  <Clock className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base font-black text-slate-900 flex items-center gap-2">
                    <span>{dutyModalForm.mode === 'edit' ? `Edit Day ${dutyModalForm.dayNumber} Shift Duty` : `Add New Shift Duty Log`}</span>
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-teal-100 text-teal-800">
                      CRUD Log
                    </span>
                  </h3>
                  <p className="text-xs text-slate-500 font-medium">
                    Configure shift timings, caregiver assignment, status and clinical notes
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowDutyModal(false)}
                className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 hover:text-slate-700 flex items-center justify-center transition-colors cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Modal Form Body */}
            <form onSubmit={handleSaveDutyModal} className="p-6 overflow-y-auto flex-1 space-y-4">

              {/* Row 1: Day Number + Duty Date */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-1.5">
                    Day Number (#) <span className="text-rose-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      min="1"
                      value={dutyModalForm.dayNumber}
                      onChange={(e) => setDutyModalForm(prev => ({ ...prev, dayNumber: Number(e.target.value) || 1 }))}
                      required
                      className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-slate-300 focus:border-teal-500 focus:ring-2 focus:ring-teal-100 text-xs font-bold text-slate-800 outline-none transition-all"
                      placeholder="1"
                    />
                    <Calendar className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  </div>
                </div>

                <div>
                  <label className="block text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-1.5">
                    Duty Date <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={dutyModalForm.date}
                    onChange={(e) => {
                      const nextDate = e.target.value;
                      const isFut = Boolean(nextDate && getNormalizedDate(nextDate) > getTodayDateStr());
                      setDutyModalForm(prev => ({
                        ...prev,
                        date: nextDate,
                        ...(isFut ? { status: 'Scheduled' } : {})
                      }));
                    }}
                    required
                    className="w-full px-3 py-2.5 rounded-xl border border-slate-300 focus:border-teal-500 focus:ring-2 focus:ring-teal-100 text-xs font-bold text-slate-800 outline-none transition-all"
                  />
                </div>
              </div>

              {/* Row 2: Work Timing (Start & End) */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-1.5">
                    Work Start Timing <span className="text-rose-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={dutyModalForm.startTime}
                      onChange={(e) => setDutyModalForm(prev => ({ ...prev, startTime: e.target.value }))}
                      placeholder="e.g. 08:00 AM or 09:00"
                      required
                      className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-slate-300 focus:border-teal-500 focus:ring-2 focus:ring-teal-100 text-xs font-bold text-slate-800 outline-none transition-all"
                    />
                    <Clock className="w-4 h-4 text-emerald-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  </div>
                  <div className="flex gap-1.5 mt-1.5">
                    {['08:00 AM', '09:00 AM', '08:00 PM'].map((t) => (
                      <button
                        key={t}
                        type="button"
                        onClick={() => setDutyModalForm(prev => ({ ...prev, startTime: t }))}
                        className="text-[10px] px-2 py-0.5 rounded bg-slate-100 hover:bg-teal-50 hover:text-teal-700 text-slate-600 font-medium transition-colors cursor-pointer"
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-1.5">
                    Work End Timing <span className="text-rose-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={dutyModalForm.endTime}
                      onChange={(e) => setDutyModalForm(prev => ({ ...prev, endTime: e.target.value }))}
                      placeholder="e.g. 08:00 PM or 21:00"
                      required
                      className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-slate-300 focus:border-teal-500 focus:ring-2 focus:ring-teal-100 text-xs font-bold text-slate-800 outline-none transition-all"
                    />
                    <Clock className="w-4 h-4 text-rose-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  </div>
                  <div className="flex gap-1.5 mt-1.5">
                    {['08:00 PM', '09:00 PM', '08:00 AM (Next Day)'].map((t) => (
                      <button
                        key={t}
                        type="button"
                        onClick={() => setDutyModalForm(prev => ({ ...prev, endTime: t }))}
                        className="text-[10px] px-2 py-0.5 rounded bg-slate-100 hover:bg-teal-50 hover:text-teal-700 text-slate-600 font-medium transition-colors cursor-pointer"
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </div>
              </div>



              {/* Row 4: Caregiver Assignment - Option 1: Old / Primary Provider vs Option 2: New Provider */}
              <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200 space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-[11px] font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
                    <UserCheck className="w-4 h-4 text-indigo-600" />
                    <span>Care Provider Assignment (Day {dutyModalForm.dayNumber})</span>
                  </label>
                  <span className="text-[10px] text-slate-500 font-medium">Daily Caregiver Allocation</span>
                </div>

                {/* 2-Option Selector Buttons */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      const primaryName = (lead?.opheProviderData?.providerName || providerForm.providerName || '').trim();
                      const primaryPhone = (lead?.opheProviderData?.providerPhone || providerForm.providerPhone || '').trim();
                      setDutyModalForm(prev => ({
                        ...prev,
                        providerOption: 'old',
                        providerName: primaryName,
                        providerPhone: primaryPhone
                      }));
                    }}
                    className={`p-2.5 rounded-xl text-xs font-bold border transition-all flex items-center justify-center gap-2 cursor-pointer ${dutyModalForm.providerOption === 'old'
                        ? 'bg-indigo-600 text-white border-indigo-600 shadow-xs'
                        : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-100'
                      }`}
                  >
                    <User className="w-3.5 h-3.5" />
                    <span>Option 1: Primary Caregiver</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setDutyModalForm(prev => ({
                        ...prev,
                        providerOption: 'new',
                        providerName: prev.providerOption === 'old' ? '' : prev.providerName,
                        providerPhone: prev.providerOption === 'old' ? '' : prev.providerPhone
                      }));
                    }}
                    className={`p-2.5 rounded-xl text-xs font-bold border transition-all flex items-center justify-center gap-2 cursor-pointer ${dutyModalForm.providerOption === 'new'
                        ? 'bg-emerald-600 text-white border-emerald-600 shadow-xs'
                        : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-100'
                      }`}
                  >
                    <UserPlus className="w-3.5 h-3.5" />
                    <span>Option 2: Assign New Provider</span>
                  </button>
                </div>

                {/* Option 1: Primary Caregiver Details */}
                {dutyModalForm.providerOption === 'old' ? (
                  <div className="p-3 bg-white rounded-xl border border-indigo-200/80 space-y-2.5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-lg bg-indigo-100 text-indigo-700 flex items-center justify-center font-black text-xs">
                          ✓
                        </div>
                        <div>
                          <div className="text-xs font-bold text-slate-900">
                            {lead?.opheProviderData?.providerName || providerForm.providerName || 'Primary Provider'}
                          </div>
                          <div className="text-[11px] text-slate-500 font-mono">
                            Phone: {lead?.opheProviderData?.providerPhone || providerForm.providerPhone || 'N/A'} • {lead?.opheProviderData?.providerRole || 'Primary Attendant'}
                          </div>
                        </div>
                      </div>
                      <span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-indigo-100 text-indigo-800">
                        Primary Caregiver
                      </span>
                    </div>

                    {/* Select from other existing providers on this case if available */}
                    {availableProviders.filter(p => !p.isPrimary).length > 0 && (
                      <div className="pt-2 border-t border-slate-100">
                        <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">
                          Or select another provider previously used in this case:
                        </label>
                        <select
                          value={dutyModalForm.providerName}
                          onChange={(e) => {
                            const sel = availableProviders.find(p => p.name === e.target.value);
                            if (sel) {
                              setDutyModalForm(prev => ({
                                ...prev,
                                providerName: sel.name,
                                providerPhone: sel.phone
                              }));
                            }
                          }}
                          className="w-full px-2.5 py-1.5 rounded-lg border border-slate-300 bg-white text-xs font-medium text-slate-800 outline-none"
                        >
                          {availableProviders.map(p => (
                            <option key={p.id} value={p.name}>{p.label}</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                ) : (
                  /* Option 2: Assign New Provider for this Day - All Provider Details */
                  <div className="p-4 bg-emerald-50/50 rounded-2xl border border-emerald-200 space-y-3.5">
                    <div className="flex items-center justify-between text-xs text-emerald-950 font-bold pb-2 border-b border-emerald-200/60">
                      <span className="flex items-center gap-1.5">
                        <UserPlus className="w-4 h-4 text-emerald-600" />
                        <span>Enter New Provider Complete Details for Day {dutyModalForm.dayNumber}:</span>
                      </span>
                      <span className="text-[10px] px-2.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-black border border-emerald-300 uppercase">
                        Day-Specific Provider
                      </span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {/* Provider Full Name */}
                      <div>
                        <label className="h-5 flex items-center text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1.5 truncate">
                          New Provider Full Name <span className="text-rose-500 ml-0.5">*</span>
                        </label>
                        <div className="relative">
                          <input
                            type="text"
                            required
                            value={dutyModalForm.providerName}
                            onChange={(e) => setDutyModalForm(prev => ({ ...prev, providerName: e.target.value }))}
                            placeholder="e.g. Ramesh Kumar / Pooja Sharma"
                            className="w-full h-11 pl-10 pr-3.5 py-2.5 rounded-xl border border-slate-300 bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 text-xs font-bold text-slate-800 outline-none transition-all"
                          />
                          <User className="w-4 h-4 text-emerald-600 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                        </div>
                      </div>

                      {/* Provider Phone */}
                      <div>
                        <label className="h-5 flex items-center text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1.5 truncate" title="Provider Phone / WhatsApp">
                          Provider Phone / WhatsApp <span className="text-rose-500 ml-0.5">*</span>
                        </label>
                        <div className="relative">
                          <input
                            type="tel"
                            maxLength={10}
                            required
                            value={dutyModalForm.providerPhone}
                            onChange={(e) => {
                              const cleaned = sanitizePhoneNumber(e.target.value);
                              setDutyModalForm(prev => ({ ...prev, providerPhone: cleaned }));
                            }}
                            placeholder="e.g. 9876543210"
                            className="w-full h-11 pl-10 pr-3.5 py-2.5 rounded-xl border border-slate-300 bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 text-xs font-bold text-slate-800 outline-none transition-all"
                          />
                          <Phone className="w-4 h-4 text-emerald-600 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                        </div>
                      </div>

                      {/* Provider Email */}
                      <div>
                        <label className="h-5 flex items-center text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1.5 truncate">
                          Provider Email Address
                        </label>
                        <div className="relative">
                          <input
                            type="email"
                            value={dutyModalForm.providerEmail}
                            onChange={(e) => setDutyModalForm(prev => ({ ...prev, providerEmail: e.target.value }))}
                            placeholder="e.g. ramesh.care@gmail.com"
                            className="w-full h-11 pl-10 pr-3.5 py-2.5 rounded-xl border border-slate-300 bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 text-xs font-medium text-slate-800 outline-none transition-all"
                          />
                          <Mail className="w-4 h-4 text-emerald-600 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                        </div>
                      </div>

                      {/* Provider Role */}
                      <div>
                        <label className="h-5 flex items-center text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1.5 truncate">
                          Provider Role / Designation
                        </label>
                        <select
                          value={dutyModalForm.providerRole}
                          onChange={(e) => setDutyModalForm(prev => ({ ...prev, providerRole: e.target.value }))}
                          className="w-full h-11 px-3.5 py-2.5 rounded-xl border border-slate-300 bg-white text-xs font-semibold text-slate-800 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 cursor-pointer transition-all"
                        >
                          <option value="Registered Nurse (GNM/BSc)">Registered Nurse (GNM/BSc)</option>
                          <option value="Trained Care Attendant">Trained Care Attendant</option>
                          <option value="Elder Care Companion">Elder Care Companion</option>
                          <option value="Physiotherapist">Physiotherapist</option>
                          <option value="Medical Equipment Technician">Medical Equipment Technician</option>
                        </select>
                      </div>

                      {/* Shift Type */}
                      <div className="sm:col-span-2">
                        <label className="h-5 flex items-center text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1.5 truncate">
                          Assigned Shift Type
                        </label>
                        <select
                          value={dutyModalForm.shiftType}
                          onChange={(e) => {
                            const val = e.target.value;
                            setDutyModalForm(prev => ({
                              ...prev,
                              shiftType: val,
                              startTime: val.includes('Night') ? '08:00 PM' : '08:00 AM',
                              endTime: val.includes('Night') ? '08:00 AM' : '08:00 PM'
                            }));
                          }}
                          className="w-full h-11 px-3.5 py-2.5 rounded-xl border border-slate-300 bg-white text-xs font-semibold text-slate-800 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 cursor-pointer transition-all"
                        >
                          <option value="12-Hour Day Shift">12-Hour Day Shift (8am - 8pm)</option>
                          <option value="12-Hour Night Shift">12-Hour Night Shift (8pm - 8am)</option>
                          <option value="24-Hour Live-in Care">24-Hour Live-in Care</option>
                          <option value="Single Visit (1-2 Hours)">Single Visit (1-2 Hours)</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Row 4: Duty Shift Status */}
              {(() => {
                const modalNormalizedDate = dutyModalForm.date ? getNormalizedDate(dutyModalForm.date) : '';
                const todayLocalStr = getTodayDateStr();
                const isModalFuture = Boolean(modalNormalizedDate && modalNormalizedDate > todayLocalStr);

                return (
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="block text-[11px] font-bold text-slate-600 uppercase tracking-wider">
                        Duty Status
                      </label>
                      {isModalFuture && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold bg-amber-50 text-amber-800 border border-amber-200">
                          <Lock className="w-3 h-3 text-amber-600" />
                          <span>Locked until shift date</span>
                        </span>
                      )}
                    </div>

                    {isModalFuture ? (
                      <div className="space-y-2">
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                          {[
                            { val: 'Scheduled', label: 'Scheduled' },
                            { val: 'In-Progress', label: 'In-Progress' },
                            { val: 'Completed', label: 'Completed' },
                            { val: 'Cancelled', label: 'Cancelled' }
                          ].map((s) => (
                            <div
                              key={s.val}
                              className={`px-3 py-2 rounded-xl text-xs font-bold border text-center select-none cursor-not-allowed ${s.val === 'Scheduled'
                                  ? 'bg-slate-800 text-white border-slate-800 shadow-xs'
                                  : 'bg-slate-50 text-slate-400 border-slate-200 opacity-60'
                                }`}
                              title={`Status is locked to Scheduled until ${dutyModalForm.date}`}
                            >
                              {s.label}
                            </div>
                          ))}
                        </div>
                        <div className="flex items-center gap-2 p-2.5 bg-amber-50/90 rounded-xl border border-amber-200 text-amber-900 text-xs">
                          <Lock className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                          <span className="text-[11px] font-medium leading-tight">
                            Duty Status is locked to <strong className="font-bold">Scheduled</strong> until {dutyModalForm.date}. It will automatically unlock when this shift date arrives.
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        {[
                          { val: 'Scheduled', label: 'Scheduled', color: 'border-slate-300 text-slate-700 hover:bg-slate-50', active: 'bg-slate-800 text-white border-slate-800' },
                          { val: 'In-Progress', label: 'In-Progress', color: 'border-blue-300 text-blue-700 hover:bg-blue-50', active: 'bg-blue-600 text-white border-blue-600' },
                          { val: 'Completed', label: 'Completed', color: 'border-emerald-300 text-emerald-700 hover:bg-emerald-50', active: 'bg-emerald-600 text-white border-emerald-600' },
                          { val: 'Cancelled', label: 'Cancelled', color: 'border-rose-300 text-rose-700 hover:bg-rose-50', active: 'bg-rose-600 text-white border-rose-600' }
                        ].map((s) => (
                          <button
                            key={s.val}
                            type="button"
                            onClick={() => setDutyModalForm(prev => ({ ...prev, status: s.val }))}
                            className={`px-3 py-2 rounded-xl text-xs font-bold border transition-all text-center cursor-pointer ${dutyModalForm.status === s.val ? s.active : s.color
                              }`}
                          >
                            {s.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Row 5: Care & Vitals Notes */}
              <div>
                <label className="block text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-1.5">
                  Shift Notes, Vitals & Daily Observations
                </label>
                <textarea
                  rows="3"
                  value={dutyModalForm.notes}
                  onChange={(e) => setDutyModalForm(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Record vitals (BP, SpO2, Pulse, Sugar), medication administered, physical therapy completed, patient mood or shift handover remarks..."
                  className="w-full px-3 py-2.5 rounded-xl border border-slate-300 focus:border-teal-500 focus:ring-2 focus:ring-teal-100 text-xs text-slate-800 outline-none transition-all placeholder:text-slate-400"
                />
              </div>

              {/* Modal Action Buttons */}
              <div className="pt-2 flex items-center justify-end gap-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowDutyModal(false)}
                  className="px-4 py-2.5 rounded-xl border border-slate-300 hover:bg-slate-100 text-slate-700 text-xs font-bold transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2.5 rounded-xl bg-teal-600 hover:bg-teal-700 text-white text-xs font-bold shadow-md shadow-teal-600/20 flex items-center gap-1.5 transition-all cursor-pointer"
                >
                  <Save className="w-4 h-4" />
                  <span>{dutyModalForm.mode === 'edit' ? 'Update Shift Duty Log' : 'Save Duty to Roster'}</span>
                </button>
              </div>

            </form>

          </div>
        </div>
      )}

      {/* 🛑 Mark as Lost Lead Modal */}
      {showLostModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs animate-fadeIn">
          <div className="bg-white rounded-3xl border border-slate-100 shadow-2xl max-w-md w-full overflow-hidden p-6 space-y-4">
            <div className="flex items-center justify-between pb-2 border-b border-slate-100">
              <div className="flex items-center gap-2.5">
                <div className="w-10 h-10 rounded-2xl bg-rose-50 text-rose-600 flex items-center justify-center border border-rose-100">
                  <XCircle className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-slate-900">Mark as Lost Lead</h3>
                  <p className="text-xs text-slate-400">Move case to OPHE Lost Leads</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => { setShowLostModal(false); setLostReason('Customer not interested'); }}
                className="p-1.5 text-slate-400 hover:text-slate-600 rounded-xl hover:bg-slate-100 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <p className="text-xs text-slate-600 leading-relaxed">
              Are you sure you want to mark <strong>{lead?.patientName || 'this patient'}</strong> ({lead?.patientPhone}) as a lost lead? This will move the case into <strong>OPHE Pipeline &gt; Lost Leads</strong>.
            </p>

            {/* Quick Reason Chips */}
            <div className="space-y-1.5">
              <label className="text-[11px] font-bold text-slate-600 uppercase tracking-wider">Quick Reason</label>
              <div className="flex flex-wrap gap-1.5">
                {[
                  'Customer not interested',
                  'Pricing too high',
                  'Found alternative service',
                  'Care no longer needed',
                  'Patient / Family declined',
                  'Unreachable / Invalid number'
                ].map(r => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => setLostReason(r)}
                    className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold border transition-all cursor-pointer ${lostReason === r
                        ? 'bg-rose-50 text-rose-700 border-rose-300 ring-1 ring-rose-300'
                        : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'
                      }`}
                  >
                    {r}
                  </button>
                ))}
              </div>
            </div>

            {/* Reason text area */}
            <div>
              <label className="text-[11px] font-bold text-slate-600 uppercase tracking-wider block mb-1">
                Reason / Loss Note
              </label>
              <textarea
                rows={2}
                value={lostReason}
                onChange={(e) => setLostReason(e.target.value)}
                placeholder="Specify reason for losing this OPHE lead..."
                className="w-full text-xs px-3 py-2 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-rose-400 focus:outline-none transition-all resize-none font-medium"
              />
            </div>

            {/* Footer Buttons */}
            <div className="flex items-center justify-between gap-2 pt-2 border-t border-slate-100">
              <button
                type="button"
                onClick={() => {
                  setShowLostModal(false);
                  navigate('/ophe/pipeline?tab=lost');
                }}
                className="text-xs text-slate-500 hover:text-slate-800 font-semibold underline underline-offset-2 cursor-pointer"
              >
                Go to Lost Leads
              </button>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => { setShowLostModal(false); setLostReason('Customer not interested'); }}
                  disabled={markingLost}
                  className="px-3.5 py-2 rounded-xl text-xs font-bold text-slate-500 hover:bg-slate-100 transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmMarkLost}
                  disabled={markingLost}
                  className="px-4 py-2 rounded-xl text-xs font-bold bg-rose-600 hover:bg-rose-700 text-white shadow-md shadow-rose-600/20 transition-all flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                >
                  {markingLost ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <XCircle className="w-3.5 h-3.5" />}
                  <span>Confirm & Go to Lost Leads</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
